import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  X, TrendingUp, Layers, Crosshair, MousePointer, 
  Minus, BarChart2, Activity, ZoomIn, ZoomOut,
  ChevronDown, ChevronRight, RotateCcw, Ruler, Type, Lock, Crown, Search, Filter, Check, Star,
  Code, Users, Sparkles, ShoppingBag, Award, Bookmark, Plus,
  Circle, Square, Triangle, PenTool, Trash2, Eye, EyeOff, 
  Move, Hash, Percent, ArrowUpRight, GitBranch, Maximize2, Settings,
  List, Info, Newspaper, PanelRightClose, PanelRight, TrendingDown,
  DollarSign, Clock, Calendar, Target, AlertCircle, Bell, Copy, 
  Zap, Brain, LineChart, Layers2, ArrowLeftRight, Play, Pause,
  ChevronLeft, SkipBack, SkipForward, BarChart, Cpu, Scissors, Flag, 
  AlertTriangle, CheckCircle, XCircle, MinusCircle, BookOpen, GraduationCap,
  Anchor, Fish, History, GitCompare, Shuffle, Gauge, Radio, Wifi,
  Database, Server, Globe, Map, Compass, Navigation, Radar, ArrowLeft, MoreHorizontal, FileText, ExternalLink, Keyboard,
  Download, Camera, Link, Pencil, Eraser
} from 'lucide-react';
import './FullscreenChart.css';

// API Base URL - uses environment variable or falls back to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================
// UTILITY FUNCTIONS
// ============================================
const formatPrice = (price) => price?.toFixed(2) || '';
const formatVolume = (vol) => {
  if (!vol) return '0';
  if (vol >= 1e9) return (vol / 1e9).toFixed(2) + 'B';
  if (vol >= 1e6) return (vol / 1e6).toFixed(2) + 'M';
  if (vol >= 1e3) return (vol / 1e3).toFixed(2) + 'K';
  return vol.toString();
};
const formatDate = (dateStr, timeframe) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (['1s', '5s', '15s', '30s', '1m', '5m', '15m', '30m', '1h', '4h'].includes(timeframe)) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Generate mock data for demo/fallback
const generateMockData = (symbol, timeframe) => {
  const days = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, '5Y': 1825 }[timeframe] || 90;
  const dataPoints = Math.min(days, 250);
  const basePrice = symbol === 'AAPL' ? 175 : symbol === 'NVDA' ? 150 : symbol === 'TSLA' ? 250 : 100;
  const data = [];
  let price = basePrice * (0.7 + Math.random() * 0.3);
  
  for (let i = dataPoints; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const volatility = 0.02 + Math.random() * 0.03;
    const change = (Math.random() - 0.48) * volatility * price;
    const open = price;
    const close = price + change;
    const high = Math.max(open, close) * (1 + Math.random() * 0.015);
    const low = Math.min(open, close) * (1 - Math.random() * 0.015);
    const volume = Math.floor(10000000 + Math.random() * 50000000);
    
    data.push({
      date: date.toISOString().split('T')[0],
      open: parseFloat(open.toFixed(2)),
      high: parseFloat(high.toFixed(2)),
      low: parseFloat(low.toFixed(2)),
      close: parseFloat(close.toFixed(2)),
      volume
    });
    
    price = close;
  }
  
  return data;
};

const calculateSMA = (data, period) => data.map((_, i) => i < period - 1 ? null : data.slice(i - period + 1, i + 1).reduce((a, b) => a + b.close, 0) / period);
const calculateEMA = (data, period) => {
  const result = [], k = 2 / (period + 1);
  let ema = data.slice(0, period).reduce((a, b) => a + b.close, 0) / period;
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) result.push(null);
    else if (i === period - 1) result.push(ema);
    else { ema = (data[i].close - ema) * k + ema; result.push(ema); }
  }
  return result;
};
const calculateBollinger = (data, period = 20, stdDev = 2) => {
  const sma = calculateSMA(data, period);
  return data.map((_, i) => {
    if (i < period - 1) return { middle: null, upper: null, lower: null };
    const slice = data.slice(i - period + 1, i + 1).map(d => d.close);
    const mean = sma[i], std = Math.sqrt(slice.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / period);
    return { middle: mean, upper: mean + stdDev * std, lower: mean - stdDev * std };
  });
};

// RSI Calculation
const calculateRSI = (data, period = 14) => {
  const result = [];
  let gains = 0, losses = 0;
  
  for (let i = 0; i < data.length; i++) {
    if (i === 0) { result.push(null); continue; }
    
    const change = data[i].close - data[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    
    if (i < period) {
      gains += gain;
      losses += loss;
      result.push(null);
    } else if (i === period) {
      gains += gain;
      losses += loss;
      const avgGain = gains / period;
      const avgLoss = losses / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push(100 - (100 / (1 + rs)));
    } else {
      const avgGain = ((result[i - 1] !== null ? (100 - result[i - 1]) / result[i - 1] * (period - 1) : 0) + gain) / period;
      const avgLoss = ((result[i - 1] !== null ? result[i - 1] / (100 - result[i - 1]) * (period - 1) : 0) + loss) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push(100 - (100 / (1 + rs)));
    }
  }
  return result;
};

// MACD Calculation
const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
  const emaFast = calculateEMA(data, fastPeriod);
  const emaSlow = calculateEMA(data, slowPeriod);
  
  const macdLine = data.map((_, i) => {
    if (emaFast[i] === null || emaSlow[i] === null) return null;
    return emaFast[i] - emaSlow[i];
  });
  
  // Calculate signal line (EMA of MACD)
  const validMacd = macdLine.filter(v => v !== null);
  const signalLine = [];
  let ema = validMacd.slice(0, signalPeriod).reduce((a, b) => a + b, 0) / signalPeriod;
  const k = 2 / (signalPeriod + 1);
  let validIdx = 0;
  
  for (let i = 0; i < data.length; i++) {
    if (macdLine[i] === null) {
      signalLine.push(null);
    } else {
      if (validIdx < signalPeriod - 1) {
        signalLine.push(null);
      } else if (validIdx === signalPeriod - 1) {
        signalLine.push(ema);
      } else {
        ema = (macdLine[i] - ema) * k + ema;
        signalLine.push(ema);
      }
      validIdx++;
    }
  }
  
  const histogram = data.map((_, i) => {
    if (macdLine[i] === null || signalLine[i] === null) return null;
    return macdLine[i] - signalLine[i];
  });
  
  return { macdLine, signalLine, histogram };
};

// Support/Resistance Level Detection
const calculateSupportResistance = (data, sensitivity = 3) => {
  if (!data || data.length < 20) return { support: [], resistance: [] };
  
  const levels = [];
  const lookback = 5;
  
  for (let i = lookback; i < data.length - lookback; i++) {
    const current = data[i];
    const leftHigh = Math.max(...data.slice(i - lookback, i).map(d => d.high));
    const rightHigh = Math.max(...data.slice(i + 1, i + lookback + 1).map(d => d.high));
    const leftLow = Math.min(...data.slice(i - lookback, i).map(d => d.low));
    const rightLow = Math.min(...data.slice(i + 1, i + lookback + 1).map(d => d.low));
    
    // Resistance (local high)
    if (current.high >= leftHigh && current.high >= rightHigh) {
      levels.push({ price: current.high, type: 'resistance', strength: 1 });
    }
    // Support (local low)
    if (current.low <= leftLow && current.low <= rightLow) {
      levels.push({ price: current.low, type: 'support', strength: 1 });
    }
  }
  
  // Cluster nearby levels
  const clustered = { support: [], resistance: [] };
  const threshold = (Math.max(...data.map(d => d.high)) - Math.min(...data.map(d => d.low))) * 0.02;
  
  levels.forEach(level => {
    const arr = clustered[level.type];
    const existing = arr.find(l => Math.abs(l.price - level.price) < threshold);
    if (existing) {
      existing.strength++;
      existing.price = (existing.price + level.price) / 2;
    } else {
      arr.push({ ...level });
    }
  });
  
  // Return top levels by strength
  clustered.support = clustered.support.sort((a, b) => b.strength - a.strength).slice(0, sensitivity);
  clustered.resistance = clustered.resistance.sort((a, b) => b.strength - a.strength).slice(0, sensitivity);
  
  return clustered;
};

// Volume Profile Calculation
const calculateVolumeProfile = (data, numBins = 20) => {
  if (!data || data.length < 5) return [];
  
  const minPrice = Math.min(...data.map(d => d.low));
  const maxPrice = Math.max(...data.map(d => d.high));
  const binSize = (maxPrice - minPrice) / numBins;
  
  const bins = Array(numBins).fill(0).map((_, i) => ({
    priceStart: minPrice + i * binSize,
    priceEnd: minPrice + (i + 1) * binSize,
    volume: 0,
    buyVolume: 0,
    sellVolume: 0
  }));
  
  data.forEach(candle => {
    const binIdx = Math.min(Math.floor((candle.close - minPrice) / binSize), numBins - 1);
    if (binIdx >= 0) {
      bins[binIdx].volume += candle.volume || 0;
      if (candle.close >= candle.open) {
        bins[binIdx].buyVolume += candle.volume || 0;
      } else {
        bins[binIdx].sellVolume += candle.volume || 0;
      }
    }
  });
  
  const maxVol = Math.max(...bins.map(b => b.volume));
  bins.forEach(b => b.percentage = maxVol > 0 ? b.volume / maxVol : 0);
  
  return bins;
};

// ============================================
// PATTERN LIBRARY - Complete Reference
// ============================================
const PATTERN_LIBRARY = {
  // CANDLESTICK - Single Candle
  doji: { name: 'Doji', category: 'candlestick', type: 'neutral', description: 'Open and close are nearly equal, forming a cross. Indicates market indecision and potential reversal.', significance: 'High when appearing after a strong trend' },
  hammer: { name: 'Hammer', category: 'candlestick', type: 'bullish', description: 'Small body at top with long lower shadow (2x body). Shows buyers rejected lower prices.', significance: 'Strong reversal signal at bottom of downtrend' },
  inverted_hammer: { name: 'Inverted Hammer', category: 'candlestick', type: 'bullish', description: 'Small body at bottom with long upper shadow. Potential bullish reversal after downtrend.', significance: 'Requires confirmation from next candle' },
  hanging_man: { name: 'Hanging Man', category: 'candlestick', type: 'bearish', description: 'Same shape as hammer but at top of uptrend. Warning of potential reversal.', significance: 'Bearish warning, wait for confirmation' },
  shooting_star: { name: 'Shooting Star', category: 'candlestick', type: 'bearish', description: 'Small body at bottom with long upper shadow. Sellers rejected higher prices.', significance: 'Strong reversal signal at top of uptrend' },
  marubozu_bull: { name: 'Bullish Marubozu', category: 'candlestick', type: 'bullish', description: 'Long green body with no shadows. Buyers controlled entire session.', significance: 'Very strong bullish sentiment' },
  marubozu_bear: { name: 'Bearish Marubozu', category: 'candlestick', type: 'bearish', description: 'Long red body with no shadows. Sellers controlled entire session.', significance: 'Very strong bearish sentiment' },
  spinning_top: { name: 'Spinning Top', category: 'candlestick', type: 'neutral', description: 'Small body with upper and lower shadows. Shows indecision between buyers and sellers.', significance: 'Weakening trend, potential reversal' },
  dragonfly_doji: { name: 'Dragonfly Doji', category: 'candlestick', type: 'bullish', description: 'T-shaped doji with long lower shadow. Buyers pushed price back up from lows.', significance: 'Bullish reversal signal' },
  gravestone_doji: { name: 'Gravestone Doji', category: 'candlestick', type: 'bearish', description: 'Inverted T-shaped doji with long upper shadow. Sellers pushed price back down.', significance: 'Bearish reversal signal' },
  long_legged_doji: { name: 'Long-Legged Doji', category: 'candlestick', type: 'neutral', description: 'Doji with very long upper and lower shadows. Extreme indecision.', significance: 'Major turning point possible' },
  
  // CANDLESTICK - Two Candle
  engulfing_bull: { name: 'Bullish Engulfing', category: 'candlestick', type: 'bullish', description: 'Green candle completely engulfs prior red candle. Strong buying pressure emerged.', significance: 'One of the most reliable reversal patterns' },
  engulfing_bear: { name: 'Bearish Engulfing', category: 'candlestick', type: 'bearish', description: 'Red candle completely engulfs prior green candle. Strong selling pressure emerged.', significance: 'One of the most reliable reversal patterns' },
  harami_bull: { name: 'Bullish Harami', category: 'candlestick', type: 'bullish', description: 'Small green candle contained within prior large red candle. Selling pressure weakening.', significance: 'Moderate reversal signal, needs confirmation' },
  harami_bear: { name: 'Bearish Harami', category: 'candlestick', type: 'bearish', description: 'Small red candle contained within prior large green candle. Buying pressure weakening.', significance: 'Moderate reversal signal, needs confirmation' },
  harami_cross_bull: { name: 'Bullish Harami Cross', category: 'candlestick', type: 'bullish', description: 'Doji contained within prior large red candle. Stronger than regular harami.', significance: 'Strong indecision after selling' },
  harami_cross_bear: { name: 'Bearish Harami Cross', category: 'candlestick', type: 'bearish', description: 'Doji contained within prior large green candle. Stronger than regular harami.', significance: 'Strong indecision after buying' },
  piercing_line: { name: 'Piercing Line', category: 'candlestick', type: 'bullish', description: 'Green candle opens below prior red close, closes above midpoint of red body.', significance: 'Bullish reversal at support' },
  dark_cloud: { name: 'Dark Cloud Cover', category: 'candlestick', type: 'bearish', description: 'Red candle opens above prior green close, closes below midpoint of green body.', significance: 'Bearish reversal at resistance' },
  tweezer_top: { name: 'Tweezer Top', category: 'candlestick', type: 'bearish', description: 'Two candles with matching highs at top of uptrend. Resistance level formed.', significance: 'Bearish reversal signal' },
  tweezer_bottom: { name: 'Tweezer Bottom', category: 'candlestick', type: 'bullish', description: 'Two candles with matching lows at bottom of downtrend. Support level formed.', significance: 'Bullish reversal signal' },
  
  // CANDLESTICK - Three Candle
  morning_star: { name: 'Morning Star', category: 'candlestick', type: 'bullish', description: 'Three candle pattern: large red, small body (gap down), large green (gap up). Major reversal.', significance: 'Very strong bullish reversal' },
  evening_star: { name: 'Evening Star', category: 'candlestick', type: 'bearish', description: 'Three candle pattern: large green, small body (gap up), large red (gap down). Major reversal.', significance: 'Very strong bearish reversal' },
  morning_doji_star: { name: 'Morning Doji Star', category: 'candlestick', type: 'bullish', description: 'Morning star with doji as middle candle. Even stronger reversal signal.', significance: 'Extremely strong bullish reversal' },
  evening_doji_star: { name: 'Evening Doji Star', category: 'candlestick', type: 'bearish', description: 'Evening star with doji as middle candle. Even stronger reversal signal.', significance: 'Extremely strong bearish reversal' },
  three_white_soldiers: { name: 'Three White Soldiers', category: 'candlestick', type: 'bullish', description: 'Three consecutive long green candles, each closing higher. Strong bullish momentum.', significance: 'Very reliable bullish continuation' },
  three_black_crows: { name: 'Three Black Crows', category: 'candlestick', type: 'bearish', description: 'Three consecutive long red candles, each closing lower. Strong bearish momentum.', significance: 'Very reliable bearish continuation' },
  three_inside_up: { name: 'Three Inside Up', category: 'candlestick', type: 'bullish', description: 'Bullish harami followed by green close above first candle. Confirmed reversal.', significance: 'Reliable bullish reversal' },
  three_inside_down: { name: 'Three Inside Down', category: 'candlestick', type: 'bearish', description: 'Bearish harami followed by red close below first candle. Confirmed reversal.', significance: 'Reliable bearish reversal' },
  three_outside_up: { name: 'Three Outside Up', category: 'candlestick', type: 'bullish', description: 'Bullish engulfing followed by higher green close. Confirmed reversal.', significance: 'Reliable bullish reversal' },
  three_outside_down: { name: 'Three Outside Down', category: 'candlestick', type: 'bearish', description: 'Bearish engulfing followed by lower red close. Confirmed reversal.', significance: 'Reliable bearish reversal' },
  abandoned_baby_bull: { name: 'Bullish Abandoned Baby', category: 'candlestick', type: 'bullish', description: 'Doji gaps away from both surrounding candles. Rare but powerful reversal.', significance: 'Extremely rare and reliable' },
  abandoned_baby_bear: { name: 'Bearish Abandoned Baby', category: 'candlestick', type: 'bearish', description: 'Doji gaps away from both surrounding candles. Rare but powerful reversal.', significance: 'Extremely rare and reliable' },
  
  // CHART PATTERNS
  double_bottom: { name: 'Double Bottom', category: 'chart', type: 'bullish', description: 'Two lows at similar price level with peak between. W-shaped reversal pattern.', significance: 'Major bullish reversal pattern' },
  double_top: { name: 'Double Top', category: 'chart', type: 'bearish', description: 'Two highs at similar price level with trough between. M-shaped reversal pattern.', significance: 'Major bearish reversal pattern' },
  triple_bottom: { name: 'Triple Bottom', category: 'chart', type: 'bullish', description: 'Three lows at similar level. Stronger than double bottom.', significance: 'Very strong support level' },
  triple_top: { name: 'Triple Top', category: 'chart', type: 'bearish', description: 'Three highs at similar level. Stronger than double top.', significance: 'Very strong resistance level' },
  head_shoulders: { name: 'Head and Shoulders', category: 'chart', type: 'bearish', description: 'Three peaks with middle peak highest. Classic reversal pattern.', significance: 'One of most reliable patterns' },
  inv_head_shoulders: { name: 'Inverse Head & Shoulders', category: 'chart', type: 'bullish', description: 'Three troughs with middle trough lowest. Classic reversal pattern.', significance: 'One of most reliable patterns' },
  ascending_triangle: { name: 'Ascending Triangle', category: 'chart', type: 'bullish', description: 'Flat top resistance with rising support. Buyers getting more aggressive.', significance: 'Usually breaks upward' },
  descending_triangle: { name: 'Descending Triangle', category: 'chart', type: 'bearish', description: 'Flat bottom support with falling resistance. Sellers getting more aggressive.', significance: 'Usually breaks downward' },
  symmetrical_triangle: { name: 'Symmetrical Triangle', category: 'chart', type: 'neutral', description: 'Converging trendlines. Can break either direction.', significance: 'Continuation pattern, watch for breakout' },
  rising_wedge: { name: 'Rising Wedge', category: 'chart', type: 'bearish', description: 'Price making higher highs/lows but converging upward. Weakening momentum.', significance: 'Usually breaks downward' },
  falling_wedge: { name: 'Falling Wedge', category: 'chart', type: 'bullish', description: 'Price making lower highs/lows but converging downward. Selling exhaustion.', significance: 'Usually breaks upward' },
  bull_flag: { name: 'Bull Flag', category: 'chart', type: 'bullish', description: 'Sharp rise (pole) followed by slight downward consolidation (flag).', significance: 'Strong continuation pattern' },
  bear_flag: { name: 'Bear Flag', category: 'chart', type: 'bearish', description: 'Sharp decline (pole) followed by slight upward consolidation (flag).', significance: 'Strong continuation pattern' },
  bull_pennant: { name: 'Bull Pennant', category: 'chart', type: 'bullish', description: 'Sharp rise followed by symmetrical triangle consolidation.', significance: 'Strong continuation pattern' },
  bear_pennant: { name: 'Bear Pennant', category: 'chart', type: 'bearish', description: 'Sharp decline followed by symmetrical triangle consolidation.', significance: 'Strong continuation pattern' },
  cup_handle: { name: 'Cup and Handle', category: 'chart', type: 'bullish', description: 'U-shaped recovery followed by small pullback. Classic accumulation pattern.', significance: 'Very reliable bullish pattern' },
  rounding_bottom: { name: 'Rounding Bottom', category: 'chart', type: 'bullish', description: 'Gradual U-shaped price curve. Slow accumulation by smart money.', significance: 'Long-term bullish reversal' },
  rounding_top: { name: 'Rounding Top', category: 'chart', type: 'bearish', description: 'Gradual inverted U-shaped price curve. Slow distribution by smart money.', significance: 'Long-term bearish reversal' },
  
  // TREND PATTERNS
  uptrend: { name: 'Uptrend', category: 'trend', type: 'bullish', description: 'Series of higher highs and higher lows. Bulls are in control.', significance: 'Trade with the trend' },
  downtrend: { name: 'Downtrend', category: 'trend', type: 'bearish', description: 'Series of lower highs and lower lows. Bears are in control.', significance: 'Trade with the trend' },
  strong_uptrend: { name: 'Strong Uptrend', category: 'trend', type: 'bullish', description: 'Price above all major MAs with bullish alignment (5>10>20>50).', significance: 'Very strong bullish momentum' },
  strong_downtrend: { name: 'Strong Downtrend', category: 'trend', type: 'bearish', description: 'Price below all major MAs with bearish alignment (5<10<20<50).', significance: 'Very strong bearish momentum' },
  golden_cross: { name: 'Golden Cross', category: 'trend', type: 'bullish', description: 'Short-term MA crosses above long-term MA. Classic buy signal.', significance: 'Major bullish signal' },
  death_cross: { name: 'Death Cross', category: 'trend', type: 'bearish', description: 'Short-term MA crosses below long-term MA. Classic sell signal.', significance: 'Major bearish signal' },
  ma_bounce: { name: 'MA Bounce', category: 'trend', type: 'bullish', description: 'Price pulls back to moving average and bounces. Trend continuation.', significance: 'Good entry point in uptrend' },
  ma_rejection: { name: 'MA Rejection', category: 'trend', type: 'bearish', description: 'Price rallies to moving average and gets rejected. Trend continuation.', significance: 'Good entry point in downtrend' },
  
  // VOLUME PATTERNS
  volume_breakout: { name: 'Volume Breakout', category: 'volume', type: 'bullish', description: 'Price breaks resistance with volume 2x+ above average. Confirmed breakout.', significance: 'High probability continuation' },
  volume_breakdown: { name: 'Volume Breakdown', category: 'volume', type: 'bearish', description: 'Price breaks support with volume 2x+ above average. Confirmed breakdown.', significance: 'High probability continuation' },
  volume_climax: { name: 'Volume Climax', category: 'volume', type: 'neutral', description: 'Extremely high volume (3x+ average). Often marks exhaustion and reversal.', significance: 'Potential trend exhaustion' },
  volume_dry_up: { name: 'Volume Dry Up', category: 'volume', type: 'neutral', description: 'Volume significantly below average. Lack of interest, consolidation phase.', significance: 'Prepare for breakout' },
  accumulation: { name: 'Accumulation', category: 'volume', type: 'bullish', description: 'Price stable or rising on increasing volume. Smart money buying.', significance: 'Bullish long-term' },
  distribution: { name: 'Distribution', category: 'volume', type: 'bearish', description: 'Price stable or falling on increasing volume. Smart money selling.', significance: 'Bearish long-term' },
  
  // SUPPORT/RESISTANCE
  support_bounce: { name: 'Support Bounce', category: 'levels', type: 'bullish', description: 'Price tests support level and bounces. Buyers defending level.', significance: 'Good long entry with stop below support' },
  resistance_reject: { name: 'Resistance Rejection', category: 'levels', type: 'bearish', description: 'Price tests resistance and gets rejected. Sellers defending level.', significance: 'Good short entry with stop above resistance' },
  support_break: { name: 'Support Breakdown', category: 'levels', type: 'bearish', description: 'Price closes below support. Level failed, more downside likely.', significance: 'Stop losses triggered, momentum down' },
  resistance_break: { name: 'Resistance Breakout', category: 'levels', type: 'bullish', description: 'Price closes above resistance. Level cleared, more upside likely.', significance: 'Short squeezes, momentum up' },
  
  // GAP PATTERNS  
  gap_up: { name: 'Gap Up', category: 'gap', type: 'bullish', description: 'Price opens above prior high. Strong overnight buying interest.', significance: 'Momentum often continues' },
  gap_down: { name: 'Gap Down', category: 'gap', type: 'bearish', description: 'Price opens below prior low. Strong overnight selling pressure.', significance: 'Momentum often continues' },
  gap_fill: { name: 'Gap Fill', category: 'gap', type: 'neutral', description: 'Price returns to fill a previous gap. Common occurrence.', significance: 'Gaps tend to fill eventually' },
  breakaway_gap: { name: 'Breakaway Gap', category: 'gap', type: 'neutral', description: 'Gap at start of new trend. Usually not filled quickly.', significance: 'Signals new trend beginning' },
  exhaustion_gap: { name: 'Exhaustion Gap', category: 'gap', type: 'neutral', description: 'Gap near end of trend. Often filled quickly as trend reverses.', significance: 'Trend may be ending' },
};

// Pattern Detection
const detectPatterns = (data) => {
  if (!data || data.length < 50) return [];
  const patterns = [];
  
  const recent = data.slice(-30);
  const closes = recent.map(d => d.close);
  const highs = recent.map(d => d.high);
  const lows = recent.map(d => d.low);
  const opens = recent.map(d => d.open);
  const volumes = recent.map(d => d.volume || 0);
  
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  const prev2 = data[data.length - 3];
  
  const bodySize = Math.abs(last.close - last.open);
  const wickSize = last.high - last.low;
  const lowerWick = Math.min(last.open, last.close) - last.low;
  const upperWick = last.high - Math.max(last.open, last.close);
  const avgBody = recent.slice(-10).reduce((a, d) => a + Math.abs(d.close - d.open), 0) / 10;
  const avgVolume = volumes.slice(-20).reduce((a, b) => a + b, 0) / 20;
  
  const addPattern = (id, confidence) => {
    const lib = PATTERN_LIBRARY[id];
    if (lib) {
      patterns.push({
        id,
        type: lib.type === 'bullish' ? 'bullish_' + lib.category : lib.type === 'bearish' ? 'bearish_' + lib.category : 'neutral_' + lib.category,
        name: lib.name,
        category: lib.category,
        confidence,
        description: lib.description,
        significance: lib.significance
      });
    }
  };

  // === TREND PATTERNS ===
  const sma5 = closes.slice(-5).reduce((a, b) => a + b, 0) / 5;
  const sma10 = closes.slice(-10).reduce((a, b) => a + b, 0) / 10;
  const sma20 = closes.slice(-20).reduce((a, b) => a + b, 0) / 20;
  
  if (data.length >= 50) {
    const sma50 = data.slice(-50).reduce((a, d) => a + d.close, 0) / 50;
    const sma50_prev = data.slice(-51, -1).reduce((a, d) => a + d.close, 0) / 50;
    
    if (sma5 > sma10 && sma10 > sma20 && sma20 > sma50 && last.close > sma5) {
      addPattern('strong_uptrend', 0.9);
    } else if (sma5 < sma10 && sma10 < sma20 && sma20 < sma50 && last.close < sma5) {
      addPattern('strong_downtrend', 0.9);
    }
    
    // Golden Cross / Death Cross
    if (sma20 > sma50 && data.slice(-52, -2).reduce((a, d) => a + d.close, 0) / 50 > sma50_prev) {
      addPattern('golden_cross', 0.8);
    } else if (sma20 < sma50 && data.slice(-52, -2).reduce((a, d) => a + d.close, 0) / 50 < sma50_prev) {
      addPattern('death_cross', 0.8);
    }
  }
  
  if (sma5 > sma20 * 1.01) {
    addPattern('uptrend', 0.7);
  } else if (sma5 < sma20 * 0.99) {
    addPattern('downtrend', 0.7);
  }

  // === SINGLE CANDLESTICK PATTERNS ===
  
  // Doji variations
  if (bodySize < wickSize * 0.1 && wickSize > 0) {
    if (lowerWick > upperWick * 3 && upperWick < bodySize) {
      addPattern('dragonfly_doji', 0.8);
    } else if (upperWick > lowerWick * 3 && lowerWick < bodySize) {
      addPattern('gravestone_doji', 0.8);
    } else if (lowerWick > avgBody && upperWick > avgBody) {
      addPattern('long_legged_doji', 0.85);
    } else {
      addPattern('doji', 0.75);
    }
  }
  
  // Spinning Top
  if (bodySize < wickSize * 0.3 && bodySize > wickSize * 0.05 && lowerWick > bodySize * 0.5 && upperWick > bodySize * 0.5) {
    addPattern('spinning_top', 0.65);
  }
  
  // Hammer / Hanging Man
  if (lowerWick > bodySize * 2 && upperWick < bodySize * 0.5 && bodySize > 0) {
    if (last.close > last.open && sma5 < sma20) {
      addPattern('hammer', 0.8);
    } else if (last.close < last.open && sma5 > sma20) {
      addPattern('hanging_man', 0.75);
    }
  }
  
  // Inverted Hammer / Shooting Star
  if (upperWick > bodySize * 2 && lowerWick < bodySize * 0.5 && bodySize > 0) {
    if (last.close > last.open && sma5 < sma20) {
      addPattern('inverted_hammer', 0.7);
    } else if (last.close < last.open && sma5 > sma20) {
      addPattern('shooting_star', 0.8);
    }
  }
  
  // Marubozu
  if (upperWick < bodySize * 0.05 && lowerWick < bodySize * 0.05 && bodySize > avgBody * 1.5) {
    if (last.close > last.open) {
      addPattern('marubozu_bull', 0.8);
    } else {
      addPattern('marubozu_bear', 0.8);
    }
  }

  // === TWO CANDLESTICK PATTERNS ===
  if (prev) {
    const prevBody = Math.abs(prev.close - prev.open);
    
    // Engulfing
    if (prev.close < prev.open && last.close > last.open && 
        last.open <= prev.close && last.close >= prev.open && bodySize > prevBody * 1.1) {
      addPattern('engulfing_bull', 0.85);
    }
    if (prev.close > prev.open && last.close < last.open && 
        last.open >= prev.close && last.close <= prev.open && bodySize > prevBody * 1.1) {
      addPattern('engulfing_bear', 0.85);
    }
    
    // Harami
    if (prevBody > avgBody * 1.2 && bodySize < prevBody * 0.5) {
      if (prev.close < prev.open && last.close > last.open && 
          last.open > prev.close && last.close < prev.open) {
        if (bodySize < wickSize * 0.15) {
          addPattern('harami_cross_bull', 0.75);
        } else {
          addPattern('harami_bull', 0.7);
        }
      }
      if (prev.close > prev.open && last.close < last.open && 
          last.open < prev.close && last.close > prev.open) {
        if (bodySize < wickSize * 0.15) {
          addPattern('harami_cross_bear', 0.75);
        } else {
          addPattern('harami_bear', 0.7);
        }
      }
    }
    
    // Piercing Line / Dark Cloud
    if (prev.close < prev.open && last.close > last.open &&
        last.open < prev.low && last.close > (prev.open + prev.close) / 2 && last.close < prev.open) {
      addPattern('piercing_line', 0.75);
    }
    if (prev.close > prev.open && last.close < last.open &&
        last.open > prev.high && last.close < (prev.open + prev.close) / 2 && last.close > prev.open) {
      addPattern('dark_cloud', 0.75);
    }
    
    // Tweezer
    if (Math.abs(last.high - prev.high) < avgBody * 0.1 && sma5 > sma20) {
      addPattern('tweezer_top', 0.7);
    }
    if (Math.abs(last.low - prev.low) < avgBody * 0.1 && sma5 < sma20) {
      addPattern('tweezer_bottom', 0.7);
    }
  }

  // === THREE CANDLESTICK PATTERNS ===
  if (prev && prev2) {
    const prev2Body = Math.abs(prev2.close - prev2.open);
    const prevBody = Math.abs(prev.close - prev.open);
    
    // Morning Star / Evening Star
    if (prev2.close < prev2.open && prev2Body > avgBody &&
        prevBody < avgBody * 0.5 &&
        last.close > last.open && bodySize > avgBody &&
        last.close > (prev2.open + prev2.close) / 2) {
      if (prevBody < wickSize * 0.15) {
        addPattern('morning_doji_star', 0.9);
      } else {
        addPattern('morning_star', 0.85);
      }
    }
    if (prev2.close > prev2.open && prev2Body > avgBody &&
        prevBody < avgBody * 0.5 &&
        last.close < last.open && bodySize > avgBody &&
        last.close < (prev2.open + prev2.close) / 2) {
      if (prevBody < wickSize * 0.15) {
        addPattern('evening_doji_star', 0.9);
      } else {
        addPattern('evening_star', 0.85);
      }
    }
    
    // Three White Soldiers / Black Crows
    if (prev2.close > prev2.open && prev.close > prev.open && last.close > last.open &&
        prev.close > prev2.close && last.close > prev.close &&
        prev.open > prev2.open && prev.open < prev2.close &&
        last.open > prev.open && last.open < prev.close) {
      addPattern('three_white_soldiers', 0.85);
    }
    if (prev2.close < prev2.open && prev.close < prev.open && last.close < last.open &&
        prev.close < prev2.close && last.close < prev.close &&
        prev.open < prev2.open && prev.open > prev2.close &&
        last.open < prev.open && last.open > prev.close) {
      addPattern('three_black_crows', 0.85);
    }
    
    // Three Inside Up/Down
    const wasBullishHarami = prev2.close < prev2.open && prev.close > prev.open &&
        prev.open > prev2.close && prev.close < prev2.open && Math.abs(prev.close - prev.open) < Math.abs(prev2.close - prev2.open) * 0.5;
    if (wasBullishHarami && last.close > last.open && last.close > prev2.open) {
      addPattern('three_inside_up', 0.8);
    }
    
    const wasBearishHarami = prev2.close > prev2.open && prev.close < prev.open &&
        prev.open < prev2.close && prev.close > prev2.open && Math.abs(prev.close - prev.open) < Math.abs(prev2.close - prev2.open) * 0.5;
    if (wasBearishHarami && last.close < last.open && last.close < prev2.open) {
      addPattern('three_inside_down', 0.8);
    }
  }

  // === CHART PATTERNS ===
  
  // Double Bottom / Top
  const recentLows = lows.slice(-15);
  const recentHighs = highs.slice(-15);
  const minLow = Math.min(...recentLows);
  const maxHigh = Math.max(...recentHighs);
  
  const lowIndices = recentLows.map((l, i) => l < minLow * 1.02 ? i : -1).filter(i => i >= 0);
  if (lowIndices.length >= 2 && lowIndices[lowIndices.length - 1] - lowIndices[0] > 3) {
    if (lowIndices.length >= 3) {
      addPattern('triple_bottom', 0.75);
    } else {
      addPattern('double_bottom', 0.7);
    }
  }
  
  const highIndices = recentHighs.map((h, i) => h > maxHigh * 0.98 ? i : -1).filter(i => i >= 0);
  if (highIndices.length >= 2 && highIndices[highIndices.length - 1] - highIndices[0] > 3) {
    if (highIndices.length >= 3) {
      addPattern('triple_top', 0.75);
    } else {
      addPattern('double_top', 0.7);
    }
  }

  // === VOLUME PATTERNS ===
  if (last.volume && avgVolume > 0) {
    if (last.volume > avgVolume * 3) {
      addPattern('volume_climax', 0.75);
    } else if (last.volume > avgVolume * 2 && last.close > last.open) {
      addPattern('volume_breakout', 0.75);
    } else if (last.volume > avgVolume * 2 && last.close < last.open) {
      addPattern('volume_breakdown', 0.75);
    } else if (last.volume < avgVolume * 0.4) {
      addPattern('volume_dry_up', 0.6);
    }
  }

  // === SUPPORT/RESISTANCE ===
  const support = Math.min(...lows.slice(-20));
  const resistance = Math.max(...highs.slice(-20));
  
  if (last.low <= support * 1.005 && last.close > support) {
    addPattern('support_bounce', 0.75);
  }
  if (last.high >= resistance * 0.995 && last.close < resistance) {
    addPattern('resistance_reject', 0.75);
  }
  if (last.close > resistance * 1.01) {
    addPattern('resistance_break', 0.8);
  }
  if (last.close < support * 0.99) {
    addPattern('support_break', 0.8);
  }

  // === GAP PATTERNS ===
  if (prev) {
    if (last.open > prev.high * 1.005) {
      addPattern('gap_up', 0.7);
    }
    if (last.open < prev.low * 0.995) {
      addPattern('gap_down', 0.7);
    }
  }

  // Sort by confidence and return top patterns
  return patterns.sort((a, b) => b.confidence - a.confidence).slice(0, 8);
};

// ============================================
// 🐋 SMART MONEY FLOW DETECTOR
// ============================================
const generateSmartMoneyData = (data, symbol) => {
  if (!data || data.length < 20) return null;
  
  const recent = data.slice(-50);
  const avgVolume = recent.reduce((a, d) => a + (d.volume || 0), 0) / recent.length;
  const current = data[data.length - 1];
  
  // Detect unusual volume spikes (potential institutional activity)
  const volumeSpikes = recent.map((d, i) => ({
    date: d.date,
    volume: d.volume,
    price: d.close,
    isSpike: d.volume > avgVolume * 2,
    multiplier: (d.volume / avgVolume).toFixed(1),
    direction: d.close > d.open ? 'buy' : 'sell'
  })).filter(d => d.isSpike);

  // Simulated dark pool data (API-ready structure)
  const darkPoolPrints = [
    { time: '14:32:15', size: 125000, price: current.close * 0.998, type: 'buy', exchange: 'FINRA ADF' },
    { time: '13:45:22', size: 89000, price: current.close * 1.001, type: 'sell', exchange: 'IEX' },
    { time: '11:20:08', size: 250000, price: current.close * 0.995, type: 'buy', exchange: 'FINRA ADF' },
    { time: '10:15:33', size: 175000, price: current.close * 1.002, type: 'buy', exchange: 'NASDAQ PSX' },
  ];

  // Simulated options flow (API-ready for Unusual Whales, FlowAlgo, etc.)
  const optionsFlow = [
    { type: 'CALL', strike: Math.round(current.close * 1.05), expiry: '2025-01-17', premium: 2.5e6, sentiment: 'bullish', unusual: true },
    { type: 'PUT', strike: Math.round(current.close * 0.95), expiry: '2025-01-17', premium: 1.8e6, sentiment: 'bearish', unusual: true },
    { type: 'CALL', strike: Math.round(current.close * 1.10), expiry: '2025-02-21', premium: 4.2e6, sentiment: 'bullish', unusual: true },
    { type: 'CALL', strike: Math.round(current.close), expiry: '2025-01-10', premium: 890000, sentiment: 'bullish', unusual: true },
  ];

  // Simulated insider transactions (API-ready for SEC Edgar, OpenInsider)
  const insiderTransactions = [
    { name: 'CEO', action: 'BUY', shares: 50000, price: current.close * 0.95, date: '2024-12-10', value: 50000 * current.close * 0.95 },
    { name: 'CFO', action: 'SELL', shares: 25000, price: current.close * 1.02, date: '2024-12-05', value: 25000 * current.close * 1.02 },
    { name: 'Director', action: 'BUY', shares: 10000, price: current.close * 0.98, date: '2024-11-28', value: 10000 * current.close * 0.98 },
  ];

  // Calculate accumulation/distribution zones
  const accumulationZones = [];
  const distributionZones = [];
  
  for (let i = 10; i < recent.length; i++) {
    const slice = recent.slice(i - 10, i);
    const avgVol = slice.reduce((a, d) => a + d.volume, 0) / 10;
    const priceChange = (slice[9].close - slice[0].close) / slice[0].close;
    
    if (avgVol > avgVolume * 1.5 && priceChange > 0.02) {
      accumulationZones.push({ startPrice: slice[0].low, endPrice: slice[9].high, strength: 'strong' });
    } else if (avgVol > avgVolume * 1.5 && priceChange < -0.02) {
      distributionZones.push({ startPrice: slice[9].low, endPrice: slice[0].high, strength: 'strong' });
    }
  }

  // Smart money vs retail flow estimate
  const largeOrdersVolume = volumeSpikes.reduce((a, v) => a + v.volume, 0);
  const totalVolume = recent.reduce((a, d) => a + d.volume, 0);
  const smartMoneyPercent = Math.min(85, Math.max(15, (largeOrdersVolume / totalVolume * 100) + 30));
  const retailPercent = 100 - smartMoneyPercent;

  // Overall smart money signal
  const buySignals = darkPoolPrints.filter(d => d.type === 'buy').length + 
                     optionsFlow.filter(o => o.sentiment === 'bullish').length +
                     insiderTransactions.filter(i => i.action === 'BUY').length;
  const sellSignals = darkPoolPrints.filter(d => d.type === 'sell').length +
                      optionsFlow.filter(o => o.sentiment === 'bearish').length +
                      insiderTransactions.filter(i => i.action === 'SELL').length;
  
  const signal = buySignals > sellSignals + 2 ? 'ACCUMULATING' : 
                 sellSignals > buySignals + 2 ? 'DISTRIBUTING' : 'NEUTRAL';

  return {
    signal,
    confidence: Math.min(95, Math.max(40, 50 + (buySignals - sellSignals) * 8)),
    volumeSpikes,
    darkPoolPrints,
    optionsFlow,
    insiderTransactions,
    accumulationZones,
    distributionZones,
    flowBreakdown: { smart: smartMoneyPercent, retail: retailPercent },
    summary: {
      darkPoolVolume: darkPoolPrints.reduce((a, d) => a + d.size, 0),
      optionsPremium: optionsFlow.reduce((a, o) => a + o.premium, 0),
      insiderNetBuying: insiderTransactions.reduce((a, i) => a + (i.action === 'BUY' ? i.value : -i.value), 0)
    }
  };
};

// ============================================
// ⏰ TIME MACHINE - Historical Pattern Matching
// ============================================
const generateTimeMachineData = (data, patterns, symbol) => {
  if (!data || data.length < 50) return null;

  const current = data[data.length - 1];
  const recent20 = data.slice(-20);
  
  // Calculate current market "fingerprint"
  const currentFingerprint = {
    priceChange5d: ((current.close - data[data.length - 5]?.close) / data[data.length - 5]?.close * 100) || 0,
    priceChange20d: ((current.close - data[data.length - 20]?.close) / data[data.length - 20]?.close * 100) || 0,
    volatility: Math.sqrt(recent20.reduce((a, d) => a + Math.pow((d.close - d.open) / d.open, 2), 0) / 20) * 100,
    volumeTrend: recent20.slice(-5).reduce((a, d) => a + d.volume, 0) / recent20.slice(0, 5).reduce((a, d) => a + d.volume, 0),
    patterns: patterns.map(p => p.name),
  };

  // Simulated historical matches (API-ready for backtesting engine)
  const historicalMatches = [
    {
      symbol: symbol,
      matchDate: '2023-10-27',
      similarity: 87,
      setup: 'Bullish Engulfing + RSI Oversold',
      outcome: { days30: +18.5, days60: +24.2, days90: +31.8 },
      description: 'Similar oversold bounce pattern led to significant rally',
      chart: 'uptrend'
    },
    {
      symbol: symbol,
      matchDate: '2023-03-15',
      similarity: 82,
      setup: 'Double Bottom + Volume Spike',
      outcome: { days30: +12.3, days60: +8.7, days90: +22.1 },
      description: 'Banking crisis low, strong recovery followed',
      chart: 'uptrend'
    },
    {
      symbol: 'AAPL',
      matchDate: '2022-06-16',
      similarity: 79,
      setup: 'Bearish Flag + High Volatility',
      outcome: { days30: -8.2, days60: +5.4, days90: +15.6 },
      description: 'Market bottom pattern, choppy before rally',
      chart: 'volatile'
    },
    {
      symbol: 'MSFT',
      matchDate: '2020-03-23',
      similarity: 75,
      setup: 'Capitulation + Extreme Fear',
      outcome: { days30: +25.1, days60: +42.3, days90: +55.8 },
      description: 'COVID crash bottom, V-shaped recovery',
      chart: 'uptrend'
    },
    {
      symbol: symbol,
      matchDate: '2024-08-05',
      similarity: 71,
      setup: 'Gap Down + Volume Climax',
      outcome: { days30: +9.8, days60: +14.2, days90: +11.5 },
      description: 'Japan carry trade unwind, quick recovery',
      chart: 'uptrend'
    },
  ];

  // Calculate probability distribution based on historical matches
  const outcomes30d = historicalMatches.map(m => m.outcome.days30);
  const outcomes60d = historicalMatches.map(m => m.outcome.days60);
  const outcomes90d = historicalMatches.map(m => m.outcome.days90);
  
  const weightedAvg = (arr, weights) => {
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    return arr.reduce((sum, val, i) => sum + val * weights[i], 0) / totalWeight;
  };
  
  const weights = historicalMatches.map(m => m.similarity);
  
  const probabilityDistribution = {
    days30: {
      expected: weightedAvg(outcomes30d, weights),
      bullish: outcomes30d.filter(o => o > 5).length / outcomes30d.length * 100,
      bearish: outcomes30d.filter(o => o < -5).length / outcomes30d.length * 100,
      range: { min: Math.min(...outcomes30d), max: Math.max(...outcomes30d) }
    },
    days60: {
      expected: weightedAvg(outcomes60d, weights),
      bullish: outcomes60d.filter(o => o > 5).length / outcomes60d.length * 100,
      bearish: outcomes60d.filter(o => o < -5).length / outcomes60d.length * 100,
      range: { min: Math.min(...outcomes60d), max: Math.max(...outcomes60d) }
    },
    days90: {
      expected: weightedAvg(outcomes90d, weights),
      bullish: outcomes90d.filter(o => o > 5).length / outcomes90d.length * 100,
      bearish: outcomes90d.filter(o => o < -5).length / outcomes90d.length * 100,
      range: { min: Math.min(...outcomes90d), max: Math.max(...outcomes90d) }
    }
  };

  // Generate price targets based on historical outcomes
  const priceTargets = {
    bullCase: current.close * (1 + Math.max(...outcomes90d) / 100),
    baseCase: current.close * (1 + weightedAvg(outcomes90d, weights) / 100),
    bearCase: current.close * (1 + Math.min(...outcomes90d) / 100),
  };

  return {
    currentFingerprint,
    historicalMatches,
    probabilityDistribution,
    priceTargets,
    bestMatch: historicalMatches[0],
    avgSimilarity: historicalMatches.reduce((a, m) => a + m.similarity, 0) / historicalMatches.length,
    signalStrength: historicalMatches[0].similarity > 80 ? 'STRONG' : historicalMatches[0].similarity > 65 ? 'MODERATE' : 'WEAK'
  };
};

// ============================================
// 🎯 AI SCENARIO SIMULATOR
// ============================================
const generateScenarioData = (data, symbol, companyName) => {
  if (!data || data.length < 20) return null;

  const current = data[data.length - 1];
  const volatility = data.slice(-20).reduce((a, d) => a + Math.abs(d.close - d.open) / d.open, 0) / 20 * 100;
  
  // Pre-built scenarios (API-ready for AI/ML integration)
  const scenarios = [
    {
      id: 'fed_hike',
      name: 'Fed Raises Rates 0.25%',
      category: 'monetary',
      icon: 'DollarSign',
      probability: 35,
      impact: {
        immediate: -2.5,
        week1: -1.8,
        month1: +0.5,
        reasoning: 'Initial selloff typical, but often priced in. Tech stocks sensitive to rates.'
      },
      historicalEvents: ['Dec 2022', 'Nov 2022', 'Sep 2022'],
      confidence: 78
    },
    {
      id: 'fed_cut',
      name: 'Fed Cuts Rates 0.25%',
      category: 'monetary',
      icon: 'TrendingUp',
      probability: 45,
      impact: {
        immediate: +3.2,
        week1: +4.5,
        month1: +8.2,
        reasoning: 'Rate cuts historically bullish for equities, especially growth stocks.'
      },
      historicalEvents: ['Sep 2024', 'Jul 2019', 'Oct 2019'],
      confidence: 82
    },
    {
      id: 'earnings_beat',
      name: 'Earnings Beat by 10%+',
      category: 'company',
      icon: 'TrendingUp',
      probability: 40,
      impact: {
        immediate: +5.8,
        week1: +7.2,
        month1: +12.5,
        reasoning: 'Strong beats typically see gap up with continuation. Watch guidance.'
      },
      historicalEvents: ['Q3 2024', 'Q1 2024', 'Q4 2023'],
      confidence: 75
    },
    {
      id: 'earnings_miss',
      name: 'Earnings Miss by 10%+',
      category: 'company',
      icon: 'TrendingDown',
      probability: 20,
      impact: {
        immediate: -8.5,
        week1: -6.2,
        month1: -4.8,
        reasoning: 'Misses punished hard initially but often recover if guidance intact.'
      },
      historicalEvents: ['Q2 2022', 'Q4 2021'],
      confidence: 72
    },
    {
      id: 'recession',
      name: 'Recession Declared',
      category: 'macro',
      icon: 'AlertTriangle',
      probability: 15,
      impact: {
        immediate: -12.0,
        week1: -15.5,
        month1: -22.0,
        reasoning: 'Major risk-off event. Quality names recover faster. Cash is king initially.'
      },
      historicalEvents: ['Mar 2020', 'Dec 2008', 'Mar 2001'],
      confidence: 85
    },
    {
      id: 'sector_rotation',
      name: 'Tech → Value Rotation',
      category: 'market',
      icon: 'Shuffle',
      probability: 30,
      impact: {
        immediate: -4.2,
        week1: -6.8,
        month1: -8.5,
        reasoning: 'Growth stocks underperform during rotations. Can last 1-3 months.'
      },
      historicalEvents: ['Sep 2024', 'Nov 2021', 'Sep 2020'],
      confidence: 68
    },
    {
      id: 'china_taiwan',
      name: 'China-Taiwan Escalation',
      category: 'geopolitical',
      icon: 'Globe',
      probability: 8,
      impact: {
        immediate: -18.0,
        week1: -25.0,
        month1: -15.0,
        reasoning: 'Semiconductor supply chain disruption. Initial panic, then assessment.'
      },
      historicalEvents: ['Aug 2022 (Pelosi visit)'],
      confidence: 60
    },
    {
      id: 'ai_breakthrough',
      name: 'Major AI Breakthrough Announced',
      category: 'technology',
      icon: 'Brain',
      probability: 25,
      impact: {
        immediate: +8.5,
        week1: +12.0,
        month1: +18.5,
        reasoning: 'AI-related names surge on breakthroughs. Rising tide lifts all boats.'
      },
      historicalEvents: ['Nov 2022 (ChatGPT)', 'Mar 2023 (GPT-4)'],
      confidence: 70
    },
  ];

  // Monte Carlo simulation results (simplified - ready for real MC engine)
  const monteCarloResults = {
    simulations: 10000,
    timeHorizon: '90 days',
    percentiles: {
      p5: current.close * 0.72,
      p25: current.close * 0.88,
      p50: current.close * 1.02,
      p75: current.close * 1.18,
      p95: current.close * 1.35,
    },
    expectedValue: current.close * 1.05,
    standardDeviation: current.close * 0.22,
    probabilityOfProfit: 58,
    probabilityOfLoss: 42,
    maxDrawdown: -28,
    sharpeRatio: 0.85
  };

  // Position sizing recommendation based on volatility
  const kellyFraction = 0.12; // Simplified Kelly Criterion
  const recommendedPosition = {
    aggressive: 8,
    moderate: 5,
    conservative: 2,
    stopLoss: current.close * 0.92,
    takeProfit1: current.close * 1.08,
    takeProfit2: current.close * 1.15,
    riskRewardRatio: 2.1
  };

  return {
    scenarios,
    monteCarloResults,
    recommendedPosition,
    currentVolatility: volatility,
    riskLevel: volatility > 3 ? 'HIGH' : volatility > 1.5 ? 'MODERATE' : 'LOW',
    summary: {
      bullishScenarios: scenarios.filter(s => s.impact.month1 > 0).length,
      bearishScenarios: scenarios.filter(s => s.impact.month1 < 0).length,
      highProbEvents: scenarios.filter(s => s.probability > 30)
    }
  };
};

// ============================================
// INDICATOR DATA - TradingView Style
// ============================================
const INDICATOR_TABS = [
  { id: 'indicators', label: 'Indicators' },
  { id: 'strategies', label: 'Strategies' },
  { id: 'profiles', label: 'Profiles' },
];

const ALL_INDICATORS = [
  // Volume & Distribution
  { id: 'volume', name: 'Volume', category: 'volume', color: '#6b7280' },
  { id: 'accumulationdistribution', name: 'Accumulation/Distribution', category: 'volume', color: '#3b82f6', pro: true },
  { id: 'adl', name: 'Advance Decline Line', category: 'volume', color: '#22c55e', pro: true },
  { id: 'adr', name: 'Advance Decline Ratio', category: 'volume', color: '#f59e0b', pro: true },
  
  // Moving Averages
  { id: 'sma9', name: 'SMA 9', category: 'ma', color: '#22c55e' },
  { id: 'sma20', name: 'SMA 20', category: 'ma', color: '#f59e0b' },
  { id: 'sma50', name: 'SMA 50', category: 'ma', color: '#3b82f6' },
  { id: 'sma100', name: 'SMA 100', category: 'ma', color: '#a855f7' },
  { id: 'sma200', name: 'SMA 200', category: 'ma', color: '#ef4444' },
  { id: 'ema9', name: 'EMA 9', category: 'ma', color: '#84cc16' },
  { id: 'ema12', name: 'EMA 12', category: 'ma', color: '#06b6d4' },
  { id: 'ema20', name: 'EMA 20', category: 'ma', color: '#8b5cf6' },
  { id: 'ema26', name: 'EMA 26', category: 'ma', color: '#f97316' },
  { id: 'ema50', name: 'EMA 50', category: 'ma', color: '#ec4899' },
  { id: 'arnaudlegoux', name: 'Arnaud Legoux Moving Average', category: 'ma', color: '#14b8a6', pro: true },
  
  // Oscillators
  { id: 'aroon', name: 'Aroon', category: 'oscillators', color: '#8b5cf6' },
  { id: 'aroonoscillator', name: 'Aroon Oscillator', category: 'oscillators', color: '#3b82f6', isNew: true },
  { id: 'rsi', name: 'RSI', category: 'oscillators', color: '#8b5cf6', pro: true },
  { id: 'macd', name: 'MACD', category: 'oscillators', color: '#3b82f6', pro: true },
  { id: 'stochastic', name: 'Stochastic', category: 'oscillators', color: '#f59e0b', pro: true },
  { id: 'stochrsi', name: 'Stochastic RSI', category: 'oscillators', color: '#ec4899', pro: true },
  { id: 'cci', name: 'CCI', category: 'oscillators', color: '#14b8a6', pro: true },
  { id: 'williams', name: 'Williams %R', category: 'oscillators', color: '#ef4444', pro: true },
  { id: 'momentum', name: 'Momentum', category: 'oscillators', color: '#22c55e', pro: true },
  { id: 'roc', name: 'ROC', category: 'oscillators', color: '#f97316', pro: true },
  
  // Volatility
  { id: 'bollinger', name: 'Bollinger Bands', category: 'volatility', color: '#ec4899' },
  { id: 'atr', name: 'ATR', category: 'volatility', color: '#f59e0b', pro: true },
  { id: 'keltner', name: 'Keltner Channel', category: 'volatility', color: '#3b82f6', pro: true },
  { id: 'donchian', name: 'Donchian Channel', category: 'volatility', color: '#22c55e', pro: true },
  { id: 'averagedailyrange', name: 'Average Daily Range', category: 'volatility', color: '#a855f7', pro: true },
  
  // Trend
  { id: 'adx', name: 'Average Directional Index', category: 'trend', color: '#f59e0b', pro: true },
  { id: 'psar', name: 'Parabolic SAR', category: 'trend', color: '#ef4444', pro: true },
  { id: 'ichimoku', name: 'Ichimoku Cloud', category: 'trend', color: '#3b82f6', pro: true },
  { id: 'supertrend', name: 'SuperTrend', category: 'trend', color: '#22c55e', pro: true },
  
  // Fibonacci & Support
  { id: 'autofibext', name: 'Auto Fib Extension', category: 'fib', color: '#f59e0b', pro: true },
  { id: 'autofibretrace', name: 'Auto Fib Retracement', category: 'fib', color: '#3b82f6', pro: true },
  { id: 'autopitchfork', name: 'Auto Pitchfork', category: 'fib', color: '#8b5cf6', pro: true },
  { id: 'autotrendlines', name: 'Auto Trendlines', category: 'fib', color: '#22c55e', isBeta: true },
  
  // Volume
  { id: 'vwap', name: 'VWAP', category: 'volume', color: '#14b8a6', pro: true },
  { id: 'obv', name: 'OBV', category: 'volume', color: '#3b82f6', pro: true },
  { id: 'mfi', name: 'MFI', category: 'volume', color: '#8b5cf6', pro: true },
  { id: 'cmf', name: 'CMF', category: 'volume', color: '#f59e0b', pro: true },
  { id: '24hourvolume', name: '24-hour Volume', category: 'volume', color: '#6b7280' },
];

const STRATEGIES = [
  { id: 'movingavg_cross', name: 'Moving Average Crossover', author: 'TradingView', color: '#3b82f6' },
  { id: 'rsi_divergence', name: 'RSI Divergence Strategy', author: 'Community', color: '#8b5cf6', pro: true },
  { id: 'macd_strategy', name: 'MACD Strategy', author: 'TradingView', color: '#22c55e', pro: true },
  { id: 'bollinger_squeeze', name: 'Bollinger Band Squeeze', author: 'Community', color: '#f59e0b', pro: true },
  { id: 'trend_following', name: 'Trend Following', author: 'TradingView', color: '#ef4444', pro: true },
];

const PATTERNS = [
  // Candlestick Patterns - Bullish
  { id: 'pattern_hammer', name: 'Hammer', category: 'bullish', color: '#22c55e', desc: 'Bullish reversal pattern' },
  { id: 'pattern_invhammer', name: 'Inverted Hammer', category: 'bullish', color: '#22c55e', desc: 'Bullish reversal pattern' },
  { id: 'pattern_bullishengulfing', name: 'Bullish Engulfing', category: 'bullish', color: '#22c55e', desc: 'Strong bullish reversal' },
  { id: 'pattern_piercing', name: 'Piercing Line', category: 'bullish', color: '#22c55e', desc: 'Bullish reversal pattern' },
  { id: 'pattern_morningstar', name: 'Morning Star', category: 'bullish', color: '#22c55e', desc: 'Strong bullish reversal' },
  { id: 'pattern_threewhitesoldiers', name: 'Three White Soldiers', category: 'bullish', color: '#22c55e', desc: 'Strong bullish continuation' },
  { id: 'pattern_bullishharami', name: 'Bullish Harami', category: 'bullish', color: '#22c55e', desc: 'Potential bullish reversal' },
  { id: 'pattern_dragonfly', name: 'Dragonfly Doji', category: 'bullish', color: '#22c55e', desc: 'Bullish reversal signal' },
  
  // Candlestick Patterns - Bearish
  { id: 'pattern_hangingman', name: 'Hanging Man', category: 'bearish', color: '#ef4444', desc: 'Bearish reversal pattern' },
  { id: 'pattern_shootingstar', name: 'Shooting Star', category: 'bearish', color: '#ef4444', desc: 'Bearish reversal pattern' },
  { id: 'pattern_bearishengulfing', name: 'Bearish Engulfing', category: 'bearish', color: '#ef4444', desc: 'Strong bearish reversal' },
  { id: 'pattern_darkcloud', name: 'Dark Cloud Cover', category: 'bearish', color: '#ef4444', desc: 'Bearish reversal pattern' },
  { id: 'pattern_eveningstar', name: 'Evening Star', category: 'bearish', color: '#ef4444', desc: 'Strong bearish reversal' },
  { id: 'pattern_threeblackcrows', name: 'Three Black Crows', category: 'bearish', color: '#ef4444', desc: 'Strong bearish continuation' },
  { id: 'pattern_bearishharami', name: 'Bearish Harami', category: 'bearish', color: '#ef4444', desc: 'Potential bearish reversal' },
  { id: 'pattern_gravestone', name: 'Gravestone Doji', category: 'bearish', color: '#ef4444', desc: 'Bearish reversal signal' },
  
  // Chart Patterns
  { id: 'pattern_headshoulders', name: 'Head and Shoulders', category: 'chart', color: '#3b82f6', desc: 'Reversal pattern', pro: true },
  { id: 'pattern_invheadshoulders', name: 'Inverse Head and Shoulders', category: 'chart', color: '#3b82f6', desc: 'Bullish reversal', pro: true },
  { id: 'pattern_doubletop', name: 'Double Top', category: 'chart', color: '#ef4444', desc: 'Bearish reversal', pro: true },
  { id: 'pattern_doublebottom', name: 'Double Bottom', category: 'chart', color: '#22c55e', desc: 'Bullish reversal', pro: true },
  { id: 'pattern_tripletop', name: 'Triple Top', category: 'chart', color: '#ef4444', desc: 'Strong bearish reversal', pro: true },
  { id: 'pattern_triplebottom', name: 'Triple Bottom', category: 'chart', color: '#22c55e', desc: 'Strong bullish reversal', pro: true },
  { id: 'pattern_ascendingtriangle', name: 'Ascending Triangle', category: 'chart', color: '#22c55e', desc: 'Bullish continuation', pro: true },
  { id: 'pattern_descendingtriangle', name: 'Descending Triangle', category: 'chart', color: '#ef4444', desc: 'Bearish continuation', pro: true },
  { id: 'pattern_symmetricaltriangle', name: 'Symmetrical Triangle', category: 'chart', color: '#f59e0b', desc: 'Continuation pattern', pro: true },
  { id: 'pattern_risingwedge', name: 'Rising Wedge', category: 'chart', color: '#ef4444', desc: 'Bearish reversal', pro: true },
  { id: 'pattern_fallingwedge', name: 'Falling Wedge', category: 'chart', color: '#22c55e', desc: 'Bullish reversal', pro: true },
  { id: 'pattern_bullflag', name: 'Bull Flag', category: 'chart', color: '#22c55e', desc: 'Bullish continuation', pro: true },
  { id: 'pattern_bearflag', name: 'Bear Flag', category: 'chart', color: '#ef4444', desc: 'Bearish continuation', pro: true },
  { id: 'pattern_cuphandle', name: 'Cup and Handle', category: 'chart', color: '#22c55e', desc: 'Bullish continuation', pro: true },
  { id: 'pattern_channel', name: 'Channel (Up/Down)', category: 'chart', color: '#8b5cf6', desc: 'Trend channel', pro: true },
  
  // Gap Patterns
  { id: 'pattern_gapup', name: 'Gap Up', category: 'gap', color: '#22c55e', desc: 'Bullish gap' },
  { id: 'pattern_gapdown', name: 'Gap Down', category: 'gap', color: '#ef4444', desc: 'Bearish gap' },
  { id: 'pattern_breakawaygap', name: 'Breakaway Gap', category: 'gap', color: '#3b82f6', desc: 'Trend initiation', pro: true },
  { id: 'pattern_runawygap', name: 'Runaway Gap', category: 'gap', color: '#8b5cf6', desc: 'Trend continuation', pro: true },
  { id: 'pattern_exhaustiongap', name: 'Exhaustion Gap', category: 'gap', color: '#f59e0b', desc: 'Trend exhaustion', pro: true },
];

// Asset categories
const ASSET_CATEGORIES = {
  stocks: { label: 'Stocks', sectors: [{ id: 'all', label: 'All Stocks' }, { id: 'tech', label: 'Technology' }, { id: 'finance', label: 'Financials' }, { id: 'healthcare', label: 'Healthcare' }] },
  crypto: { label: 'Crypto', sectors: [{ id: 'all', label: 'All Crypto' }, { id: 'layer1', label: 'Layer 1' }, { id: 'defi', label: 'DeFi' }] },
  etfs: { label: 'ETFs', sectors: [{ id: 'all', label: 'All ETFs' }, { id: 'index', label: 'Index' }] }
};

const POPULAR_ASSETS = {
  stocks: [
    { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock', sector: 'tech' },
    { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock', sector: 'tech' },
    { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock', sector: 'tech' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock', sector: 'tech' },
    { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock', sector: 'tech' },
    { symbol: 'META', name: 'Meta Platforms', type: 'stock', sector: 'tech' },
    { symbol: 'JPM', name: 'JPMorgan Chase', type: 'stock', sector: 'finance' },
  ],
  crypto: [
    { symbol: 'BTC', name: 'Bitcoin', type: 'crypto', sector: 'layer1' },
    { symbol: 'ETH', name: 'Ethereum', type: 'crypto', sector: 'layer1' },
    { symbol: 'SOL', name: 'Solana', type: 'crypto', sector: 'layer1' },
  ],
  etfs: [
    { symbol: 'SPY', name: 'S&P 500 ETF', type: 'etf', sector: 'index' },
    { symbol: 'QQQ', name: 'Nasdaq 100 ETF', type: 'etf', sector: 'index' },
  ]
};

// ============================================
// INDICATORS MODAL - TradingView Style
// ============================================
const IndicatorsModal = ({ isOpen, onClose, indicators, setIndicators, isPro, onOpenAI, symbol }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('indicators');
  const [sidebarSection, setSidebarSection] = useState('technicals');
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => searchRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) { document.addEventListener('keydown', handleEsc); document.body.style.overflow = 'hidden'; }
    return () => { document.removeEventListener('keydown', handleEsc); document.body.style.overflow = ''; };
  }, [isOpen, onClose]);

  const toggleIndicator = (id, requiresPro) => {
    if (requiresPro && !isPro) return;
    setIndicators(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredIndicators = useMemo(() => {
    let results;
    if (activeTab === 'strategies') {
      results = STRATEGIES;
    } else if (activeTab === 'patterns') {
      results = PATTERNS;
    } else {
      results = ALL_INDICATORS;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      results = results.filter(ind => ind.name.toLowerCase().includes(q));
    }
    return results;
  }, [activeTab, searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="tv-modal-overlay" onClick={onClose}>
      <div className="tv-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="tv-modal-header">
          <h2>Indicators, metrics, and strategies</h2>
          <button className="tv-modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Search */}
        <div className="tv-modal-search">
          <Search size={18} />
          <input ref={searchRef} type="text" placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>

        {/* Body */}
        <div className="tv-modal-body">
          {/* Left Sidebar */}
          <div className="tv-modal-sidebar">
            <div className="tv-sidebar-section">
              <div className="tv-sidebar-label">PERSONAL</div>
              <button className={`tv-sidebar-item ${sidebarSection === 'myscripts' ? 'active' : ''}`} onClick={() => setSidebarSection('myscripts')}>
                <Code size={16} /><span>My scripts</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'inviteonly' ? 'active' : ''}`} onClick={() => setSidebarSection('inviteonly')}>
                <Users size={16} /><span>Invite-only</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'purchased' ? 'active' : ''}`} onClick={() => setSidebarSection('purchased')}>
                <ShoppingBag size={16} /><span>Purchased</span>
              </button>
            </div>

            <div className="tv-sidebar-section">
              <div className="tv-sidebar-label">BUILT-IN</div>
              <button className={`tv-sidebar-item ${sidebarSection === 'technicals' ? 'active' : ''}`} onClick={() => setSidebarSection('technicals')}>
                <BarChart2 size={16} /><span>Technicals</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'fundamentals' ? 'active' : ''}`} onClick={() => setSidebarSection('fundamentals')}>
                <TrendingUp size={16} /><span>Fundamentals</span>
              </button>
            </div>

            <div className="tv-sidebar-section">
              <div className="tv-sidebar-label">COMMUNITY</div>
              <button className={`tv-sidebar-item ${sidebarSection === 'editorspicks' ? 'active' : ''}`} onClick={() => setSidebarSection('editorspicks')}>
                <Award size={16} /><span>Editor's picks</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'top' ? 'active' : ''}`} onClick={() => setSidebarSection('top')}>
                <Star size={16} /><span>Top</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'trending' ? 'active' : ''}`} onClick={() => setSidebarSection('trending')}>
                <Sparkles size={16} /><span>Trending</span>
              </button>
              <button className={`tv-sidebar-item ${sidebarSection === 'store' ? 'active' : ''}`} onClick={() => setSidebarSection('store')}>
                <ShoppingBag size={16} /><span>Store</span>
              </button>
            </div>
          </div>

          {/* Right Content */}
          <div className="tv-modal-content">
            {/* Tabs */}
            <div className="tv-modal-tabs">
              {INDICATOR_TABS.map(tab => (
                <button key={tab.id} className={`tv-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Script Name Header */}
            <div className="tv-list-header">SCRIPT NAME</div>

            {/* Indicator List */}
            <div className="tv-indicator-list">
              {filteredIndicators.map(ind => (
                <div
                  key={ind.id}
                  className={`tv-indicator-row ${indicators[ind.id] ? 'active' : ''} ${ind.pro && !isPro ? 'locked' : ''}`}
                  onClick={() => toggleIndicator(ind.id, ind.pro)}
                >
                  {ind.author ? (
                    <Star size={14} className="tv-favorite-icon" />
                  ) : indicators[ind.id] ? (
                    <Check size={14} className="tv-check-icon" />
                  ) : (
                    <div className="tv-indicator-spacer" />
                  )}
                  <span className="tv-indicator-name">{ind.name}</span>
                  {ind.isNew && <span className="tv-badge tv-badge-new">NEW</span>}
                  {ind.isBeta && <span className="tv-badge tv-badge-beta">BETA</span>}
                  {ind.pro && !isPro && <Lock size={12} className="tv-lock-icon" />}
                  {ind.author && <span className="tv-indicator-author">by {ind.author}</span>}
                </div>
              ))}
              {filteredIndicators.length === 0 && (
                <div className="tv-no-results">No indicators found matching "{searchQuery}"</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Create Script Button */}
        <div className="tv-modal-footer">
          <button className="tv-create-script-btn">
            <Plus size={16} />
            <span>Create your own script</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SYMBOL SELECTOR COMPONENT
// ============================================
const SymbolSelector = ({ symbol, companyName, onSymbolChange, assetType, sectorFilter, onAssetTypeChange, onSectorFilterChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSectorDropdown, setShowSectorDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const sectorRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) { setShowDropdown(false); setSearchQuery(''); }
      if (sectorRef.current && !sectorRef.current.contains(e.target)) setShowSectorDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentSectors = ASSET_CATEGORIES[assetType]?.sectors || [];
  const currentAssets = POPULAR_ASSETS[assetType] || [];
  const currentSectorLabel = currentSectors.find(s => s.id === sectorFilter)?.label || 'All';
  const filteredAssets = useMemo(() => {
    let assets = currentAssets;
    if (sectorFilter !== 'all') assets = assets.filter(a => a.sector === sectorFilter);
    if (searchQuery) { const q = searchQuery.toLowerCase(); assets = assets.filter(a => a.symbol.toLowerCase().includes(q) || a.name.toLowerCase().includes(q)); }
    return assets;
  }, [currentAssets, sectorFilter, searchQuery]);

  const getTypeIcon = (type) => type === 'crypto' ? '₿' : type === 'etf' ? 'E' : 'S';

  return (
    <div className="fs-symbol-selector" ref={dropdownRef} onClick={e => e.stopPropagation()}>
      <button className="fs-symbol-btn-minimal" onClick={() => setShowDropdown(!showDropdown)}>
        <Search size={16} className="fs-symbol-search-icon" />
        <span className="fs-symbol-ticker-only">{symbol}</span>
        <ChevronDown size={14} className={showDropdown ? 'flip' : ''} />
      </button>
      {showDropdown && (
        <div className="fs-symbol-dropdown">
          <div className="fs-symbol-search"><Search size={14} /><input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div>
          <div className="fs-filter-row">
            <div className="fs-symbol-tabs">{Object.entries(ASSET_CATEGORIES).map(([key, cat]) => (<button key={key} className={assetType === key ? 'active' : ''} onClick={() => { onAssetTypeChange(key); onSectorFilterChange('all'); }}>{cat.label}</button>))}</div>
            <div className="fs-sector-dropdown" ref={sectorRef}>
              <button className="fs-sector-btn" onClick={(e) => { e.stopPropagation(); setShowSectorDropdown(!showSectorDropdown); }}><Filter size={12} />{currentSectorLabel}<ChevronDown size={12} className={showSectorDropdown ? 'flip' : ''} /></button>
              {showSectorDropdown && (<div className="fs-sector-menu">{currentSectors.map(s => (<button key={s.id} className={`fs-sector-option ${sectorFilter === s.id ? 'selected' : ''}`} onClick={(e) => { e.stopPropagation(); onSectorFilterChange(s.id); setShowSectorDropdown(false); }}>{s.label}{sectorFilter === s.id && <Check size={12} />}</button>))}</div>)}
            </div>
          </div>
          <div className="fs-symbol-list">{filteredAssets.map(a => (<button key={a.symbol} className={`fs-symbol-item ${symbol === a.symbol ? 'active' : ''}`} onClick={() => { onSymbolChange(a.symbol, a.name, a.type); setShowDropdown(false); }}><span className={`fs-symbol-type ${a.type}`}>{getTypeIcon(a.type)}</span><span className="fs-symbol-ticker">{a.symbol}</span><span className="fs-symbol-full">{a.name}</span></button>))}</div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MINI CHART - Simplified chart for multi-layout
// ============================================
const MiniChart = memo(({ symbol, timeframe, onSymbolChange, isActive, onClick, onExpand }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/chart/${symbol}?timeframe=${timeframe}`);
        if (response.ok) {
          const result = await response.json();
          setData(result.data || result);
        } else {
          setData(generateMockData(symbol, timeframe));
        }
      } catch {
        setData(generateMockData(symbol, timeframe));
      }
      setLoading(false);
    };
    fetchData();
  }, [symbol, timeframe]);
  
  if (loading || !data?.length) {
    return (
      <div className={`mini-chart ${isActive ? 'active' : ''}`} onClick={onClick}>
        <div className="mini-chart-header">
          <span className="mini-chart-symbol">{symbol}</span>
        </div>
        <div className="mini-chart-loading">Loading...</div>
      </div>
    );
  }
  
  const current = data[data.length - 1];
  const first = data[0];
  const change = current.close - first.close;
  const changePct = (change / first.close) * 100;
  const isUp = change >= 0;
  
  const width = 100;
  const height = 60;
  const padding = 2;
  
  const prices = data.map(d => d.close);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice || 1;
  
  const getX = (i) => padding + (i / (data.length - 1)) * (width - padding * 2);
  const getY = (price) => padding + ((maxPrice - price) / priceRange) * (height - padding * 2);
  
  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${getX(i)},${getY(d.close)}`).join(' ');
  const areaPath = `${linePath} L${getX(data.length - 1)},${height - padding} L${getX(0)},${height - padding} Z`;
  
  return (
    <div 
      className={`mini-chart ${isActive ? 'active' : ''} ${hovered ? 'hovered' : ''}`} 
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="mini-chart-header">
        <span className="mini-chart-symbol">{symbol}</span>
        <span className={`mini-chart-change ${isUp ? 'up' : 'down'}`}>
          {isUp ? '+' : ''}{changePct.toFixed(2)}%
        </span>
      </div>
      <div className="mini-chart-price">${formatPrice(current.close)}</div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`miniGrad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={isUp ? '#26a69a' : '#ef5350'} stopOpacity="0.3"/>
            <stop offset="100%" stopColor={isUp ? '#26a69a' : '#ef5350'} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#miniGrad-${symbol})`} />
        <path d={linePath} fill="none" stroke={isUp ? '#26a69a' : '#ef5350'} strokeWidth="1.5" />
      </svg>
      {hovered && (
        <div className="mini-chart-actions">
          <button 
            className="mini-chart-btn"
            onClick={(e) => {
              e.stopPropagation();
              const newSymbol = prompt('Enter symbol:', symbol);
              if (newSymbol && newSymbol !== symbol) {
                onSymbolChange(newSymbol.toUpperCase());
              }
            }}
            title="Change Symbol"
          >
            <PenTool size={12} />
          </button>
          {onExpand && (
            <button 
              className="mini-chart-btn expand"
              onClick={(e) => {
                e.stopPropagation();
                onExpand(symbol);
              }}
              title="Expand to Split Screen"
            >
              <Maximize2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
});

// ============================================
// SPLIT CHART VIEW - Full chart for split screen
// ============================================
const SplitChartView = memo(({ symbol, timeframe, chartType, chartSettings, onFullscreen, onRemove, onSymbolChange, onTimeframeChange, onChartTypeChange, hoveredDate, onHover, showControls = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState(false);
  const [symbolInput, setSymbolInput] = useState(symbol);
  const [showTimeframeMenu, setShowTimeframeMenu] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 300 });
  const [localHoveredIndex, setLocalHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState(null); // For cursor tooltip
  
  const timeframes = ['1D', '1W', '1M', '3M', '6M', '1Y', '5Y'];
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/chart/${symbol}?timeframe=${timeframe}`);
        if (response.ok) {
          const result = await response.json();
          setData(result.data || result);
        } else {
          setData(generateMockData(symbol, timeframe));
        }
      } catch {
        setData(generateMockData(symbol, timeframe));
      }
      setLoading(false);
    };
    fetchData();
  }, [symbol, timeframe]);
  
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    if (editingSymbol && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingSymbol]);
  
  const handleSymbolSubmit = () => {
    if (symbolInput.trim() && symbolInput !== symbol && onSymbolChange) {
      onSymbolChange(symbolInput.toUpperCase().trim());
    }
    setEditingSymbol(false);
  };
  
  if (loading || !data?.length) {
    return (
      <div className="split-chart-content" ref={containerRef}>
        <div className="split-chart-loading">Loading {symbol}...</div>
      </div>
    );
  }
  
  const current = data[data.length - 1];
  const first = data[0];
  
  // Safety check for required properties
  if (!current?.close || !first?.close) {
    return (
      <div className="split-chart-content" ref={containerRef}>
        <div className="split-chart-loading">Invalid data for {symbol}</div>
      </div>
    );
  }
  
  const change = current.close - first.close;
  const changePct = (change / first.close) * 100;
  const isUp = change >= 0;
  
  // Volume calculations
  const volumes = data.map(d => d.volume || 0);
  const maxVolume = Math.max(...volumes) || 1;
  const volumeHeight = 40; // Height reserved for volume bars
  
  const padding = { top: 45, right: 60, bottom: 25 + volumeHeight, left: 10 };
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height - padding.top - padding.bottom - volumeHeight;
  
  // Filter valid price data
  const validData = data.filter(d => d.high != null && d.low != null);
  const prices = validData.flatMap(d => [d.high, d.low]);
  const minPrice = Math.min(...prices) || current.close * 0.9;
  const maxPrice = Math.max(...prices) || current.close * 1.1;
  const priceRange = (maxPrice - minPrice) || 1;
  const paddedMin = minPrice - priceRange * 0.05;
  const paddedMax = maxPrice + priceRange * 0.05;
  const paddedRange = paddedMax - paddedMin;
  
  const candleWidth = Math.max(1, Math.min(6, (chartWidth / data.length) * 0.6));
  const candleGap = (chartWidth - candleWidth * data.length) / (data.length + 1);
  
  const getX = (i) => padding.left + candleGap + i * (candleWidth + candleGap);
  const getY = (price) => padding.top + ((paddedMax - price) / paddedRange) * chartHeight;
  const getVolumeY = (vol) => dimensions.height - padding.bottom + volumeHeight - (vol / maxVolume) * volumeHeight;
  
  return (
    <div 
      className="split-chart-content" 
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowTimeframeMenu(false); }}
    >
      {/* Chart Header Bar */}
      <div className="split-chart-header-bar">
        <div className="split-chart-header-left">
          {/* Symbol - Editable on click */}
          {editingSymbol ? (
            <input
              ref={inputRef}
              className="split-chart-symbol-input-inline"
              value={symbolInput}
              onChange={(e) => setSymbolInput(e.target.value.toUpperCase())}
              onBlur={handleSymbolSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSymbolSubmit();
                if (e.key === 'Escape') { setSymbolInput(symbol); setEditingSymbol(false); }
              }}
            />
          ) : (
            <span 
              className="split-chart-symbol" 
              onClick={() => { if (onSymbolChange) { setSymbolInput(symbol); setEditingSymbol(true); }}}
              title={onSymbolChange ? "Click to change symbol" : ""}
              style={{ cursor: onSymbolChange ? 'pointer' : 'default' }}
            >
              {symbol}
            </span>
          )}
          
          {/* Timeframe Dropdown */}
          {onTimeframeChange && (
            <div className="split-chart-tf-dropdown">
              <button 
                className="split-chart-tf-btn"
                onClick={() => setShowTimeframeMenu(!showTimeframeMenu)}
              >
                {timeframe}
                <ChevronDown size={10} />
              </button>
              {showTimeframeMenu && (
                <div className="split-chart-tf-menu">
                  {timeframes.map(tf => (
                    <button 
                      key={tf}
                      className={tf === timeframe ? 'active' : ''}
                      onClick={() => { 
                        onTimeframeChange(tf); 
                        setShowTimeframeMenu(false); 
                      }}
                    >
                      {tf}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Chart Type Toggle */}
          {onChartTypeChange && (
            <div className="split-chart-type-toggle">
              <button 
                className={chartType === 'candle' ? 'active' : ''}
                onClick={() => onChartTypeChange('candle')}
                title="Candlestick"
              >
                <BarChart2 size={12} />
              </button>
              <button 
                className={chartType === 'area' ? 'active' : ''}
                onClick={() => onChartTypeChange('area')}
                title="Area"
              >
                <Activity size={12} />
              </button>
            </div>
          )}
          
          {/* Price & Change - Show hovered data or current */}
          {(() => {
            const hoveredData = localHoveredIndex !== null && data[localHoveredIndex] ? data[localHoveredIndex] : null;
            const displayData = hoveredData || current;
            if (!displayData?.close) return null;
            const displayChange = hoveredData 
              ? ((hoveredData.close - first.close) / first.close) * 100
              : changePct;
            const displayIsUp = hoveredData 
              ? hoveredData.close >= first.close
              : isUp;
            
            return (
              <>
                {hoveredData && (
                  <span className="split-chart-date">
                    {formatDate(hoveredData.date, timeframe)}
                  </span>
                )}
                <span className="split-chart-price">
                  ${formatPrice(displayData.close)}
                </span>
                <span className={`split-chart-change ${displayIsUp ? 'up' : 'down'}`}>
                  {displayIsUp ? '+' : ''}{displayChange.toFixed(2)}%
                </span>
                {hoveredData && chartType === 'candle' && hoveredData.high != null && (
                  <span className="split-chart-ohlc">
                    O:{formatPrice(hoveredData.open)} H:{formatPrice(hoveredData.high)} L:{formatPrice(hoveredData.low)} C:{formatPrice(hoveredData.close)}
                  </span>
                )}
              </>
            );
          })()}
        </div>
        
        {/* Action Buttons - Only show on hover */}
        {showControls && hovered && (
          <div className="split-chart-header-actions">
            {onFullscreen && (
              <button onClick={onFullscreen} title="Fullscreen">
                <Maximize2 size={14} />
              </button>
            )}
            {onRemove && (
              <button onClick={onRemove} title="Remove" className="remove-btn">
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </div>
      
      <svg 
        width={dimensions.width} 
        height={dimensions.height}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          setMousePos({ x, y });
          // Find which candle we're over
          for (let i = 0; i < data.length; i++) {
            const candleX = getX(i);
            if (x >= candleX && x <= candleX + candleWidth + candleGap) {
              setLocalHoveredIndex(i);
              if (onHover && data[i]?.date) {
                onHover(data[i].date);
              }
              break;
            }
          }
        }}
        onMouseLeave={() => {
          setLocalHoveredIndex(null);
          setMousePos(null);
          if (onHover) onHover(null);
        }}
      >
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
          const y = padding.top + chartHeight * pct;
          const price = paddedMax - paddedRange * pct;
          return (
            <g key={i}>
              <line x1={padding.left} x2={dimensions.width - padding.right} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
              <text x={dimensions.width - padding.right + 5} y={y + 4} fill="rgba(255,255,255,0.4)" fontSize="9">
                {formatPrice(price)}
              </text>
            </g>
          );
        })}
        
        {/* Candlesticks or Line */}
        {chartType === 'candle' ? (
          data.map((d, i) => {
            // Skip invalid data points
            if (!d || d.high == null || d.low == null || d.open == null || d.close == null) return null;
            const up = d.close >= d.open;
            const color = up ? chartSettings.candleUpColor : chartSettings.candleDownColor;
            const x = getX(i);
            const bodyTop = getY(Math.max(d.open, d.close));
            const bodyBottom = getY(Math.min(d.open, d.close));
            // Highlight if this is the synced hovered date
            const isHoveredBySync = hoveredDate && d.date === hoveredDate;
            const isLocalHovered = localHoveredIndex === i;
            return (
              <g key={i}>
                <line x1={x + candleWidth/2} x2={x + candleWidth/2} y1={getY(d.high)} y2={getY(d.low)} stroke={color} strokeWidth={isHoveredBySync || isLocalHovered ? 2 : 1} />
                <rect x={x} y={bodyTop} width={candleWidth} height={Math.max(1, bodyBottom - bodyTop)} fill={color} opacity={isHoveredBySync || isLocalHovered ? 1 : 0.9} />
                {(isHoveredBySync || isLocalHovered) && (
                  <>
                    <line x1={x + candleWidth/2} x2={x + candleWidth/2} y1={padding.top} y2={padding.top + chartHeight} stroke="rgba(255,255,255,0.3)" strokeDasharray="2,2" />
                  </>
                )}
              </g>
            );
          })
        ) : (
          <>
            <defs>
              <linearGradient id={`splitGrad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isUp ? chartSettings.candleUpColor : chartSettings.candleDownColor} stopOpacity="0.3"/>
                <stop offset="100%" stopColor={isUp ? chartSettings.candleUpColor : chartSettings.candleDownColor} stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path 
              d={`${data.map((d, i) => `${i === 0 ? 'M' : 'L'}${getX(i) + candleWidth/2},${getY(d.close)}`).join(' ')} L${getX(data.length-1) + candleWidth/2},${padding.top + chartHeight} L${getX(0) + candleWidth/2},${padding.top + chartHeight} Z`}
              fill={`url(#splitGrad-${symbol})`}
            />
            <path 
              d={data.map((d, i) => `${i === 0 ? 'M' : 'L'}${getX(i) + candleWidth/2},${getY(d.close)}`).join(' ')}
              fill="none"
              stroke={isUp ? chartSettings.candleUpColor : chartSettings.candleDownColor}
              strokeWidth="1.5"
            />
            {/* Crosshair sync for area chart */}
            {data.map((d, i) => {
              if (!d || d.close == null) return null;
              const isHoveredBySync = hoveredDate && d.date === hoveredDate;
              const isLocalHovered = localHoveredIndex === i;
              if (isHoveredBySync || isLocalHovered) {
                return (
                  <g key={`hover-${i}`}>
                    <line x1={getX(i) + candleWidth/2} x2={getX(i) + candleWidth/2} y1={padding.top} y2={padding.top + chartHeight} stroke="rgba(255,255,255,0.3)" strokeDasharray="2,2" />
                    <circle cx={getX(i) + candleWidth/2} cy={getY(d.close)} r={4} fill={isUp ? chartSettings.candleUpColor : chartSettings.candleDownColor} stroke="#fff" strokeWidth={1} />
                  </g>
                );
              }
              return null;
            })}
          </>
        )}
        
        {/* Volume Bars */}
        <g className="volume-bars">
          {/* Volume area separator line */}
          <line 
            x1={padding.left} 
            x2={dimensions.width - padding.right} 
            y1={dimensions.height - padding.bottom} 
            y2={dimensions.height - padding.bottom} 
            stroke="rgba(255,255,255,0.1)" 
          />
          {data.map((d, i) => {
            if (!d || d.close == null || d.open == null) return null;
            const volHeight = ((d.volume || 0) / maxVolume) * (volumeHeight - 5);
            const volY = dimensions.height - padding.bottom + volumeHeight - volHeight;
            const volUp = d.close >= d.open;
            const isHovered = localHoveredIndex === i || (hoveredDate && d.date === hoveredDate);
            return (
              <rect
                key={`vol-${i}`}
                x={getX(i)}
                y={volY}
                width={candleWidth}
                height={Math.max(0, volHeight)}
                fill={volUp ? chartSettings.candleUpColor : chartSettings.candleDownColor}
                opacity={isHovered ? 0.6 : 0.35}
              />
            );
          })}
        </g>
        
        {/* Current price line */}
        <line 
          x1={padding.left} 
          x2={dimensions.width - padding.right} 
          y1={getY(current.close)} 
          y2={getY(current.close)} 
          stroke={isUp ? chartSettings.candleUpColor : chartSettings.candleDownColor} 
          strokeDasharray="3,3" 
          opacity={0.5}
        />
      </svg>
      
      {/* Cursor Tooltip - follows mouse (only for area charts) */}
      {chartType === 'area' && mousePos && localHoveredIndex !== null && data[localHoveredIndex] && (
        <div 
          className="split-chart-cursor-tooltip"
          style={{
            left: mousePos.x > dimensions.width / 2 ? mousePos.x - 130 : mousePos.x + 15,
            top: Math.min(mousePos.y + 10, dimensions.height - 80)
          }}
        >
          <div className="cursor-tooltip-date">
            {formatDate(data[localHoveredIndex].date, timeframe)}
          </div>
          <div className="cursor-tooltip-price">
            ${formatPrice(data[localHoveredIndex].close)}
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================
// TIMEFRAME SELECTOR - Advanced with Dropdown
// ============================================
const TimeframeSelector = ({ timeframe, onTimeframeChange, isPro = true }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Quick access timeframes (shown in bar)
  const quickTf = ['1D', '1W', '1M', '3M', '6M', '1Y', '5Y'];
  
  // All timeframes organized by category
  const timeframeGroups = {
    seconds: {
      label: 'Seconds',
      icon: <Zap size={14} />,
      items: [
        { value: '1S', label: '1s', pro: true },
        { value: '5S', label: '5s', pro: true },
        { value: '10S', label: '10s', pro: true },
        { value: '15S', label: '15s', pro: true },
        { value: '30S', label: '30s', pro: true },
      ]
    },
    minutes: {
      label: 'Minutes',
      icon: <Clock size={14} />,
      items: [
        { value: '1m', label: '1m', pro: false },
        { value: '3m', label: '3m', pro: true },
        { value: '5m', label: '5m', pro: false },
        { value: '15m', label: '15m', pro: false },
        { value: '30m', label: '30m', pro: false },
        { value: '45m', label: '45m', pro: true },
      ]
    },
    hours: {
      label: 'Hours',
      icon: <Clock size={14} />,
      items: [
        { value: '1H', label: '1h', pro: false },
        { value: '2H', label: '2h', pro: true },
        { value: '4H', label: '4h', pro: false },
      ]
    },
    days: {
      label: 'Days',
      icon: <Calendar size={14} />,
      items: [
        { value: '1D', label: '1D', pro: false },
        { value: '1W', label: '1W', pro: false },
        { value: '1M', label: '1M', pro: false },
        { value: '3M', label: '3M', pro: false },
        { value: '6M', label: '6M', pro: false },
        { value: '1Y', label: '1Y', pro: false },
        { value: '5Y', label: '5Y', pro: false },
      ]
    }
  };

  const handleSelect = (value, requiresPro) => {
    if (requiresPro && !isPro) return;
    onTimeframeChange(value);
    setShowDropdown(false);
  };

  // Check if current timeframe is in quick list
  const isQuickTf = quickTf.includes(timeframe);

  return (
    <div className="fs-timeframe-selector" ref={dropdownRef} onClick={e => e.stopPropagation()}>
      {/* Quick access buttons */}
      <div className="fs-tf-quick">
        {quickTf.map(tf => (
          <button 
            key={tf} 
            className={timeframe === tf ? 'active' : ''} 
            onClick={() => onTimeframeChange(tf)}
          >
            {tf}
          </button>
        ))}
      </div>

      {/* Dropdown trigger */}
      <div className="fs-tf-dropdown-wrapper">
        <button 
          className={`fs-tf-dropdown-btn ${showDropdown ? 'open' : ''} ${!isQuickTf ? 'has-custom' : ''}`}
          onClick={() => setShowDropdown(!showDropdown)}
        >
          {!isQuickTf && <span className="fs-tf-custom-value">{timeframe}</span>}
          <ChevronDown size={14} className={showDropdown ? 'flip' : ''} />
        </button>

        {/* Dropdown menu */}
        {showDropdown && (
          <div className="fs-tf-dropdown">
            <div className="fs-tf-dropdown-header">
              <Crown size={12} />
              <span>Advanced Timeframes</span>
            </div>

            {Object.entries(timeframeGroups).map(([key, group]) => (
              <div key={key} className="fs-tf-group">
                <div className="fs-tf-group-label">
                  <span>{group.icon}</span>
                  <span>{group.label}</span>
                </div>
                <div className="fs-tf-group-items">
                  {group.items.map(item => (
                    <button
                      key={item.value}
                      className={`fs-tf-item ${timeframe === item.value ? 'active' : ''} ${item.pro && !isPro ? 'locked' : ''}`}
                      onClick={() => handleSelect(item.value, item.pro)}
                    >
                      {item.label}
                      {item.pro && !isPro && <Lock size={10} />}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {!isPro && (
              <div className="fs-tf-upgrade">
                <Crown size={14} />
                <span>Upgrade to PRO for all timeframes</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// PATTERN LIBRARY MODAL
// ============================================
const PatternLibraryModal = ({ onClose, detectedPatterns }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [expandedPattern, setExpandedPattern] = useState(null);

  const categories = [
    { id: 'all', label: 'All Patterns', icon: Layers },
    { id: 'candlestick', label: 'Candlestick', icon: BarChart2 },
    { id: 'chart', label: 'Chart Patterns', icon: TrendingUp },
    { id: 'trend', label: 'Trend', icon: Activity },
    { id: 'volume', label: 'Volume', icon: BarChart },
    { id: 'levels', label: 'Support/Resistance', icon: Layers2 },
    { id: 'gap', label: 'Gap Patterns', icon: ArrowLeftRight },
  ];

  const types = [
    { id: 'all', label: 'All' },
    { id: 'bullish', label: 'Bullish' },
    { id: 'bearish', label: 'Bearish' },
    { id: 'neutral', label: 'Neutral' },
  ];

  const detectedIds = detectedPatterns.map(p => p.id);

  const filteredPatterns = useMemo(() => {
    return Object.entries(PATTERN_LIBRARY).filter(([id, pattern]) => {
      if (selectedCategory !== 'all' && pattern.category !== selectedCategory) return false;
      if (selectedType !== 'all' && pattern.type !== selectedType) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return pattern.name.toLowerCase().includes(q) || pattern.description.toLowerCase().includes(q);
      }
      return true;
    }).sort((a, b) => {
      // Sort detected patterns first
      const aDetected = detectedIds.includes(a[0]);
      const bDetected = detectedIds.includes(b[0]);
      if (aDetected && !bDetected) return -1;
      if (!aDetected && bDetected) return 1;
      return a[1].name.localeCompare(b[1].name);
    });
  }, [selectedCategory, selectedType, searchQuery, detectedIds]);

  const patternCount = Object.keys(PATTERN_LIBRARY).length;

  return (
    <div className="fs-pattern-lib-overlay" onClick={onClose}>
      <div className="fs-pattern-lib-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="fs-pattern-lib-header">
          <div className="fs-pattern-lib-title">
            <BookOpen size={22} />
            <div>
              <h2>Pattern Library</h2>
              <span>{patternCount} patterns • {detectedPatterns.length} detected</span>
            </div>
          </div>
          <button className="fs-pattern-lib-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="fs-pattern-lib-search">
          <Search size={16} />
          <input 
            type="text" 
            placeholder="Search patterns..." 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Body */}
        <div className="fs-pattern-lib-body">
          {/* Sidebar */}
          <div className="fs-pattern-lib-sidebar">
            <div className="fs-pattern-lib-section-title">Categories</div>
            {categories.map(cat => {
              const Icon = cat.icon;
              const count = cat.id === 'all' 
                ? Object.keys(PATTERN_LIBRARY).length 
                : Object.values(PATTERN_LIBRARY).filter(p => p.category === cat.id).length;
              return (
                <button 
                  key={cat.id}
                  className={`fs-pattern-lib-cat ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <Icon size={16} />
                  <span>{cat.label}</span>
                  <span className="count">{count}</span>
                </button>
              );
            })}

            <div className="fs-pattern-lib-section-title" style={{ marginTop: 16 }}>Type Filter</div>
            <div className="fs-pattern-lib-types">
              {types.map(type => (
                <button 
                  key={type.id}
                  className={`fs-pattern-lib-type ${selectedType === type.id ? 'active' : ''} ${type.id}`}
                  onClick={() => setSelectedType(type.id)}
                >
                  {type.label}
                </button>
              ))}
            </div>

            {detectedPatterns.length > 0 && (
              <>
                <div className="fs-pattern-lib-section-title" style={{ marginTop: 16 }}>
                  <Zap size={14} /> Detected Now
                </div>
                <div className="fs-pattern-lib-detected">
                  {detectedPatterns.map((p, idx) => (
                    <div 
                      key={idx} 
                      className={`fs-pattern-lib-detected-item ${p.type.includes('bullish') ? 'bullish' : p.type.includes('bearish') ? 'bearish' : 'neutral'}`}
                      onClick={() => setExpandedPattern(p.id)}
                    >
                      {p.type.includes('bullish') ? <TrendingUp size={12} /> : p.type.includes('bearish') ? <TrendingDown size={12} /> : <Minus size={12} />}
                      <span>{p.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Pattern Grid */}
          <div className="fs-pattern-lib-content">
            <div className="fs-pattern-lib-results">
              Showing {filteredPatterns.length} of {patternCount} patterns
            </div>
            <div className="fs-pattern-lib-grid">
              {filteredPatterns.map(([id, pattern]) => {
                const isDetected = detectedIds.includes(id);
                const isExpanded = expandedPattern === id;
                return (
                  <div 
                    key={id} 
                    className={`fs-pattern-lib-card ${pattern.type} ${isDetected ? 'detected' : ''} ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => setExpandedPattern(isExpanded ? null : id)}
                  >
                    <div className="fs-pattern-lib-card-header">
                      <div className="fs-pattern-lib-card-icon">
                        {pattern.type === 'bullish' ? <TrendingUp size={18} /> : pattern.type === 'bearish' ? <TrendingDown size={18} /> : <Minus size={18} />}
                      </div>
                      <div className="fs-pattern-lib-card-info">
                        <span className="name">{pattern.name}</span>
                        <span className="category">{pattern.category}</span>
                      </div>
                      {isDetected && (
                        <div className="fs-pattern-lib-detected-badge">
                          <Zap size={10} /> DETECTED
                        </div>
                      )}
                    </div>
                    <p className="fs-pattern-lib-card-desc">{pattern.description}</p>
                    {isExpanded && (
                      <div className="fs-pattern-lib-card-expanded">
                        <div className="fs-pattern-lib-card-sig">
                          <GraduationCap size={14} />
                          <div>
                            <span className="label">Trading Significance</span>
                            <span className="value">{pattern.significance}</span>
                          </div>
                        </div>
                        <div className={`fs-pattern-lib-card-type ${pattern.type}`}>
                          {pattern.type === 'bullish' ? 'Bullish Signal' : pattern.type === 'bearish' ? 'Bearish Signal' : 'Neutral Signal'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🐋 SMART MONEY FLOW MODAL
// ============================================
const SmartMoneyModal = ({ isOpen, onClose, data, symbol }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const smartMoneyData = useMemo(() => generateSmartMoneyData(data, symbol), [data, symbol]);
  
  if (!isOpen || !smartMoneyData) return null;

  const formatMoney = (val) => {
    if (val >= 1e6) return '$' + (val / 1e6).toFixed(1) + 'M';
    if (val >= 1e3) return '$' + (val / 1e3).toFixed(0) + 'K';
    return '$' + val.toFixed(0);
  };

  return (
    <div className="fs-smart-money-overlay" onClick={onClose}>
      <div className="fs-smart-money-modal" onClick={e => e.stopPropagation()}>
        <div className="fs-sm-header">
          <div className="fs-sm-title">
            <Fish size={22} />
            <div>
              <h2>Smart Money Flow</h2>
              <span>Institutional Activity for {symbol}</span>
            </div>
          </div>
          <button className="fs-sm-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Signal Banner */}
        <div className={`fs-sm-signal ${smartMoneyData.signal.toLowerCase()}`}>
          <div className="signal-indicator">
            {smartMoneyData.signal === 'ACCUMULATING' ? <TrendingUp size={20} /> : 
             smartMoneyData.signal === 'DISTRIBUTING' ? <TrendingDown size={20} /> : <Minus size={20} />}
            <span className="signal-text">{smartMoneyData.signal}</span>
          </div>
          <div className="signal-confidence">
            <span>{smartMoneyData.confidence}%</span>
            <span className="label">Confidence</span>
          </div>
          <div className="flow-breakdown">
            <div className="flow-bar">
              <div className="smart" style={{ width: `${smartMoneyData.flowBreakdown.smart}%` }}>
                <span>Smart {smartMoneyData.flowBreakdown.smart.toFixed(0)}%</span>
              </div>
              <div className="retail" style={{ width: `${smartMoneyData.flowBreakdown.retail}%` }}>
                <span>Retail {smartMoneyData.flowBreakdown.retail.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="fs-sm-tabs">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
            <Gauge size={14} /> Overview
          </button>
          <button className={activeTab === 'darkpool' ? 'active' : ''} onClick={() => setActiveTab('darkpool')}>
            <Database size={14} /> Dark Pool
          </button>
          <button className={activeTab === 'options' ? 'active' : ''} onClick={() => setActiveTab('options')}>
            <Activity size={14} /> Options Flow
          </button>
          <button className={activeTab === 'insider' ? 'active' : ''} onClick={() => setActiveTab('insider')}>
            <Users size={14} /> Insider
          </button>
        </div>

        {/* Content */}
        <div className="fs-sm-content">
          {activeTab === 'overview' && (
            <div className="fs-sm-overview">
              <div className="fs-sm-stats-grid">
                <div className="fs-sm-stat">
                  <Database size={18} />
                  <div className="stat-info">
                    <span className="value">{formatMoney(smartMoneyData.summary.darkPoolVolume)}</span>
                    <span className="label">Dark Pool Volume</span>
                  </div>
                </div>
                <div className="fs-sm-stat">
                  <Activity size={18} />
                  <div className="stat-info">
                    <span className="value">{formatMoney(smartMoneyData.summary.optionsPremium)}</span>
                    <span className="label">Options Premium</span>
                  </div>
                </div>
                <div className="fs-sm-stat">
                  <Users size={18} />
                  <div className="stat-info">
                    <span className={`value ${smartMoneyData.summary.insiderNetBuying > 0 ? 'bullish' : 'bearish'}`}>
                      {smartMoneyData.summary.insiderNetBuying > 0 ? '+' : ''}{formatMoney(smartMoneyData.summary.insiderNetBuying)}
                    </span>
                    <span className="label">Insider Net Activity</span>
                  </div>
                </div>
                <div className="fs-sm-stat">
                  <Zap size={18} />
                  <div className="stat-info">
                    <span className="value">{smartMoneyData.volumeSpikes.length}</span>
                    <span className="label">Volume Spikes</span>
                  </div>
                </div>
              </div>

              <div className="fs-sm-section">
                <h3>Key Insights</h3>
                <div className="fs-sm-insights">
                  <div className={`insight ${smartMoneyData.signal === 'ACCUMULATING' ? 'bullish' : smartMoneyData.signal === 'DISTRIBUTING' ? 'bearish' : 'neutral'}`}>
                    <CheckCircle size={14} />
                    <span>Institutions are {smartMoneyData.signal.toLowerCase()} {symbol}</span>
                  </div>
                  <div className={`insight ${smartMoneyData.optionsFlow.filter(o => o.sentiment === 'bullish').length > 2 ? 'bullish' : 'neutral'}`}>
                    <Activity size={14} />
                    <span>{smartMoneyData.optionsFlow.filter(o => o.unusual).length} unusual options activities detected</span>
                  </div>
                  <div className={`insight ${smartMoneyData.darkPoolPrints.filter(d => d.type === 'buy').length > 2 ? 'bullish' : 'neutral'}`}>
                    <Database size={14} />
                    <span>Dark pool prints showing {smartMoneyData.darkPoolPrints.filter(d => d.type === 'buy').length > smartMoneyData.darkPoolPrints.filter(d => d.type === 'sell').length ? 'buying' : 'selling'} pressure</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'darkpool' && (
            <div className="fs-sm-darkpool">
              <div className="fs-sm-section-title">Recent Dark Pool Prints</div>
              <div className="fs-sm-table">
                <div className="table-header">
                  <span>Time</span>
                  <span>Size</span>
                  <span>Price</span>
                  <span>Type</span>
                  <span>Exchange</span>
                </div>
                {smartMoneyData.darkPoolPrints.map((print, idx) => (
                  <div key={idx} className={`table-row ${print.type}`}>
                    <span>{print.time}</span>
                    <span>{print.size.toLocaleString()}</span>
                    <span>${print.price.toFixed(2)}</span>
                    <span><span className={`type-badge ${print.type}`}>{print.type.toUpperCase()}</span></span>
                    <span className="exchange">{print.exchange}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'options' && (
            <div className="fs-sm-options">
              <div className="fs-sm-section-title">Unusual Options Activity</div>
              <div className="fs-sm-table">
                <div className="table-header">
                  <span>Type</span>
                  <span>Strike</span>
                  <span>Expiry</span>
                  <span>Premium</span>
                  <span>Signal</span>
                </div>
                {smartMoneyData.optionsFlow.map((opt, idx) => (
                  <div key={idx} className={`table-row ${opt.sentiment}`}>
                    <span><span className={`type-badge ${opt.type.toLowerCase()}`}>{opt.type}</span></span>
                    <span>${opt.strike}</span>
                    <span>{opt.expiry}</span>
                    <span>{formatMoney(opt.premium)}</span>
                    <span className={`sentiment ${opt.sentiment}`}>
                      {opt.unusual && <Zap size={12} />}
                      {opt.sentiment.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'insider' && (
            <div className="fs-sm-insider">
              <div className="fs-sm-section-title">Recent Insider Transactions</div>
              <div className="fs-sm-table">
                <div className="table-header">
                  <span>Insider</span>
                  <span>Action</span>
                  <span>Shares</span>
                  <span>Price</span>
                  <span>Value</span>
                </div>
                {smartMoneyData.insiderTransactions.map((txn, idx) => (
                  <div key={idx} className={`table-row ${txn.action.toLowerCase()}`}>
                    <span className="insider-name">{txn.name}</span>
                    <span><span className={`type-badge ${txn.action.toLowerCase()}`}>{txn.action}</span></span>
                    <span>{txn.shares.toLocaleString()}</span>
                    <span>${txn.price.toFixed(2)}</span>
                    <span>{formatMoney(txn.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// ⏰ TIME MACHINE MODAL
// ============================================
const TimeMachineModal = ({ isOpen, onClose, data, patterns, symbol }) => {
  const [selectedMatch, setSelectedMatch] = useState(null);
  
  const timeMachineData = useMemo(() => generateTimeMachineData(data, patterns, symbol), [data, patterns, symbol]);
  
  if (!isOpen || !timeMachineData) return null;

  const current = data?.[data.length - 1];

  return (
    <div className="fs-time-machine-overlay" onClick={onClose}>
      <div className="fs-time-machine-modal" onClick={e => e.stopPropagation()}>
        <div className="fs-tm-header">
          <div className="fs-tm-title">
            <History size={22} />
            <div>
              <h2>Time Machine</h2>
              <span>Historical Pattern Matching for {symbol}</span>
            </div>
          </div>
          <button className="fs-tm-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Signal Strength */}
        <div className={`fs-tm-signal ${timeMachineData.signalStrength.toLowerCase()}`}>
          <div className="signal-badge">
            <Radar size={18} />
            <span>{timeMachineData.signalStrength} MATCH</span>
          </div>
          <div className="similarity-score">
            <span className="value">{timeMachineData.avgSimilarity.toFixed(0)}%</span>
            <span className="label">Avg Similarity</span>
          </div>
        </div>

        <div className="fs-tm-body">
          {/* Historical Matches */}
          <div className="fs-tm-matches">
            <div className="fs-tm-section-title">Historical Matches</div>
            {timeMachineData.historicalMatches.map((match, idx) => (
              <div 
                key={idx} 
                className={`fs-tm-match-card ${selectedMatch === idx ? 'selected' : ''} ${match.outcome.days90 > 0 ? 'bullish' : 'bearish'}`}
                onClick={() => setSelectedMatch(selectedMatch === idx ? null : idx)}
              >
                <div className="match-header">
                  <div className="match-info">
                    <span className="match-symbol">{match.symbol}</span>
                    <span className="match-date">{match.matchDate}</span>
                  </div>
                  <div className="match-similarity">
                    <div className="similarity-bar" style={{ width: `${match.similarity}%` }} />
                    <span>{match.similarity}%</span>
                  </div>
                </div>
                <div className="match-setup">{match.setup}</div>
                <div className="match-outcomes">
                  <div className={`outcome ${match.outcome.days30 > 0 ? 'up' : 'down'}`}>
                    <span className="period">30D</span>
                    <span className="value">{match.outcome.days30 > 0 ? '+' : ''}{match.outcome.days30.toFixed(1)}%</span>
                  </div>
                  <div className={`outcome ${match.outcome.days60 > 0 ? 'up' : 'down'}`}>
                    <span className="period">60D</span>
                    <span className="value">{match.outcome.days60 > 0 ? '+' : ''}{match.outcome.days60.toFixed(1)}%</span>
                  </div>
                  <div className={`outcome ${match.outcome.days90 > 0 ? 'up' : 'down'}`}>
                    <span className="period">90D</span>
                    <span className="value">{match.outcome.days90 > 0 ? '+' : ''}{match.outcome.days90.toFixed(1)}%</span>
                  </div>
                </div>
                {selectedMatch === idx && (
                  <div className="match-description">
                    <p>{match.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Probability & Targets */}
          <div className="fs-tm-analysis">
            <div className="fs-tm-section-title">Probability Distribution</div>
            <div className="fs-tm-prob-grid">
              {['days30', 'days60', 'days90'].map(period => {
                const prob = timeMachineData.probabilityDistribution[period];
                return (
                  <div key={period} className="fs-tm-prob-card">
                    <div className="prob-header">{period.replace('days', '')} Days</div>
                    <div className="prob-expected">
                      <span className={prob.expected > 0 ? 'bullish' : 'bearish'}>
                        {prob.expected > 0 ? '+' : ''}{prob.expected.toFixed(1)}%
                      </span>
                      <span className="label">Expected</span>
                    </div>
                    <div className="prob-bars">
                      <div className="prob-bar bullish" style={{ width: `${prob.bullish}%` }}>
                        <span>{prob.bullish.toFixed(0)}%</span>
                      </div>
                      <div className="prob-bar bearish" style={{ width: `${prob.bearish}%` }}>
                        <span>{prob.bearish.toFixed(0)}%</span>
                      </div>
                    </div>
                    <div className="prob-range">
                      Range: {prob.range.min.toFixed(1)}% to +{prob.range.max.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="fs-tm-section-title" style={{ marginTop: 20 }}>Price Targets</div>
            <div className="fs-tm-targets">
              <div className="target-card bull">
                <TrendingUp size={16} />
                <div className="target-info">
                  <span className="label">Bull Case (90D)</span>
                  <span className="price">${timeMachineData.priceTargets.bullCase.toFixed(2)}</span>
                  <span className="change">+{((timeMachineData.priceTargets.bullCase / current.close - 1) * 100).toFixed(1)}%</span>
                </div>
              </div>
              <div className="target-card base">
                <Target size={16} />
                <div className="target-info">
                  <span className="label">Base Case (90D)</span>
                  <span className="price">${timeMachineData.priceTargets.baseCase.toFixed(2)}</span>
                  <span className="change">{((timeMachineData.priceTargets.baseCase / current.close - 1) * 100) > 0 ? '+' : ''}{((timeMachineData.priceTargets.baseCase / current.close - 1) * 100).toFixed(1)}%</span>
                </div>
              </div>
              <div className="target-card bear">
                <TrendingDown size={16} />
                <div className="target-info">
                  <span className="label">Bear Case (90D)</span>
                  <span className="price">${timeMachineData.priceTargets.bearCase.toFixed(2)}</span>
                  <span className="change">{((timeMachineData.priceTargets.bearCase / current.close - 1) * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 🎯 SCENARIO SIMULATOR MODAL
// ============================================
const ScenarioModal = ({ isOpen, onClose, data, symbol, companyName }) => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showMonteCarlo, setShowMonteCarlo] = useState(false);
  
  const scenarioData = useMemo(() => generateScenarioData(data, symbol, companyName), [data, symbol, companyName]);
  
  if (!isOpen || !scenarioData) return null;

  const current = data?.[data.length - 1];
  const mc = scenarioData.monteCarloResults;

  const getScenarioIcon = (iconName) => {
    switch(iconName) {
      case 'DollarSign': return <DollarSign size={18} />;
      case 'TrendingUp': return <TrendingUp size={18} />;
      case 'TrendingDown': return <TrendingDown size={18} />;
      case 'AlertTriangle': return <AlertTriangle size={18} />;
      case 'Shuffle': return <Shuffle size={18} />;
      case 'Globe': return <Globe size={18} />;
      case 'Brain': return <Brain size={18} />;
      default: return <Zap size={18} />;
    }
  };

  return (
    <div className="fs-scenario-overlay" onClick={onClose}>
      <div className="fs-scenario-modal" onClick={e => e.stopPropagation()}>
        <div className="fs-sc-header">
          <div className="fs-sc-title">
            <Compass size={22} />
            <div>
              <h2>Scenario Simulator</h2>
              <span>What-If Analysis for {symbol}</span>
            </div>
          </div>
          <button className="fs-sc-close" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Risk Level Banner */}
        <div className={`fs-sc-risk-banner ${scenarioData.riskLevel.toLowerCase()}`}>
          <div className="risk-info">
            <Gauge size={18} />
            <span>Current Volatility: {scenarioData.currentVolatility.toFixed(2)}%</span>
            <span className="risk-badge">{scenarioData.riskLevel} RISK</span>
          </div>
          <button className="monte-carlo-btn" onClick={() => setShowMonteCarlo(!showMonteCarlo)}>
            <Radar size={14} /> Monte Carlo
          </button>
        </div>

        {/* Monte Carlo Panel */}
        {showMonteCarlo && (
          <div className="fs-sc-monte-carlo">
            <div className="mc-header">
              <h3>Monte Carlo Simulation</h3>
              <span>{mc.simulations.toLocaleString()} simulations • {mc.timeHorizon}</span>
            </div>
            <div className="mc-distribution">
              <div className="mc-percentiles">
                <div className="mc-pct" style={{ left: '5%' }}>
                  <span className="price">${mc.percentiles.p5.toFixed(0)}</span>
                  <span className="label">5th %ile</span>
                </div>
                <div className="mc-pct" style={{ left: '25%' }}>
                  <span className="price">${mc.percentiles.p25.toFixed(0)}</span>
                  <span className="label">25th</span>
                </div>
                <div className="mc-pct expected" style={{ left: '50%' }}>
                  <span className="price">${mc.percentiles.p50.toFixed(0)}</span>
                  <span className="label">Median</span>
                </div>
                <div className="mc-pct" style={{ left: '75%' }}>
                  <span className="price">${mc.percentiles.p75.toFixed(0)}</span>
                  <span className="label">75th</span>
                </div>
                <div className="mc-pct" style={{ left: '95%' }}>
                  <span className="price">${mc.percentiles.p95.toFixed(0)}</span>
                  <span className="label">95th %ile</span>
                </div>
              </div>
              <div className="mc-bar">
                <div className="mc-range" style={{ left: '5%', width: '90%' }} />
                <div className="mc-iqr" style={{ left: '25%', width: '50%' }} />
                <div className="mc-median" style={{ left: '50%' }} />
              </div>
            </div>
            <div className="mc-stats">
              <div className="mc-stat">
                <span className="value bullish">{mc.probabilityOfProfit}%</span>
                <span className="label">Prob. of Profit</span>
              </div>
              <div className="mc-stat">
                <span className="value">{mc.sharpeRatio.toFixed(2)}</span>
                <span className="label">Sharpe Ratio</span>
              </div>
              <div className="mc-stat">
                <span className="value bearish">{mc.maxDrawdown}%</span>
                <span className="label">Max Drawdown</span>
              </div>
            </div>
          </div>
        )}

        <div className="fs-sc-body">
          {/* Scenarios List */}
          <div className="fs-sc-scenarios">
            <div className="fs-sc-section-title">Event Scenarios</div>
            {scenarioData.scenarios.map((scenario, idx) => (
              <div 
                key={idx}
                className={`fs-sc-card ${selectedScenario === idx ? 'selected' : ''} ${scenario.impact.month1 > 0 ? 'bullish' : 'bearish'}`}
                onClick={() => setSelectedScenario(selectedScenario === idx ? null : idx)}
              >
                <div className="scenario-header">
                  <div className="scenario-icon">
                    {getScenarioIcon(scenario.icon)}
                  </div>
                  <div className="scenario-info">
                    <span className="scenario-name">{scenario.name}</span>
                    <span className="scenario-category">{scenario.category}</span>
                  </div>
                  <div className="scenario-probability">
                    <span className="prob-value">{scenario.probability}%</span>
                    <span className="prob-label">Probability</span>
                  </div>
                </div>
                
                <div className="scenario-impacts">
                  <div className={`impact ${scenario.impact.immediate > 0 ? 'up' : 'down'}`}>
                    <span className="label">Immediate</span>
                    <span className="value">{scenario.impact.immediate > 0 ? '+' : ''}{scenario.impact.immediate}%</span>
                  </div>
                  <div className={`impact ${scenario.impact.week1 > 0 ? 'up' : 'down'}`}>
                    <span className="label">1 Week</span>
                    <span className="value">{scenario.impact.week1 > 0 ? '+' : ''}{scenario.impact.week1}%</span>
                  </div>
                  <div className={`impact ${scenario.impact.month1 > 0 ? 'up' : 'down'}`}>
                    <span className="label">1 Month</span>
                    <span className="value">{scenario.impact.month1 > 0 ? '+' : ''}{scenario.impact.month1}%</span>
                  </div>
                </div>

                {selectedScenario === idx && (
                  <div className="scenario-details">
                    <p className="reasoning">{scenario.impact.reasoning}</p>
                    <div className="historical-refs">
                      <span className="refs-label">Historical:</span>
                      {scenario.historicalEvents.map((evt, i) => (
                        <span key={i} className="ref-tag">{evt}</span>
                      ))}
                    </div>
                    <div className="confidence-bar">
                      <span>Confidence: {scenario.confidence}%</span>
                      <div className="bar">
                        <div className="fill" style={{ width: `${scenario.confidence}%` }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Position Sizing */}
          <div className="fs-sc-position">
            <div className="fs-sc-section-title">Position Sizing Recommendation</div>
            <div className="position-grid">
              <div className="position-card aggressive">
                <span className="label">Aggressive</span>
                <span className="value">{scenarioData.recommendedPosition.aggressive}%</span>
                <span className="desc">of portfolio</span>
              </div>
              <div className="position-card moderate">
                <span className="label">Moderate</span>
                <span className="value">{scenarioData.recommendedPosition.moderate}%</span>
                <span className="desc">of portfolio</span>
              </div>
              <div className="position-card conservative">
                <span className="label">Conservative</span>
                <span className="value">{scenarioData.recommendedPosition.conservative}%</span>
                <span className="desc">of portfolio</span>
              </div>
            </div>

            <div className="position-levels">
              <div className="level stop-loss">
                <AlertTriangle size={14} />
                <span className="label">Stop Loss</span>
                <span className="price">${scenarioData.recommendedPosition.stopLoss.toFixed(2)}</span>
                <span className="pct">-8%</span>
              </div>
              <div className="level take-profit">
                <Target size={14} />
                <span className="label">Take Profit 1</span>
                <span className="price">${scenarioData.recommendedPosition.takeProfit1.toFixed(2)}</span>
                <span className="pct">+8%</span>
              </div>
              <div className="level take-profit">
                <Target size={14} />
                <span className="label">Take Profit 2</span>
                <span className="price">${scenarioData.recommendedPosition.takeProfit2.toFixed(2)}</span>
                <span className="pct">+15%</span>
              </div>
            </div>

            <div className="risk-reward">
              <span>Risk/Reward Ratio</span>
              <span className="ratio">{scenarioData.recommendedPosition.riskRewardRatio}:1</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ALERTS DROPDOWN COMPONENT
// ============================================
const AlertsDropdown = ({ symbol, currentPrice, priceAlerts, setPriceAlerts, onClose, formState, setFormState, onCreateAlert, onDeleteAlert, onToggleAlert }) => {
  const dropdownRef = useRef(null);
  
  // Use form state from parent (persists when dropdown closes)
  const { type: alertType, condition: alertCondition, notifyMethod, price: newAlertPrice, name: alertName } = formState;
  
  const updateFormState = (updates) => {
    setFormState(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleCreateAlert = async () => {
    if (!newAlertPrice || isNaN(parseFloat(newAlertPrice))) return;
    
    const newAlert = {
      symbol,
      type: alertType || 'price',
      condition: alertCondition || 'above',
      price: parseFloat(newAlertPrice),
      name: alertName || `${symbol} ${alertCondition || 'crosses'} $${newAlertPrice}`,
      notify: notifyMethod
    };
    
    // Try API first
    if (onCreateAlert) {
      const apiAlert = await onCreateAlert(newAlert);
      if (apiAlert) {
        setPriceAlerts(prev => [...prev, apiAlert]);
        updateFormState({ price: '', name: '' });
        return;
      }
    }
    
    // Fallback to local-only
    setPriceAlerts(prev => [...prev, {
      ...newAlert,
      id: Date.now(),
      active: true,
      triggered: false,
      createdAt: new Date().toISOString()
    }]);
    updateFormState({ price: '', name: '' });
  };

  const handleDeleteAlert = async (id) => {
    setPriceAlerts(prev => prev.filter(a => a.id !== id));
    if (onDeleteAlert) {
      await onDeleteAlert(id);
    }
  };

  const handleToggleAlert = async (id) => {
    const alert = priceAlerts.find(a => a.id === id);
    const newActive = !alert?.active;
    setPriceAlerts(prev => prev.map(a => a.id === id ? { ...a, active: newActive } : a));
    if (onToggleAlert) {
      await onToggleAlert(id, newActive);
    }
  };

  const toggleNotifyMethod = (method) => {
    const newMethods = notifyMethod.includes(method) 
      ? notifyMethod.filter(m => m !== method)
      : [...notifyMethod, method];
    updateFormState({ notifyMethod: newMethods });
  };

  return (
    <div className="fs-alerts-dropdown" ref={dropdownRef} onClick={e => e.stopPropagation()}>
      <div className="fs-alerts-header">
        <Bell size={16} />
        <span>Price Alerts</span>
        <span className="fs-alerts-symbol">{symbol}</span>
      </div>

      {/* Quick Price Buttons */}
      <div className="fs-alerts-quick-prices">
        <button onClick={() => updateFormState({ price: (currentPrice * 1.05).toFixed(2) })}>+5%</button>
        <button onClick={() => updateFormState({ price: (currentPrice * 1.02).toFixed(2) })}>+2%</button>
        <button onClick={() => updateFormState({ price: (currentPrice * 0.98).toFixed(2) })}>-2%</button>
        <button onClick={() => updateFormState({ price: (currentPrice * 0.95).toFixed(2) })}>-5%</button>
      </div>

      {/* Create Alert Form */}
      <div className="fs-alerts-form">
        {/* Alert Type */}
        <div className="fs-alerts-row">
          <label>Type</label>
          <div className="fs-alerts-type-btns">
            <button className={alertType === 'price' ? 'active' : ''} onClick={() => updateFormState({ type: alertType === 'price' ? '' : 'price' })}>
              <DollarSign size={12} /> Price
            </button>
            <button className={alertType === 'percent' ? 'active' : ''} onClick={() => updateFormState({ type: alertType === 'percent' ? '' : 'percent' })}>
              <Percent size={12} /> Percent
            </button>
            <button className={alertType === 'rsi' ? 'active' : ''} onClick={() => updateFormState({ type: alertType === 'rsi' ? '' : 'rsi' })}>
              <Activity size={12} /> RSI
            </button>
          </div>
        </div>

        {/* Condition */}
        <div className="fs-alerts-row">
          <label>Condition</label>
          <div className="fs-alerts-condition-btns">
            <button className={alertCondition === 'above' ? 'active' : ''} onClick={() => updateFormState({ condition: alertCondition === 'above' ? '' : 'above' })}>
              <TrendingUp size={12} /> Above
            </button>
            <button className={alertCondition === 'below' ? 'active' : ''} onClick={() => updateFormState({ condition: alertCondition === 'below' ? '' : 'below' })}>
              <TrendingDown size={12} /> Below
            </button>
            <button className={alertCondition === 'cross' ? 'active' : ''} onClick={() => updateFormState({ condition: alertCondition === 'cross' ? '' : 'cross' })}>
              <ArrowLeftRight size={12} /> Cross
            </button>
          </div>
        </div>

        {/* Price Input */}
        <div className="fs-alerts-row">
          <label>{alertType === 'rsi' ? 'RSI Value' : alertType === 'percent' ? 'Percent Change' : 'Price'}</label>
          <div className="fs-alerts-price-input">
            {alertType === 'price' && <span className="prefix">$</span>}
            <input 
              type="number" 
              value={newAlertPrice}
              onChange={(e) => updateFormState({ price: e.target.value })}
              placeholder={alertType === 'rsi' ? '70' : alertType === 'percent' ? '5' : currentPrice?.toFixed(2)}
            />
            {alertType === 'percent' && <span className="suffix">%</span>}
          </div>
        </div>

        {/* Alert Name */}
        <div className="fs-alerts-row">
          <label>Name (optional)</label>
          <input 
            type="text" 
            className="fs-alerts-name-input"
            value={alertName}
            onChange={(e) => updateFormState({ name: e.target.value })}
            placeholder="My price alert"
          />
        </div>

        {/* Notification Method */}
        <div className="fs-alerts-row">
          <label>Notify via</label>
          <div className="fs-alerts-notify-btns">
            <button 
              className={notifyMethod.includes('app') ? 'active' : ''} 
              onClick={() => toggleNotifyMethod('app')}
            >
              <Bell size={12} /> App
            </button>
            <button 
              className={notifyMethod.includes('email') ? 'active' : ''} 
              onClick={() => toggleNotifyMethod('email')}
            >
              <AlertCircle size={12} /> Email
            </button>
            <button 
              className={notifyMethod.includes('sms') ? 'active' : ''} 
              onClick={() => toggleNotifyMethod('sms')}
            >
              <Zap size={12} /> SMS
            </button>
          </div>
        </div>

        {/* Create Button */}
        <button className="fs-alerts-create-btn" onClick={handleCreateAlert}>
          <Plus size={14} />
          <span>Create Alert</span>
        </button>
      </div>

      {/* Active Alerts List */}
      {priceAlerts.length > 0 && (
        <div className="fs-alerts-list">
          <div className="fs-alerts-list-header">
            <span>Active Alerts ({priceAlerts.length})</span>
          </div>
          {priceAlerts.map(alert => (
            <div key={alert.id} className={`fs-alert-item ${!alert.active ? 'disabled' : ''}`}>
              <div className="fs-alert-info">
                <div className="fs-alert-icon">
                  {alert.condition === 'above' ? <TrendingUp size={14} /> : alert.condition === 'below' ? <TrendingDown size={14} /> : <ArrowLeftRight size={14} />}
                </div>
                <div className="fs-alert-details">
                  <span className="fs-alert-name">{alert.name}</span>
                  <span className="fs-alert-price">
                    {alert.condition} ${alert.price.toFixed(2)}
                    {currentPrice && (
                      <span className={alert.condition === 'above' ? (currentPrice >= alert.price ? 'triggered' : '') : (currentPrice <= alert.price ? 'triggered' : '')}>
                        {' '}({((alert.price - currentPrice) / currentPrice * 100).toFixed(1)}%)
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="fs-alert-actions">
                <button 
                  className={`fs-alert-toggle ${alert.active ? 'active' : ''}`}
                  onClick={() => handleToggleAlert(alert.id)}
                  title={alert.active ? 'Disable' : 'Enable'}
                >
                  {alert.active ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button 
                  className="fs-alert-delete"
                  onClick={() => handleDeleteAlert(alert.id)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {priceAlerts.length === 0 && (
        <div className="fs-alerts-empty">
          <Bell size={24} />
          <span>No alerts set for {symbol}</span>
          <span className="sub">Create an alert to get notified when price conditions are met</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// FULLSCREEN CHART COMPONENT
// ============================================
const FullscreenChart = memo(({ 
  // Props are now optional - can get from URL
  data: propData, 
  symbol: propSymbol, 
  companyName: propCompanyName, 
  timeframe: propTimeframe, 
  onClose, 
  onTimeframeChange, 
  onSymbolChange, 
  chartType: initialChartType, 
  isPro = true 
}) => {
  // React Router hooks for page-based routing
  const { symbol: urlSymbol } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use URL params or props (backwards compatible)
  const symbol = urlSymbol || propSymbol || 'AAPL';
  const urlTimeframe = searchParams.get('tf');
  
  // State for fetched data when used as a route
  const [fetchedData, setFetchedData] = useState(null);
  const [fetchedCompanyName, setFetchedCompanyName] = useState(null);
  const [isLoading, setIsLoading] = useState(!propData);
  const [error, setError] = useState(null);
  
  // Determine data source
  const data = propData || fetchedData;
  const companyName = propCompanyName || fetchedCompanyName || symbol;
  
  // Timeframe - use URL param, prop, or default
  const [localTimeframe, setLocalTimeframe] = useState(urlTimeframe || propTimeframe || '3M');
  const timeframe = urlTimeframe || propTimeframe || localTimeframe;
  
  // Handle timeframe change - update URL if in route mode
  const handleTimeframeChange = useCallback((newTf) => {
    if (onTimeframeChange) {
      onTimeframeChange(newTf);
    } else {
      setLocalTimeframe(newTf);
      setSearchParams(prev => {
        prev.set('tf', newTf);
        return prev;
      });
    }
  }, [onTimeframeChange, setSearchParams]);
  
  // Handle symbol change - navigate to new URL if in route mode
  const handleSymbolChange = useCallback((newSymbol) => {
    if (onSymbolChange) {
      onSymbolChange(newSymbol);
    } else {
      navigate(`/chart/${newSymbol}?tf=${timeframe}`);
    }
  }, [onSymbolChange, navigate, timeframe]);
  
  // Handle back/close - navigate back if in route mode
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1); // Go back in history
    }
  }, [onClose, navigate]);
  
  // Lock body scroll when chart is mounted
  useEffect(() => {
    document.body.classList.add('chart-open');
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.classList.remove('chart-open');
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch data when used as a route (no propData provided)
  useEffect(() => {
    if (propData) {
      setIsLoading(false);
      return;
    }
    
    const fetchChartData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // First get company info
        const infoRes = await fetch(`${API_BASE_URL}/api/stock/company?symbol=${symbol}`);
        if (infoRes.ok) {
          const info = await infoRes.json();
          setFetchedCompanyName(info.name || info.companyName || symbol);
        }
        
        // Then get historical data
        const histRes = await fetch(`${API_BASE_URL}/api/stock/historical?symbol=${symbol}&timeframe=${timeframe}`);
        if (!histRes.ok) throw new Error('Failed to fetch chart data');
        
        const histData = await histRes.json();
        
        // Transform data if needed
        const chartData = histData.data || histData;
        if (Array.isArray(chartData) && chartData.length > 0) {
          setFetchedData(chartData);
        } else {
          throw new Error('No data available');
        }
      } catch (err) {
        console.error('Chart data fetch error:', err);
        setError(err.message);
        
        // Generate mock data as fallback for demo
        const mockData = generateMockData(symbol, timeframe);
        setFetchedData(mockData);
        setFetchedCompanyName(symbol + ' Corporation');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchChartData();
  }, [symbol, timeframe, propData]);
  
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
  const [mousePos, setMousePos] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [chartType, setChartType] = useState(initialChartType || 'candle');
  const [indicators, setIndicators] = useState({ volume: false }); // All off by default
  const [selectedTool, setSelectedTool] = useState('crosshair');
  const [assetType, setAssetType] = useState('stocks');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [showIndicatorsModal, setShowIndicatorsModal] = useState(false);
  
  // Multi-chart layout state
  const [chartLayout, setChartLayout] = useState('single'); // 'single', '2h', '3', '4grid'
  const [multiChartSymbols, setMultiChartSymbols] = useState(['SPY', 'QQQ', 'AAPL']); // Additional chart symbols
  const [multiChartTimeframes, setMultiChartTimeframes] = useState(['3M', '3M', '3M']); // Individual timeframes
  const [multiChartTypes, setMultiChartTypes] = useState(['candle', 'candle', 'candle']); // Individual chart types
  const [gridHoveredDate, setGridHoveredDate] = useState(null); // For crosshair sync across charts
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  
  // Right sidebar state
  const [showRightPanel, setShowRightPanel] = useState(true); // Always true - panel cannot be closed
  const [rightPanelTab, setRightPanelTab] = useState('watchlist');
  
  // Fundamentals data - memoized per symbol, will be replaced by API data
  const [fundamentals, setFundamentals] = useState(null);
  
  // Generate stable mock fundamentals when symbol changes (will be replaced by API)
  useEffect(() => {
    // Seed random based on symbol for consistent values
    const seed = symbol.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const seededRandom = (min, max) => {
      const x = Math.sin(seed * 9999) * 10000;
      return min + (x - Math.floor(x)) * (max - min);
    };
    
    const currentPrice = data?.[data.length - 1]?.close || 100;
    
    setFundamentals({
      // Valuation
      peRatio: (18 + seededRandom(0, 15)).toFixed(2),
      fwdPE: (15 + seededRandom(0, 12)).toFixed(2),
      peg: (1 + seededRandom(0, 2)).toFixed(2),
      ps: (3 + seededRandom(0, 8)).toFixed(2),
      pb: (2 + seededRandom(0, 10)).toFixed(2),
      evEbitda: (10 + seededRandom(0, 15)).toFixed(1),
      // Earnings
      eps: (3 + seededRandom(0, 8)).toFixed(2),
      epsGrowth: (5 + seededRandom(0, 25)).toFixed(1),
      revenue: (50 + seededRandom(0, 350)).toFixed(0),
      revGrowth: (2 + seededRandom(0, 20)).toFixed(1),
      netIncome: (5 + seededRandom(0, 80)).toFixed(1),
      profitMargin: (8 + seededRandom(0, 25)).toFixed(1),
      // Risk
      beta: (0.8 + seededRandom(0, 0.8)).toFixed(2),
      high52w: (currentPrice * (1.1 + seededRandom(0, 0.3))).toFixed(2),
      low52w: (currentPrice * (0.5 + seededRandom(0, 0.3))).toFixed(2),
      volatility: (15 + seededRandom(0, 30)).toFixed(1),
      divYield: (seededRandom(0, 3)).toFixed(2),
      payoutRatio: (15 + seededRandom(0, 40)).toFixed(0),
      // Balance Sheet
      mktCap: (100 + seededRandom(0, 2900)).toFixed(0),
      enterpriseVal: (120 + seededRandom(0, 3000)).toFixed(0),
      cash: (10 + seededRandom(0, 150)).toFixed(0),
      debt: (5 + seededRandom(0, 100)).toFixed(0),
      deRatio: (0.2 + seededRandom(0, 1.5)).toFixed(2),
      currentRatio: (0.8 + seededRandom(0, 2)).toFixed(2),
      // Efficiency
      roe: (10 + seededRandom(0, 40)).toFixed(1),
      roa: (5 + seededRandom(0, 20)).toFixed(1),
      roic: (8 + seededRandom(0, 25)).toFixed(1),
      assetTurn: (0.3 + seededRandom(0, 1.2)).toFixed(2),
      grossMargin: (25 + seededRandom(0, 50)).toFixed(1),
      opMargin: (10 + seededRandom(0, 30)).toFixed(1),
      // Ownership
      insiderOwn: (0.1 + seededRandom(0, 15)).toFixed(2),
      instOwn: (50 + seededRandom(0, 40)).toFixed(1),
      shortFloat: (1 + seededRandom(0, 8)).toFixed(2),
      shortRatio: (1 + seededRandom(0, 5)).toFixed(1),
      sharesOut: (1 + seededRandom(0, 15)).toFixed(1),
      float: (1 + seededRandom(0, 14)).toFixed(1),
    });
  }, [symbol, data]);
  
  // Sidebar collapse states
  const [collapsedSections, setCollapsedSections] = useState({
    fundamentals: false,
    watchlist: false,
  });
  
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Watchlist state - persist to localStorage
  const defaultWatchlist = [
    { symbol: 'SPX', name: 'S&P 500', price: 6794.74, change: -21.78, changePct: -0.32, type: 'index', logo: '000', color: '#ef5350' },
    { symbol: 'NDQ', name: 'NASDAQ', price: 25093.84, change: 26.58, changePct: 0.11, type: 'index', logo: 'N', color: '#26a69a' },
    { symbol: 'DJI', name: 'Dow Jones', price: 48107.68, change: -308.88, changePct: -0.64, type: 'index', logo: 'DJ', color: '#2962ff' },
    { symbol: 'VIX', name: 'Volatility', price: 16.65, change: 0.15, changePct: 0.91, type: 'index', logo: 'V', color: '#26a69a', superscript: 'D' },
    { symbol: 'DXY', name: 'Dollar Index', price: 98.12, change: -0.13, changePct: -0.13, type: 'index', logo: '$', color: '#26a69a' },
    { symbol: 'AAPL', name: 'Apple', price: 274.44, change: 0.32, changePct: 0.12, type: 'stock', logo: '', color: '#ef5350' },
    { symbol: 'TSLA', name: 'Tesla', price: 486.27, change: 10.96, changePct: 2.31, type: 'stock', logo: 'T', color: '#ef5350' },
    { symbol: 'NFLX', name: 'Netflix', price: 94.30, change: 0.53, changePct: 0.56, type: 'stock', logo: 'N', color: '#ef5350' },
    { symbol: 'USOIL', name: 'Crude Oil', price: 55.25, change: -1.42, changePct: -2.51, type: 'futures', logo: 'O', color: '#787b86' },
  ];
  const [watchlist, setWatchlist] = useState(defaultWatchlist);
  const [showSymbolSearch, setShowSymbolSearch] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [activeWatchlistId, setActiveWatchlistId] = useState(null);
  
  // Load watchlist from API on mount
  useEffect(() => {
    const loadWatchlist = async () => {
      const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
      if (!token) {
        // Not logged in - use localStorage fallback
        try {
          const saved = localStorage.getItem('elemental-watchlist');
          if (saved) setWatchlist(JSON.parse(saved));
        } catch { /* use default */ }
        return;
      }
      
      try {
        const res = await fetch(`${API_BASE_URL}/api/watchlists`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.watchlists?.length > 0) {
            const firstList = data.watchlists[0];
            setActiveWatchlistId(firstList.id);
            // Convert stored symbols to full watchlist format
            if (firstList.symbols?.length > 0) {
              const watchlistItems = firstList.symbols.map(sym => 
                typeof sym === 'string' 
                  ? { symbol: sym, name: sym, price: 0, change: 0, changePct: 0, type: 'stock' }
                  : sym
              );
              setWatchlist(watchlistItems);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to load watchlist from API:', err);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('elemental-watchlist');
          if (saved) setWatchlist(JSON.parse(saved));
        } catch { /* use default */ }
      }
    };
    loadWatchlist();
  }, []);
  
  // Save watchlist to API when it changes (debounced)
  const saveWatchlistRef = useRef(null);
  useEffect(() => {
    // Always save to localStorage as backup
    try {
      localStorage.setItem('elemental-watchlist', JSON.stringify(watchlist));
    } catch { /* ignore */ }
    
    // Debounce API saves
    if (saveWatchlistRef.current) clearTimeout(saveWatchlistRef.current);
    saveWatchlistRef.current = setTimeout(async () => {
      const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
      if (!token || !activeWatchlistId) return;
      
      try {
        await fetch(`${API_BASE_URL}/api/watchlists/${activeWatchlistId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ symbols: watchlist })
        });
      } catch (err) {
        console.warn('Failed to save watchlist to API:', err);
      }
    }, 1000); // 1 second debounce
    
    return () => {
      if (saveWatchlistRef.current) clearTimeout(saveWatchlistRef.current);
    };
  }, [watchlist, activeWatchlistId]);
  
  // Drawing tools state - persist per symbol
  const [drawings, setDrawings] = useState(() => {
    try {
      const saved = localStorage.getItem(`elemental-drawings-${symbol}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [showDrawingMenu, setShowDrawingMenu] = useState(null);
  const [currentPencilPath, setCurrentPencilPath] = useState([]); // For freehand drawing
  const pencilPathRef = useRef([]); // Ref for instant updates without re-render delay
  
  // Undo/Redo history for drawings
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Helper to add to history when drawings change
  const addToHistory = useCallback((newDrawings) => {
    setDrawingHistory(prev => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add current state
      newHistory.push([...newDrawings]);
      // Limit history to 50 states
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);
  
  // Undo function
  const undoDrawing = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setDrawings(drawingHistory[newIndex] || []);
    } else if (historyIndex === 0 && drawings.length > 0) {
      // At start of history but have drawings - clear them
      setHistoryIndex(-1);
      setDrawings([]);
    }
  }, [historyIndex, drawingHistory, drawings]);
  
  // Redo function
  const redoDrawing = useCallback(() => {
    if (historyIndex < drawingHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setDrawings(drawingHistory[newIndex] || []);
    }
  }, [historyIndex, drawingHistory]);
  
  // Wrap setDrawings to track history
  const setDrawingsWithHistory = useCallback((updater) => {
    setDrawings(prev => {
      const newDrawings = typeof updater === 'function' ? updater(prev) : updater;
      // Only add to history if drawings actually changed
      if (JSON.stringify(newDrawings) !== JSON.stringify(prev)) {
        addToHistory(newDrawings);
      }
      return newDrawings;
    });
  }, [addToHistory]);
  
  // Save drawings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(`elemental-drawings-${symbol}`, JSON.stringify(drawings));
    } catch (e) { console.warn('Failed to save drawings:', e); }
  }, [drawings, symbol]);
  
  // Load drawings when symbol changes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`elemental-drawings-${symbol}`);
      setDrawings(saved ? JSON.parse(saved) : []);
    } catch { setDrawings([]); }
  }, [symbol]);
  
  // Text annotation state
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputPos, setTextInputPos] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');
  const textInputRef = useRef(null);
  
  // Drawing style state
  const [drawingColor, setDrawingColor] = useState('#667eea');
  const [drawingStrokeWidth, setDrawingStrokeWidth] = useState(2);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const drawingColors = ['#667eea', '#ef4444', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6', '#ffffff', '#6b7280'];
  const strokeWidths = [1, 2, 3, 5];
  
  // Zoom & Pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState(0);
  
  // Touch gesture state for pinch-to-zoom
  const touchStartRef = useRef(null);
  const lastTouchDistanceRef = useRef(null);
  
  // === NEW FEATURES STATE ===
  
  // Sub-chart panels (RSI, MACD)
  const [showRSI, setShowRSI] = useState(false);
  const [showMACD, setShowMACD] = useState(false);
  
  // Context menu
  const [contextMenu, setContextMenu] = useState(null); // { x, y, price, candle, drawingId }
  
  // Price alerts - load from API, fallback to localStorage
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertPrice, setAlertPrice] = useState('');
  const [showAlertsDropdown, setShowAlertsDropdown] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  // Load alerts from API on mount
  useEffect(() => {
    const loadAlerts = async () => {
      const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
      if (!token) {
        // Not logged in - use localStorage fallback
        try {
          const saved = localStorage.getItem('elemental-price-alerts');
          if (saved) setPriceAlerts(JSON.parse(saved));
        } catch { /* use empty */ }
        return;
      }
      
      setAlertsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPriceAlerts(data.alerts || []);
        }
      } catch (err) {
        console.warn('Failed to load alerts from API:', err);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('elemental-price-alerts');
          if (saved) setPriceAlerts(JSON.parse(saved));
        } catch { /* use empty */ }
      } finally {
        setAlertsLoading(false);
      }
    };
    loadAlerts();
  }, []);
  
  // Save alerts to localStorage as backup
  useEffect(() => {
    try {
      localStorage.setItem('elemental-price-alerts', JSON.stringify(priceAlerts));
    } catch { /* ignore */ }
  }, [priceAlerts]);
  
  // API helper functions for alerts
  const createAlertAPI = async (alert) => {
    const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
    if (!token) return null;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/alerts`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alert)
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('Failed to create alert via API:', err);
    }
    return null;
  };
  
  const deleteAlertAPI = async (id) => {
    const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
    if (!token) return;
    
    try {
      await fetch(`${API_BASE_URL}/api/alerts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.warn('Failed to delete alert via API:', err);
    }
  };
  
  const toggleAlertAPI = async (id, active) => {
    const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
    if (!token) return;
    
    try {
      await fetch(`${API_BASE_URL}/api/alerts/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active })
      });
    } catch (err) {
      console.warn('Failed to toggle alert via API:', err);
    }
  };
  
  // Alert form state (persisted when dropdown closes)
  const [alertFormState, setAlertFormState] = useState({
    type: 'price',
    condition: 'above',
    notifyMethod: ['app'],
    price: '',
    name: ''
  });
  
  // Volume profile
  const [showVolumeProfile, setShowVolumeProfile] = useState(false);
  
  // Support/Resistance
  const [showSupportResistance, setShowSupportResistance] = useState(false);
  
  // Pattern recognition
  const [showPatterns, setShowPatterns] = useState(false); // Collapsed by default
  const [showPatternLibrary, setShowPatternLibrary] = useState(false);
  
  // Revolutionary Features
  const [showSmartMoney, setShowSmartMoney] = useState(false);
  const [showTimeMachine, setShowTimeMachine] = useState(false);
  const [showScenario, setShowScenario] = useState(false);
  
  // AI Analysis
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  
  // Keyboard shortcuts modal
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  
  // Onboarding tour with spotlight
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      return !localStorage.getItem('elemental-onboarding-complete');
    } catch { return true; }
  });
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const onboardingSteps = [
    {
      title: 'Welcome to ELEMENTAL',
      description: 'Your professional-grade charting platform for technical analysis. Let\'s take a quick tour!',
      selector: null,
      position: 'center'
    },
    {
      title: 'Drawing Tools',
      description: 'Access crosshair, trendlines, channels, Fibonacci tools, shapes, text annotations, freehand drawing, and eraser. Pro users get advanced pattern recognition tools.',
      selector: '.fs-toolbar',
      position: 'right'
    },
    {
      title: 'Symbol Search',
      description: 'Search stocks, ETFs, forex, and crypto across 50+ global exchanges. Type any ticker or company name to find it instantly.',
      selector: '.fs-symbol-selector',
      position: 'bottom'
    },
    {
      title: 'Timeframe Selection',
      description: 'Choose from 1D to 5Y timeframes on the free plan. Pro and Enterprise unlock real-time data with intervals from 1 second to 45 minutes.',
      selector: '.fs-timeframe-selector',
      position: 'bottom'
    },
    {
      title: 'Multi-Chart Layouts',
      description: 'Compare multiple symbols side-by-side. Free users get up to 2 charts, Pro unlocks 4-chart grid layouts with synchronized crosshairs.',
      selector: '.fs-layout-wrapper',
      position: 'bottom'
    },
    {
      title: 'Indicators & AI Analysis',
      description: 'Add technical indicators like RSI, MACD, Bollinger Bands, and moving averages. AI Analysis provides instant buy/sell signals and pattern detection.',
      selector: '.fs-indicators-ai-group',
      position: 'bottom'
    },
    {
      title: 'Pro Features',
      description: 'Smart Money tracks institutional flow, Time Machine replays historical price action, Scenarios models what-if situations, and Patterns auto-detects chart formations. Available on Pro and Enterprise plans.',
      selector: '.fs-features-group',
      position: 'bottom'
    },
    {
      title: 'Watchlist & Fundamentals',
      description: 'Track your favorite symbols and view key fundamentals like P/E ratio, market cap, and earnings. Pro users get real-time alerts and extended data.',
      selector: '.fs-right-panel',
      position: 'left'
    },
    {
      title: 'Keyboard Shortcuts',
      description: 'Press ? anytime to see all shortcuts. Use Ctrl+Z to undo, S for screenshot, I for indicators, and number keys 1-4 for layouts.',
      selector: null,
      position: 'center'
    },
    {
      title: 'You\'re Ready!',
      description: 'Start analyzing like a pro. Draw trendlines, add indicators, and customize your workspace. Happy trading!',
      selector: null,
      position: 'center'
    }
  ];
  
  // Update spotlight position when step changes
  useEffect(() => {
    if (!showOnboarding) return;
    
    const step = onboardingSteps[onboardingStep];
    
    if (!step?.selector) {
      setSpotlightRect(null);
      setIsTransitioning(false);
      return;
    }
    
    // Find element and set spotlight
    const element = document.querySelector(step.selector);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      setSpotlightRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      });
    } else {
      setSpotlightRect(null);
    }
    
    // End transition after spotlight is positioned
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [onboardingStep, showOnboarding]);
  
  const completeOnboarding = () => {
    setShowOnboarding(false);
    setSpotlightRect(null);
    setIsTransitioning(false);
    try {
      localStorage.setItem('elemental-onboarding-complete', 'true');
    } catch {}
  };
  
  const nextStep = () => {
    if (onboardingStep < onboardingSteps.length - 1) {
      setIsTransitioning(true);
      setSpotlightRect(null);
      // Wait for fade out, then change step
      setTimeout(() => {
        setOnboardingStep(prev => prev + 1);
      }, 150);
    } else {
      completeOnboarding();
    }
  };
  
  const prevStep = () => {
    if (onboardingStep > 0) {
      setIsTransitioning(true);
      setSpotlightRect(null);
      // Wait for fade out, then change step
      setTimeout(() => {
        setOnboardingStep(prev => prev - 1);
      }, 150);
    }
  };
  
  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if typing in an input or textarea
      const tagName = e.target.tagName?.toUpperCase();
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Only allow Escape to work in inputs
        if (e.key === 'Escape') {
          e.target.blur();
        }
        return;
      }
      
      // Don't trigger shortcuts when text input is showing
      if (showTextInput) {
        if (e.key === 'Escape') {
          setShowTextInput(false);
          setTextInputPos(null);
          setTextInputValue('');
        }
        return;
      }
      
      // Undo/Redo - works even when modals are open
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redoDrawing();
        } else {
          undoDrawing();
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redoDrawing();
        return;
      }
      
      // Don't trigger shortcuts when modals are open (except Escape)
      if (showKeyboardShortcuts || showIndicatorsModal || showAIAnalysis) {
        if (e.key === 'Escape') {
          setShowKeyboardShortcuts(false);
          setShowIndicatorsModal(false);
          setShowAIAnalysis(false);
        }
        return;
      }
      
      const key = e.key.toLowerCase();
      
      // Layout shortcuts (1-4)
      if (key === '1') { setChartLayout('single'); return; }
      if (key === '2') { setChartLayout('2h'); return; }
      if (key === '3') { setChartLayout('3'); return; }
      if (key === '4') { setChartLayout('4grid'); return; }
      
      // Chart type
      if (key === 'c') { 
        setChartType('candle'); 
        if (chartLayout !== 'single') setMultiChartTypes(['candle', 'candle', 'candle']);
        return; 
      }
      if (key === 'a') { 
        setChartType('area'); 
        if (chartLayout !== 'single') setMultiChartTypes(['area', 'area', 'area']);
        return; 
      }
      
      // Timeframe shortcuts
      if (key === 'd') { handleTimeframeChange('1D'); return; }
      if (key === 'w') { handleTimeframeChange('1W'); return; }
      if (key === 'm') { handleTimeframeChange('1M'); return; }
      if (key === 'q') { handleTimeframeChange('3M'); return; }
      if (key === 'y') { handleTimeframeChange('1Y'); return; }
      
      // Tools
      if (key === 'x') { setSelectedTool('crosshair'); return; }
      if (key === 'p') { setSelectedTool('pointer'); return; }
      if (key === 'l') { setSelectedTool('line'); return; }
      if (key === 'h') { setSelectedTool('hline'); return; }
      if (key === 'r') { setSelectedTool('rectangle'); return; }
      
      // Features
      if (key === 'i') { setShowIndicatorsModal(true); return; }
      if (key === 's') { e.preventDefault(); setShowAIAnalysis(true); return; }
      
      // Help - check for ? (shift+/) or just /
      if (e.key === '?' || e.key === '/') { 
        setShowKeyboardShortcuts(true); 
        return; 
      }
      
      // Escape to close modals
      if (key === 'escape') {
        setShowKeyboardShortcuts(false);
        setShowIndicatorsModal(false);
        setShowAIAnalysis(false);
        setShowLayoutMenu(false);
        return;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [chartLayout, handleTimeframeChange, showKeyboardShortcuts, showIndicatorsModal, showAIAnalysis, showTextInput, undoDrawing, redoDrawing]);
  
  // Replay Mode - Step through historical data
  const [replayMode, setReplayMode] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(500); // ms between candles
  const [replayPlaying, setReplayPlaying] = useState(false);
  const replayIntervalRef = useRef(null);
  
  // Settings Panel
  const [showSettings, setShowSettings] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [chartSettings, setChartSettings] = useState(() => {
    // Try to load saved settings from localStorage
    const savedSettings = localStorage.getItem('elemental-chart-settings');
    const defaultSettings = {
    // Chart Appearance
    theme: 'dark',
    candleUpColor: '#26a69a',
    candleDownColor: '#ef5350',
    backgroundColor: '#0a0a0f',
    gridColor: '#ffffff',
    gridOpacity: 0.04,
    textColor: '#ffffff',
    gridLines: true,
    
    // Chart Behavior
    autoScale: true,
    logScale: false,
    showVolume: false,
    showCrosshair: true,
    snapToCandle: true,
    
    // Price Scale
    priceScalePosition: 'right',
    showPriceLabels: true,
    showLastPrice: true,
    
    // Time Scale
    showTimeLabels: true,
    timezone: 'local',
    use24Hour: true,
    
    // Trading
    showPositions: true,
    showOrders: true,
    orderLineColor: '#f59e0b',
    
    // Performance
    maxBars: 500,
    enableAnimations: true,
    
    // Alerts
    alertSound: true,
    alertPopup: true,
    
    // Data
    extendedHours: false,
    adjustForDividends: true,
    
    // Hotkeys
    enableHotkeys: true
    };
    
    if (savedSettings) {
      try {
        return { ...defaultSettings, ...JSON.parse(savedSettings) };
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Theme presets
  const applyTheme = useCallback((themeName) => {
    const themes = {
      dark: {
        backgroundColor: '#0a0a0f',
        gridColor: '#ffffff',
        gridOpacity: 0.04,
        textColor: '#ffffff',
        candleUpColor: '#26a69a',
        candleDownColor: '#ef5350'
      },
      light: {
        backgroundColor: '#ffffff',
        gridColor: '#000000',
        gridOpacity: 0.08,
        textColor: '#1a1a2e',
        candleUpColor: '#22c55e',
        candleDownColor: '#ef4444'
      }
    };
    
    if (themes[themeName]) {
      setChartSettings(prev => ({
        ...prev,
        theme: themeName,
        ...themes[themeName]
      }));
    }
  }, []);
  
  // Screenshot function with ELEMENTAL watermark
  const takeScreenshot = useCallback(async () => {
    if (!chartRef.current) return;
    
    try {
      const svg = chartRef.current;
      const svgData = new XMLSerializer().serializeToString(svg);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      canvas.width = svg.clientWidth || 1200;
      canvas.height = svg.clientHeight || 800;
      
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      img.onload = () => {
        // Fill background
        ctx.fillStyle = chartSettings.theme === 'light' ? '#ffffff' : '#0a0a0f';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        // Add ELEMENTAL watermark - bottom right
        const padding = 20;
        const watermarkHeight = 36;
        
        // Watermark background pill
        ctx.fillStyle = chartSettings.theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
        const watermarkWidth = 180;
        ctx.beginPath();
        ctx.roundRect(canvas.width - watermarkWidth - padding, canvas.height - watermarkHeight - padding, watermarkWidth, watermarkHeight, 8);
        ctx.fill();
        
        // ELEMENTAL logo icon (simplified atom symbol)
        const iconX = canvas.width - watermarkWidth - padding + 14;
        const iconY = canvas.height - padding - watermarkHeight / 2;
        ctx.strokeStyle = '#667eea';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(iconX, iconY, 8, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(iconX, iconY, 12, 6, Math.PI / 4, 0, Math.PI * 2);
        ctx.stroke();
        
        // ELEMENTAL text
        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        ctx.fillStyle = chartSettings.theme === 'light' ? '#1a1a2e' : '#ffffff';
        ctx.textAlign = 'left';
        ctx.fillText('ELEMENTAL', iconX + 20, iconY + 5);
        
        // Symbol and date - top left info bar
        ctx.fillStyle = chartSettings.theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
        ctx.beginPath();
        ctx.roundRect(padding, padding, 220, 32, 6);
        ctx.fill();
        
        ctx.font = 'bold 13px Inter, system-ui, sans-serif';
        ctx.fillStyle = chartSettings.theme === 'light' ? '#1a1a2e' : '#ffffff';
        ctx.textAlign = 'left';
        ctx.fillText(`${symbol} • ${timeframe}`, padding + 12, padding + 21);
        
        ctx.font = '11px Inter, system-ui, sans-serif';
        ctx.fillStyle = chartSettings.theme === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)';
        const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        ctx.fillText(dateStr, padding + 140, padding + 21);
        
        // Download
        const link = document.createElement('a');
        link.download = `${symbol}_${timeframe}_${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        URL.revokeObjectURL(url);
      };
      
      img.src = url;
    } catch (err) {
      console.error('Screenshot failed:', err);
    }
  }, [symbol, timeframe, chartSettings.theme]);
  
  // Export to CSV function
  const exportToCSV = useCallback(() => {
    if (!data?.length) return;
    
    // Build CSV content
    const headers = ['Date', 'Open', 'High', 'Low', 'Close', 'Volume'];
    const rows = data.map(d => [
      d.date,
      d.open,
      d.high,
      d.low,
      d.close,
      d.volume || 0
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${symbol}_${timeframe}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [data, symbol, timeframe]);
  
  // Export to JSON function (includes drawings and indicators)
  const exportToJSON = useCallback(() => {
    if (!data?.length) return;
    
    const exportData = {
      symbol,
      timeframe,
      exportDate: new Date().toISOString(),
      priceData: data,
      drawings: drawings,
      indicators: {
        sma20: indicators.sma20,
        sma50: indicators.sma50,
        ema20: indicators.ema20,
        bollinger: indicators.bollinger,
        volume: indicators.volume
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${symbol}_${timeframe}_full_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [data, symbol, timeframe, drawings, indicators]);
  
  // Replay Mode Controls
  const startReplay = useCallback(() => {
    if (!data?.length) return;
    setReplayMode(true);
    setReplayIndex(0);
    setReplayPlaying(false);
  }, [data]);
  
  const stopReplay = useCallback(() => {
    setReplayMode(false);
    setReplayPlaying(false);
    setReplayIndex(0);
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
  }, []);
  
  const toggleReplayPlay = useCallback(() => {
    if (replayPlaying) {
      // Pause
      setReplayPlaying(false);
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
        replayIntervalRef.current = null;
      }
    } else {
      // Play
      setReplayPlaying(true);
    }
  }, [replayPlaying]);
  
  const replayStepForward = useCallback(() => {
    if (!data?.length) return;
    setReplayIndex(prev => Math.min(prev + 1, data.length - 1));
  }, [data]);
  
  const replayStepBackward = useCallback(() => {
    setReplayIndex(prev => Math.max(prev - 1, 0));
  }, []);
  
  const replayGoToStart = useCallback(() => {
    setReplayIndex(0);
    setReplayPlaying(false);
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
  }, []);
  
  const replayGoToEnd = useCallback(() => {
    if (!data?.length) return;
    setReplayIndex(data.length - 1);
    setReplayPlaying(false);
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
  }, [data]);
  
  // Replay auto-play effect
  useEffect(() => {
    if (replayMode && replayPlaying && data?.length) {
      replayIntervalRef.current = setInterval(() => {
        setReplayIndex(prev => {
          if (prev >= data.length - 1) {
            setReplayPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, replaySpeed);
      
      return () => {
        if (replayIntervalRef.current) {
          clearInterval(replayIntervalRef.current);
        }
      };
    }
  }, [replayMode, replayPlaying, replaySpeed, data]);
  
  // Get visible data for replay mode (only show candles up to replayIndex)
  const replayData = useMemo(() => {
    if (!replayMode || !data?.length) return data;
    return data.slice(0, replayIndex + 1);
  }, [replayMode, data, replayIndex]);
  
  // Display data - use replayData when in replay mode, otherwise full data
  const displayData = replayMode ? replayData : data;
  
  // Comparison mode
  const [compareSymbol, setCompareSymbol] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [showCompareDropdown, setShowCompareDropdown] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');
  const [showCompareTechnicals, setShowCompareTechnicals] = useState(false);
  const [showCompareAIAnalysis, setShowCompareAIAnalysis] = useState(false);
  const compareDropdownRef = useRef(null);
  
  // Generate mock historical data for compare
  const generateMockHistoricalData = useCallback((sym, days = 90) => {
    const mockData = [];
    const basePrices = {
      'AAPL': 177, 'MSFT': 432, 'GOOGL': 175, 'AMZN': 198, 'NVDA': 142,
      'META': 585, 'TSLA': 352, 'AMD': 138, 'NFLX': 720, 'PLTR': 71,
      'COIN': 312, 'SPY': 605, 'QQQ': 520, 'BTC': 101000, 'ETH': 3800
    };
    let currentPrice = basePrices[sym] || 100 + Math.random() * 200;
    const volatility = sym === 'BTC' || sym === 'ETH' ? 0.03 : 0.02;
    const today = new Date();
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      const change = (Math.random() - 0.48) * volatility * currentPrice;
      currentPrice = Math.max(currentPrice + change, currentPrice * 0.5);
      const dayVol = currentPrice * 0.015;
      const open = currentPrice + (Math.random() - 0.5) * dayVol;
      const close = currentPrice;
      const high = Math.max(open, close) + Math.random() * dayVol;
      const low = Math.min(open, close) - Math.random() * dayVol;
      mockData.push({
        date: date.toISOString().split('T')[0],
        open: parseFloat(open.toFixed(2)),
        high: parseFloat(high.toFixed(2)),
        low: parseFloat(low.toFixed(2)),
        close: parseFloat(close.toFixed(2)),
        volume: Math.floor(Math.random() * 50000000) + 10000000
      });
    }
    return mockData;
  }, []);

  // Handle compare selection
  const handleCompare = useCallback(async (compareWith) => {
    setCompareSymbol(compareWith);
    setShowCompareDropdown(false);
    setCompareSearch('');
    setChartType('area'); // Force area chart for comparison
    
    try {
      let historicalData = null;
      try {
        const res = await fetch(`${API_BASE_URL}/api/stock/historical?symbol=${compareWith}&timeframe=${timeframe}`);
        if (res.ok) {
          const result = await res.json();
          if (result?.length > 0) historicalData = result;
        }
      } catch (e) {
        console.log('Using mock compare data');
      }
      
      if (!historicalData || historicalData.length === 0) {
        const days = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, '5Y': 1825 };
        historicalData = generateMockHistoricalData(compareWith, days[timeframe] || 90);
      }
      setCompareData(historicalData);
    } catch (err) {
      console.error('Error fetching compare data:', err);
      const days = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, '5Y': 1825 };
      setCompareData(generateMockHistoricalData(compareWith, days[timeframe] || 90));
    }
  }, [timeframe, generateMockHistoricalData]);

  // Clear compare
  const clearCompare = useCallback(() => {
    setCompareSymbol(null);
    setCompareData(null);
  }, []);

  // Compare suggestions
  const compareSuggestions = useMemo(() => {
    const stocks = [
      { symbol: 'SPY', name: 'S&P 500 ETF' },
      { symbol: 'QQQ', name: 'NASDAQ 100 ETF' },
      { symbol: 'AAPL', name: 'Apple Inc.' },
      { symbol: 'MSFT', name: 'Microsoft' },
      { symbol: 'GOOGL', name: 'Alphabet' },
      { symbol: 'AMZN', name: 'Amazon' },
      { symbol: 'NVDA', name: 'NVIDIA' },
      { symbol: 'TSLA', name: 'Tesla' },
      { symbol: 'META', name: 'Meta' },
      { symbol: 'AMD', name: 'AMD' },
    ].filter(s => s.symbol !== symbol);
    
    if (compareSearch) {
      const q = compareSearch.toLowerCase();
      return stocks.filter(s => 
        s.symbol.toLowerCase().includes(q) || 
        s.name.toLowerCase().includes(q)
      );
    }
    return stocks;
  }, [symbol, compareSearch]);

  // Close compare dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (compareDropdownRef.current && !compareDropdownRef.current.contains(e.target)) {
        setShowCompareDropdown(false);
      }
    };
    if (showCompareDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCompareDropdown]);
  
  // Events/Earnings markers
  const [showEvents, setShowEvents] = useState(true);
  const [events, setEvents] = useState([
    { date: '2024-02-15', type: 'earnings', title: 'Q4 Earnings' },
    { date: '2024-05-15', type: 'earnings', title: 'Q1 Earnings' },
    { date: '2024-08-15', type: 'earnings', title: 'Q2 Earnings' },
    { date: '2024-03-15', type: 'dividend', title: 'Dividend Ex-Date' },
  ]);
  
  // Mini-map navigator
  const [showMiniMap, setShowMiniMap] = useState(false);
  const [viewportStart, setViewportStart] = useState(0);
  const [viewportEnd, setViewportEnd] = useState(100);
  
  // Animation states
  const [isAnimating, setIsAnimating] = useState(false);
  const [pulsePrice, setPulsePrice] = useState(true);
  
  // Keyboard navigation
  const [selectedCandleIndex, setSelectedCandleIndex] = useState(null);
  
  // Layout constants
  const TOOLBAR_WIDTH = 50, HEADER_HEIGHT = 50, PRICE_AXIS_WIDTH = 70, TIME_AXIS_HEIGHT = 30, VOLUME_HEIGHT = 60;
  const RIGHT_PANEL_WIDTH = showRightPanel ? 240 : 0;
  // Smaller indicator heights to minimize main chart impact
  const RSI_HEIGHT = showRSI ? 80 : 0;
  const MACD_HEIGHT = showMACD ? 80 : 0;
  const SUB_CHARTS_HEIGHT = RSI_HEIGHT + MACD_HEIGHT;

  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }, []);
  useEffect(() => { const update = () => setDimensions({ width: window.innerWidth, height: window.innerHeight }); update(); window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }, []);
  
  // Keyboard shortcuts (enhanced with arrow navigation)
  useEffect(() => { 
    const handleKeyDown = (e) => { 
      // Don't trigger if typing in an input or textarea
      const tagName = e.target.tagName?.toUpperCase();
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      // Don't trigger when text input is showing
      if (showTextInput) {
        return;
      }
      
      if (e.key === 'Escape') {
        if (contextMenu) {
          setContextMenu(null);
        } else if (showDrawingMenu) {
          setShowDrawingMenu(null);
        } else if (showAIAnalysis) {
          setShowAIAnalysis(false);
        } else if (showAlertModal) {
          setShowAlertModal(false);
        } else if (showIndicatorsModal) {
          // Modal handles its own escape
        } else {
          handleClose();
        }
        return;
      }
      
      // Arrow key navigation through candles
      if (!showIndicatorsModal && !showAIAnalysis && data?.length) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setSelectedCandleIndex(prev => {
            const newIdx = prev === null ? data.length - 1 : Math.max(0, prev - 1);
            setHoveredIndex(newIdx);
            return newIdx;
          });
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setSelectedCandleIndex(prev => {
            const newIdx = prev === null ? 0 : Math.min(data.length - 1, prev + 1);
            setHoveredIndex(newIdx);
            return newIdx;
          });
          return;
        }
        if (e.key === 'Home') {
          e.preventDefault();
          setSelectedCandleIndex(0);
          setHoveredIndex(0);
          return;
        }
        if (e.key === 'End') {
          e.preventDefault();
          setSelectedCandleIndex(data.length - 1);
          setHoveredIndex(data.length - 1);
          return;
        }
      }
      
      // Tool shortcuts (only if hotkeys enabled)
      if (chartSettings.enableHotkeys && !showIndicatorsModal && !showAIAnalysis && !e.ctrlKey && !e.metaKey) {
        switch(e.key.toLowerCase()) {
          case 'c': setSelectedTool('crosshair'); break;
          case 'v': setSelectedTool('pointer'); break;
          case 't': setSelectedTool('trendline'); break;
          case 'h': setSelectedTool('hline'); break;
          case 'm': setSelectedTool('measure'); break;
          case 'x': setSelectedTool('text'); break;
          case 'r': if (!replayMode) { setZoomLevel(1); setPanOffset(0); } else { startReplay(); } break;
          case '+': case '=': setZoomLevel(prev => Math.min(prev * 1.2, 5)); break;
          case '-': setZoomLevel(prev => Math.max(prev / 1.2, 0.5)); break;
          case 'a': setShowAIAnalysis(true); break; // AI Analysis
          case 's': takeScreenshot(); break; // Screenshot
          case 'p': setShowPatterns(prev => !prev); break; // Patterns
          case 'i': setShowIndicatorsModal(true); break; // Indicators
          case 'd': setSelectedTool('trendline'); break; // Drawing mode
          case 'f': document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen(); break; // Fullscreen
          default: break;
        }
        
        // Replay mode controls
        if (replayMode) {
          if (e.key === ' ' || e.key === 'Space') {
            e.preventDefault();
            toggleReplayPlay();
          }
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            replayStepBackward();
          }
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            replayStepForward();
          }
          if (e.key === 'Home') {
            e.preventDefault();
            replayGoToStart();
          }
          if (e.key === 'End') {
            e.preventDefault();
            replayGoToEnd();
          }
          if (e.key === 'Escape') {
            stopReplay();
          }
        } else {
          // Normal arrow key navigation (not in replay mode)
          if (e.key === 'ArrowLeft' && data?.length) {
            e.preventDefault();
            setHoveredIndex(prev => prev === null ? data.length - 1 : Math.max(0, prev - 1));
          }
          if (e.key === 'ArrowRight' && data?.length) {
            e.preventDefault();
            setHoveredIndex(prev => prev === null ? 0 : Math.min(data.length - 1, prev + 1));
          }
          if (e.key === 'ArrowUp' && data?.length) {
            e.preventDefault();
            setZoomLevel(prev => Math.min(prev * 1.1, 5));
          }
          if (e.key === 'ArrowDown' && data?.length) {
            e.preventDefault();
            setZoomLevel(prev => Math.max(prev / 1.1, 0.5));
          }
        }
      }
    }; 
    window.addEventListener('keydown', handleKeyDown); 
    return () => window.removeEventListener('keydown', handleKeyDown); 
  }, [handleClose, showIndicatorsModal, showDrawingMenu, showAIAnalysis, showAlertModal, contextMenu, data, chartSettings.enableHotkeys, takeScreenshot, replayMode, toggleReplayPlay, replayStepBackward, replayStepForward, replayGoToStart, replayGoToEnd, stopReplay, startReplay, showTextInput]);

  // Global mouseup listener for pencil drawing - saves drawing when mouse released anywhere
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDrawing && selectedTool === 'pencil') {
        const path = pencilPathRef.current;
        if (path.length > 1) {
          // Save all points for exact cursor path (like MS Paint)
          const newDrawing = {
            id: Date.now(),
            type: 'pencil',
            path: [...path],
            color: drawingColor,
        strokeWidth: drawingStrokeWidth,
          };
          setDrawingsWithHistory(prev => [...prev, newDrawing]);
        }
        // Reset
        pencilPathRef.current = [];
        setIsDrawing(false);
        setDrawStart(null);
        setCurrentPencilPath([]);
      }
    };
    
    if (selectedTool === 'pencil' && isDrawing) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDrawing, selectedTool]);

  // Close context menu and drawing menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close context menu from this handler - it has its own logic
      // Only close drawing menus here
      const isInsideToolbarMenu = e.target.closest('.fs-toolbar-menu');
      const isToolbarButton = e.target.closest('.fs-toolbar button');
      
      if (!isInsideToolbarMenu && !isToolbarButton && showDrawingMenu) {
        setShowDrawingMenu(null);
      }
    };
    
    if (showDrawingMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDrawingMenu]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.fs-export-dropdown')) {
        setShowExportMenu(false);
      }
    };
    
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  // Close layout menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.fs-layout-dropdown')) {
        setShowLayoutMenu(false);
      }
    };
    
    if (showLayoutMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLayoutMenu]);

  // Separate handler for closing context menu on click anywhere
  useEffect(() => {
    const handleCloseContextMenu = (e) => {
      // Don't close if clicking inside the context menu itself
      if (e.target.closest('.fs-context-menu')) {
        return;
      }
      setContextMenu(null);
    };
    
    if (contextMenu) {
      // Use click instead of mousedown, and add after a delay
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleCloseContextMenu);
      }, 50);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleCloseContextMenu);
      };
    }
  }, [contextMenu]);

  // Calculate RSI and MACD data
  const rsiData = useMemo(() => showRSI && data?.length > 14 ? calculateRSI(data) : [], [data, showRSI]);
  const macdData = useMemo(() => showMACD && data?.length > 26 ? calculateMACD(data) : { macdLine: [], signalLine: [], histogram: [] }, [data, showMACD]);
  const supportResistance = useMemo(() => showSupportResistance && data?.length > 20 ? calculateSupportResistance(data) : { support: [], resistance: [] }, [data, showSupportResistance]);
  const volumeProfile = useMemo(() => showVolumeProfile && data?.length > 5 ? calculateVolumeProfile(data) : [], [data, showVolumeProfile]);
  const patterns = useMemo(() => showPatterns && data?.length > 50 ? detectPatterns(data) : [], [data, showPatterns]);

  // Alert checking system
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [showAlertNotification, setShowAlertNotification] = useState(null);
  
  // Check alerts when price changes
  useEffect(() => {
    if (!data?.length || !priceAlerts.length) return;
    
    const currentPrice = data[data.length - 1]?.close;
    if (!currentPrice) return;
    
    priceAlerts.forEach(alert => {
      if (!alert.active || alert.triggered) return;
      
      let isTriggered = false;
      
      if (alert.type === 'price') {
        if (alert.condition === 'above' && currentPrice >= alert.price) isTriggered = true;
        if (alert.condition === 'below' && currentPrice <= alert.price) isTriggered = true;
        if (alert.condition === 'cross') {
          // Check if price crossed the alert level
          const prevPrice = data[data.length - 2]?.close;
          if (prevPrice && ((prevPrice < alert.price && currentPrice >= alert.price) || 
              (prevPrice > alert.price && currentPrice <= alert.price))) {
            isTriggered = true;
          }
        }
      } else if (alert.type === 'percent') {
        const startPrice = data[0]?.close;
        if (startPrice) {
          const pctChange = ((currentPrice - startPrice) / startPrice) * 100;
          if (alert.condition === 'above' && pctChange >= alert.price) isTriggered = true;
          if (alert.condition === 'below' && pctChange <= -alert.price) isTriggered = true;
        }
      } else if (alert.type === 'rsi' && rsiData.length > 0) {
        const currentRSI = rsiData[rsiData.length - 1];
        if (alert.condition === 'above' && currentRSI >= alert.price) isTriggered = true;
        if (alert.condition === 'below' && currentRSI <= alert.price) isTriggered = true;
      }
      
      if (isTriggered) {
        // Mark alert as triggered
        setPriceAlerts(prev => prev.map(a => 
          a.id === alert.id ? { ...a, triggered: true, triggeredAt: new Date().toISOString() } : a
        ));
        
        // Play sound if enabled
        if (chartSettings.alertSound) {
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = 880;
            oscillator.type = 'sine';
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
          } catch (e) {
            console.log('Audio not available');
          }
        }
        
        // Show popup notification if enabled
        if (chartSettings.alertPopup) {
          setShowAlertNotification({
            id: alert.id,
            symbol: alert.symbol,
            name: alert.name,
            price: currentPrice,
            alertPrice: alert.price,
            condition: alert.condition,
            type: alert.type
          });
          
          // Auto-dismiss after 5 seconds
          setTimeout(() => {
            setShowAlertNotification(null);
          }, 5000);
        }
        
        // Browser notification if permitted
        if (Notification.permission === 'granted') {
          new Notification(`Alert: ${alert.symbol}`, {
            body: `${alert.name} - Price: $${currentPrice.toFixed(2)}`,
            icon: '/favicon.ico'
          });
        }
      }
    });
  }, [data, priceAlerts, chartSettings.alertSound, chartSettings.alertPopup, rsiData]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const chartLeft = TOOLBAR_WIDTH;
  const chartWidth = dimensions.width - TOOLBAR_WIDTH - PRICE_AXIS_WIDTH - RIGHT_PANEL_WIDTH;
  
  // Keep original layout - volume and dates at bottom stay fixed
  const chartTop = HEADER_HEIGHT;
  const volumeTop = dimensions.height - TIME_AXIS_HEIGHT - VOLUME_HEIGHT;
  // Candle area shrinks when subcharts shown - they take space from TOP of candle area
  const mainChartHeight = volumeTop - chartTop - SUB_CHARTS_HEIGHT - 10;
  // Offset for drawing candles - push them down by subchart height
  const candleYOffset = SUB_CHARTS_HEIGHT;

  // Use displayData for calculations in replay mode
  const chartDataSource = displayData || data;
  
  const { maxPrice, priceRange, maxVolume } = useMemo(() => {
    if (!chartDataSource?.length) return { maxPrice: 100, priceRange: 100, maxVolume: 1 };
    const validData = chartDataSource.filter(d => d && d.high != null && d.low != null);
    if (!validData.length) return { maxPrice: 100, priceRange: 100, maxVolume: 1 };
    const prices = validData.flatMap(d => [d.high, d.low]);
    const min = Math.min(...prices), max = Math.max(...prices), pad = (max - min) * 0.08;
    return { maxPrice: max + pad, priceRange: (max - min) + pad * 2, maxVolume: Math.max(...chartDataSource.map(d => d.volume || 0)) || 1 };
  }, [chartDataSource]);

  const sma20 = useMemo(() => (indicators.sma20 && chartDataSource?.length > 0) ? calculateSMA(chartDataSource, 20) : [], [chartDataSource, indicators.sma20]);
  const sma50 = useMemo(() => (indicators.sma50 && chartDataSource?.length > 0) ? calculateSMA(chartDataSource, 50) : [], [chartDataSource, indicators.sma50]);
  const ema20 = useMemo(() => (indicators.ema20 && chartDataSource?.length > 0) ? calculateEMA(chartDataSource, 20) : [], [chartDataSource, indicators.ema20]);
  const bollinger = useMemo(() => (indicators.bollinger && chartDataSource?.length > 0) ? calculateBollinger(chartDataSource) : [], [chartDataSource, indicators.bollinger]);

  // Calculate these values for both loading and loaded states
  const candleWidth = chartDataSource?.length ? Math.max(1, Math.min(8, (chartWidth / chartDataSource.length) * 0.5)) : 5;
  const candleGap = chartDataSource?.length ? (chartWidth - candleWidth * chartDataSource.length) / (chartDataSource.length + 1) : 5;
  const getX = (i) => chartLeft + candleGap + i * (candleWidth + candleGap);
  // getY now includes offset to push candles down when subcharts are shown
  const getY = (price) => chartTop + candleYOffset + ((maxPrice - price) / priceRange) * mainChartHeight;
  const getPriceFromY = (y) => maxPrice - ((y - chartTop - candleYOffset) / mainChartHeight) * priceRange;

  // Drawing tool handlers
  const isDrawingTool = ['trendline', 'hline', 'vline', 'ray', 'channel', 'fibretracement', 'fibextension', 'rectangle', 'ellipse', 'triangle', 'arrow', 'measure', 'pencil'].includes(selectedTool);
  const isTextTool = selectedTool === 'text';
  const isPencilTool = selectedTool === 'pencil';

  // Focus text input when it appears
  useEffect(() => {
    if (showTextInput && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [showTextInput]);

  const handleChartMouseDown = useCallback((e) => {
    // Only handle left click
    if (e.button !== 0) return;
    
    // Prevent drawing when clicking on UI elements
    const target = e.target;
    const isUIElement = target.closest('.fs-ai-chat-window') ||
                        target.closest('.fs-right-panel') ||
                        target.closest('.fs-header') ||
                        target.closest('.fs-toolbar') ||
                        target.closest('.fs-context-menu') ||
                        target.closest('.fs-alerts-dropdown') ||
                        target.closest('.fs-patterns-container') ||
                        target.closest('.tv-modal-overlay') ||
                        target.closest('.fs-pattern-lib-overlay') ||
                        target.closest('.fs-drawing-menu') ||
                        target.closest('button') ||
                        target.closest('input') ||
                        target.closest('select');
    
    if (isUIElement) return;
    
    // Get SVG coordinates
    const svgElement = chartRef.current;
    const rect = svgElement?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = getPriceFromY(y);
    
    // Handle text tool - single click to place
    if (selectedTool === 'text') {
      setTextInputPos({ x, y, price });
      setTextInputValue('');
      setShowTextInput(true);
      return;
    }
    
    // Check if it's a drawing tool
    const drawingTools = ['trendline', 'hline', 'vline', 'ray', 'channel', 'fibretracement', 'fibextension', 'rectangle', 'ellipse', 'triangle', 'arrow', 'measure', 'pencil'];
    if (!drawingTools.includes(selectedTool)) return;
    
    // Prevent text selection while drawing
    e.preventDefault();
    
    // Start drawing
    setIsDrawing(true);
    setDrawStart({ x, y, price });
    
    // For pencil, initialize the path with first point
    if (selectedTool === 'pencil') {
      pencilPathRef.current = [{ x, y }];
      setCurrentPencilPath([{ x, y }]);
    }
  }, [selectedTool, getPriceFromY]);

  const handleTextSubmit = useCallback(() => {
    if (textInputValue.trim() && textInputPos) {
      const newDrawing = {
        id: Date.now(),
        type: 'text',
        x: textInputPos.x,
        y: textInputPos.y,
        price: textInputPos.price,
        text: textInputValue.trim(),
        color: drawingColor,
        strokeWidth: drawingStrokeWidth,
      };
      setDrawingsWithHistory(prev => [...prev, newDrawing]);
    }
    setShowTextInput(false);
    setTextInputPos(null);
    setTextInputValue('');
  }, [textInputValue, textInputPos]);

  const handleTextKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Enter without Shift submits the text
      e.preventDefault();
      handleTextSubmit();
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Shift+Enter adds a new line - let the default behavior happen
      // The textarea will naturally add a newline
    } else if (e.key === 'Escape') {
      setShowTextInput(false);
      setTextInputPos(null);
      setTextInputValue('');
    }
  }, [handleTextSubmit]);

  const handleChartMouseUp = useCallback((e) => {
    // Prevent drawing when releasing on UI elements
    const target = e.target;
    const isUIElement = target.closest('.fs-ai-chat-window') ||
                        target.closest('.fs-right-panel') ||
                        target.closest('.fs-header') ||
                        target.closest('.fs-toolbar') ||
                        target.closest('.fs-context-menu') ||
                        target.closest('.fs-alerts-dropdown') ||
                        target.closest('.fs-patterns-container') ||
                        target.closest('.tv-modal-overlay') ||
                        target.closest('.fs-pattern-lib-overlay') ||
                        target.closest('.fs-drawing-menu') ||
                        target.closest('button') ||
                        target.closest('input') ||
                        target.closest('select');
    
    if (isUIElement) {
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentPencilPath([]);
      return;
    }
    
    if (!isDrawing || !drawStart) {
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentPencilPath([]);
      return;
    }
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = getPriceFromY(y);
    
    // Handle pencil tool - save the path
    if (selectedTool === 'pencil' && currentPencilPath.length > 1) {
      const newDrawing = {
        id: Date.now(),
        type: 'pencil',
        path: [...currentPencilPath],
        color: drawingColor,
        strokeWidth: drawingStrokeWidth,
      };
      setDrawingsWithHistory(prev => [...prev, newDrawing]);
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentPencilPath([]);
      return;
    } else if (selectedTool === 'pencil') {
      // Path too short, just reset
      setIsDrawing(false);
      setDrawStart(null);
      setCurrentPencilPath([]);
      return;
    }
    
    // Create the drawing for other tools
    const newDrawing = {
      id: Date.now(),
      type: selectedTool,
      start: drawStart,
      end: { x, y, price },
      color: drawingColor,
        strokeWidth: drawingStrokeWidth,
    };
    
    // For horizontal line, use only the price
    if (selectedTool === 'hline') {
      newDrawing.price = drawStart.price;
    }
    
    // For vertical line, use only the x position
    if (selectedTool === 'vline') {
      newDrawing.x = drawStart.x;
    }
    
    setDrawingsWithHistory(prev => [...prev, newDrawing]);
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentPencilPath([]);
  }, [isDrawing, drawStart, selectedTool, getPriceFromY, currentPencilPath]);

  // Right-click context menu handler
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !data?.length) return;
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = getPriceFromY(y);
    const idx = Math.floor((x - chartLeft - candleGap / 2) / (candleWidth + candleGap));
    const candle = idx >= 0 && idx < data.length ? data[idx] : null;
    
    setContextMenu({ x: e.clientX, y: e.clientY, price, candle, chartX: x, chartY: y });
  }, [data, getPriceFromY, chartLeft, candleGap, candleWidth]);

  // Context menu actions
  const handleSetAlert = useCallback((price) => {
    setAlertPrice(price.toFixed(2));
    setShowAlertModal(true);
    setContextMenu(null);
  }, []);

  const handleAddToWatchlist = useCallback(() => {
    if (!watchlist.find(w => w.symbol === symbol)) {
      const current = data[data.length - 1];
      setWatchlist(prev => [...prev, {
        symbol,
        name: companyName,
        price: current.close,
        change: current.close - data[0].close,
        changePct: ((current.close - data[0].close) / data[0].close) * 100,
        type: 'stock'
      }]);
    }
    setContextMenu(null);
  }, [symbol, companyName, data, watchlist]);

  const handleCopyPrice = useCallback((price) => {
    navigator.clipboard.writeText(price.toFixed(2));
    setContextMenu(null);
  }, []);

  // Refresh watchlist data - uses batch endpoint (1 API call for all symbols)
  const refreshWatchlistData = useCallback(async () => {
    if (watchlist.length === 0) return;
    
    setWatchlistLoading(true);
    try {
      // Use batch endpoint - single API call for all symbols
      const symbols = watchlist.map(w => w.symbol).join(',');
      const res = await fetch(`${API_BASE_URL}/api/stocks/quotes?symbols=${symbols}`);
      
      if (res.ok) {
        const data = await res.json();
        const quoteMap = new Map(data.quotes?.map(q => [q.symbol, q]) || []);
        
        setWatchlist(prev => prev.map(item => {
          const quote = quoteMap.get(item.symbol.toUpperCase());
          if (quote && !quote.error) {
            return {
              ...item,
              price: quote.price || item.price,
              change: quote.change || item.change,
              changePct: quote.changePercent || item.changePct
            };
          }
          return item;
        }));
        
        if (data.partial) {
          console.warn('Some quotes missing due to rate limit:', data.missing);
        }
      }
    } catch (error) {
      console.error('Failed to refresh watchlist:', error);
    } finally {
      setWatchlistLoading(false);
    }
  }, [watchlist]);

  // Add symbol to watchlist
  const addToWatchlist = useCallback((newSymbol, name, type = 'stock') => {
    if (!watchlist.find(w => w.symbol === newSymbol)) {
      setWatchlist(prev => [...prev, {
        symbol: newSymbol,
        name: name || newSymbol,
        price: 0,
        change: 0,
        changePct: 0,
        type: type
      }]);
      // Trigger data fetch for new symbol
      refreshWatchlistData();
    }
    setShowSymbolSearch(false);
  }, [watchlist, refreshWatchlistData]);

  // Remove from watchlist
  const removeFromWatchlist = useCallback((symbolToRemove) => {
    setWatchlist(prev => prev.filter(w => w.symbol !== symbolToRemove));
  }, []);

  // useCallback must be called before any early returns
  const handleMouseMove = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !data?.length) return;
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const idx = Math.floor((x - chartLeft - candleGap / 2) / (candleWidth + candleGap));
    setHoveredIndex(idx >= 0 && idx < data.length ? idx : null);
    const price = maxPrice - ((y - chartTop) / mainChartHeight) * priceRange;
    setMousePos({ x, y, price });
    
    // For pencil tool - use ref for instant updates
    if (selectedTool === 'pencil' && isDrawing && e.buttons === 1) {
      const svgRect = chartRef.current?.getBoundingClientRect();
      if (svgRect) {
        const svgX = e.clientX - svgRect.left;
        const svgY = e.clientY - svgRect.top;
        // Add to ref immediately
        pencilPathRef.current.push({ x: svgX, y: svgY });
        // Update state for rendering (batched by React)
        setCurrentPencilPath([...pencilPathRef.current]);
      }
    }
  }, [data, chartLeft, candleGap, candleWidth, maxPrice, chartTop, mainChartHeight, priceRange, selectedTool, isDrawing]);

  // Touch handlers for pinch-to-zoom on tablets/mobile
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      lastTouchDistanceRef.current = distance;
      touchStartRef.current = { x: (touch1.clientX + touch2.clientX) / 2, y: (touch1.clientY + touch2.clientY) / 2 };
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && lastTouchDistanceRef.current !== null) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      
      const scale = distance / lastTouchDistanceRef.current;
      setZoomLevel(prev => Math.max(0.5, Math.min(5, prev * scale)));
      lastTouchDistanceRef.current = distance;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastTouchDistanceRef.current = null;
    touchStartRef.current = null;
  }, []);

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoomLevel(prev => Math.max(0.5, Math.min(5, prev * delta)));
    }
  }, []);

  // AI Analysis function
  const runAIAnalysis = useCallback(async () => {
    setAiAnalysisLoading(true);
    // Simulate AI analysis (would connect to your backend)
    setTimeout(() => {
      const current = data[data.length - 1];
      const priceChange = ((current.close - data[0].close) / data[0].close) * 100;
      const trend = priceChange > 5 ? 'bullish' : priceChange < -5 ? 'bearish' : 'neutral';
      
      setAiAnalysisResult({
        signal: trend === 'bullish' ? 'BUY' : trend === 'bearish' ? 'SELL' : 'HOLD',
        confidence: Math.floor(60 + Math.random() * 30),
        trend,
        summary: `${symbol} shows ${trend} momentum with ${Math.abs(priceChange).toFixed(1)}% ${priceChange >= 0 ? 'gain' : 'loss'} over the period. ${patterns.length > 0 ? `Detected pattern: ${patterns[0]?.name}.` : ''} Volume ${current.volume > (data.reduce((a, b) => a + b.volume, 0) / data.length) ? 'above' : 'below'} average.`,
        keyLevels: {
          resistance: supportResistance.resistance[0]?.price || current.high * 1.02,
          support: supportResistance.support[0]?.price || current.low * 0.98,
          target: trend === 'bullish' ? current.close * 1.1 : current.close * 0.95,
          stopLoss: trend === 'bullish' ? current.close * 0.95 : current.close * 1.05
        },
        indicators: {
          rsi: rsiData[rsiData.length - 1] || 50,
          macdSignal: macdData.histogram[macdData.histogram.length - 1] > 0 ? 'Bullish' : 'Bearish'
        }
      });
      setAiAnalysisLoading(false);
    }, 1500);
  }, [data, symbol, patterns, supportResistance, rsiData, macdData]);

  // Early return if no data - show loading skeleton instead of spinner
  // Show loading state with skeleton
  if (isLoading) {
    return (
      <div className="fs-overlay">
        <div className={`fs-container ${chartSettings.theme === 'light' ? 'light-theme' : ''}`}>
          {/* Skeleton Header */}
          <div className="fs-header">
            <div className="fs-header-left">
              <button className="fs-back-btn" onClick={handleClose}>
                <ArrowLeft size={18} /><span>Back</span>
              </button>
              <div className="fs-skeleton fs-skeleton-btn" style={{ width: '120px', height: '36px' }}></div>
              <div className="fs-skeleton fs-skeleton-btn" style={{ width: '80px', height: '36px' }}></div>
            </div>
            <div className="fs-header-center">
              <div className="fs-skeleton" style={{ width: '400px', height: '28px', borderRadius: '6px' }}></div>
            </div>
            <div className="fs-header-right">
              <div className="fs-skeleton fs-skeleton-btn" style={{ width: '100px', height: '36px' }}></div>
              <div className="fs-skeleton fs-skeleton-btn" style={{ width: '100px', height: '36px' }}></div>
            </div>
          </div>
          
          {/* Skeleton Chart Area */}
          <div className="fs-chart-skeleton">
            <div className="fs-skeleton-toolbar">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="fs-skeleton fs-skeleton-icon"></div>
              ))}
            </div>
            <div className="fs-skeleton-chart">
              <div className="fs-skeleton-candles">
                {[...Array(40)].map((_, i) => (
                  <div 
                    key={i} 
                    className="fs-skeleton fs-skeleton-candle"
                    style={{ 
                      height: `${30 + Math.random() * 40}%`,
                      animationDelay: `${i * 0.02}s`
                    }}
                  ></div>
                ))}
              </div>
              <div className="fs-skeleton-loading-text">
                <div className="fs-loading-spinner"></div>
                <span>Loading {symbol} chart data...</span>
              </div>
            </div>
            <div className="fs-skeleton-panel">
              <div className="fs-skeleton" style={{ width: '100%', height: '40px', marginBottom: '12px' }}></div>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="fs-skeleton" style={{ width: '100%', height: '50px', marginBottom: '8px' }}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="fs-overlay">
        <div className="fs-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
          <div style={{ color: '#fff', fontSize: '18px' }}>No data available for {symbol}</div>
          <button 
            className="fs-back-btn" 
            onClick={handleClose}
            style={{ marginTop: '20px' }}
          >
            <ArrowLeft size={18} /><span>Back</span>
          </button>
        </div>
      </div>
    );
  }

  const current = data[data.length - 1], first = data[0];
  const change = current.close - first.close, changePct = (change / first.close) * 100;
  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;
  const cursorX = hoveredIndex !== null ? getX(hoveredIndex) + candleWidth / 2 : null;
  // SVG is full height below header - subcharts are rendered INSIDE the SVG at the top
  const svgHeight = dimensions.height - HEADER_HEIGHT;
  const timeAxisY = svgHeight - TIME_AXIS_HEIGHT;
  const activeCount = Object.values(indicators).filter(Boolean).length;

  // Get cursor style based on selected tool
  const getCursorStyle = () => {
    const drawingTools = ['trendline', 'hline', 'vline', 'ray', 'channel', 'fibretracement', 'fibextension', 'rectangle', 'ellipse', 'triangle', 'arrow', 'measure', 'pencil', 'text'];
    if (drawingTools.includes(selectedTool)) return 'crosshair';
    if (selectedTool === 'eraser') return 'pointer';
    if (selectedTool === 'pointer') return 'default';
    return 'crosshair';
  };

  return (
    <div className="fs-overlay">
      <div 
        className={`fs-container ${chartSettings.theme === 'light' ? 'light-theme' : ''}`}
        ref={containerRef} 
        onMouseMove={handleMouseMove} 
        onMouseDown={handleChartMouseDown}
        onMouseUp={handleChartMouseUp}
        onContextMenu={handleContextMenu}
        onMouseLeave={() => { setMousePos(null); setHoveredIndex(null); setIsDrawing(false); setDrawStart(null); }}
        style={{ 
          cursor: getCursorStyle(),
          backgroundColor: chartSettings.backgroundColor,
          '--text-color': chartSettings.textColor,
          '--grid-color': chartSettings.gridColor,
          '--grid-opacity': chartSettings.gridOpacity,
          '--candle-up': chartSettings.candleUpColor,
          '--candle-down': chartSettings.candleDownColor
        }}
      >
        {/* Header */}
        <div className="fs-header" onClick={e => e.stopPropagation()}>
          <div className="fs-header-left">
            <button className="fs-back-btn" onClick={handleClose}><ArrowLeft size={18} /><span>Back</span></button>
            
            {/* Combined Search + Symbol Button */}
            <SymbolSelector symbol={symbol} companyName={companyName} onSymbolChange={handleSymbolChange} assetType={assetType} sectorFilter={sectorFilter} onAssetTypeChange={setAssetType} onSectorFilterChange={setSectorFilter} />
            
            {/* Multi-Chart Layout Button */}
            <div className="fs-layout-wrapper">
              <button 
                className={`fs-layout-btn ${chartLayout !== 'single' ? 'active' : ''}`}
                onClick={() => setShowLayoutMenu(!showLayoutMenu)}
                title="Chart Layout"
              >
                <Layers size={14} />
                <span>{chartLayout === 'single' ? 'Layout' : chartLayout === '2h' ? '2' : chartLayout === '3' ? '3' : '4'}</span>
                <ChevronDown size={12} className={showLayoutMenu ? 'rotated' : ''} />
              </button>
              
              {showLayoutMenu && (
                <div className="fs-layout-dropdown" onClick={(e) => e.stopPropagation()}>
                  <button 
                    className={chartLayout === 'single' ? 'active' : ''} 
                    onClick={() => { setChartLayout('single'); setShowLayoutMenu(false); }}
                  >
                    <Square size={14} /> Single Chart
                  </button>
                  <button 
                    className={chartLayout === '2h' ? 'active' : ''} 
                    onClick={() => { setChartLayout('2h'); setShowLayoutMenu(false); }}
                  >
                    <div className="layout-icon layout-2h"><div /><div /></div>
                    2 Charts
                  </button>
                  <button 
                    className={chartLayout === '3' ? 'active' : ''} 
                    onClick={() => { setChartLayout('3'); setShowLayoutMenu(false); }}
                  >
                    <div className="layout-icon layout-3"><div /><div /><div /></div>
                    3 Charts
                  </button>
                  <button 
                    className={chartLayout === '4grid' ? 'active' : ''} 
                    onClick={() => { setChartLayout('4grid'); setShowLayoutMenu(false); }}
                  >
                    <div className="layout-icon layout-4"><div /><div /><div /><div /></div>
                    4 Grid
                  </button>
                </div>
              )}
            </div>
            <div className="fs-indicators-ai-group">
              <button className="fs-indicators-header-btn" onClick={() => setShowIndicatorsModal(true)}>
                <Layers size={14} />
                <span>Indicators</span>
                {activeCount > 0 && <span className="fs-ind-badge">{activeCount}</span>}
              </button>
              <button 
                className={`fs-ai-header-btn ${showAIAnalysis ? 'active' : ''}`}
                onClick={() => setShowAIAnalysis(true)}
                title="AI Analysis"
              >
                <Sparkles size={14} />
                <span>AI Analysis</span>
              </button>
            </div>
            <div className="fs-alerts-dropdown-wrap">
              <button className="fs-alerts-header-btn" onClick={() => setShowAlertsDropdown(!showAlertsDropdown)}>
                <Bell size={14} />
                <span>Alerts</span>
                {priceAlerts.length > 0 && <span className="fs-ind-badge">{priceAlerts.length}</span>}
                <ChevronDown size={12} className={showAlertsDropdown ? 'rotated' : ''} />
              </button>
              {showAlertsDropdown && (
                <AlertsDropdown 
                  symbol={symbol}
                  currentPrice={current?.close}
                  priceAlerts={priceAlerts}
                  setPriceAlerts={setPriceAlerts}
                  onClose={() => setShowAlertsDropdown(false)}
                  formState={alertFormState}
                  setFormState={setAlertFormState}
                  onCreateAlert={createAlertAPI}
                  onDeleteAlert={deleteAlertAPI}
                  onToggleAlert={toggleAlertAPI}
                />
              )}
            </div>
            <div className="fs-header-divider" />
            {/* Revolutionary Features */}
            <div className="fs-features-group">
              <button className="fs-feature-btn" onClick={() => setShowSmartMoney(true)} title="Smart Money Flow">
                <Fish size={14} />
                <span>Smart Money</span>
              </button>
              <button className="fs-feature-btn" onClick={() => setShowTimeMachine(true)} title="Time Machine">
                <History size={14} />
                <span>Time Machine</span>
              </button>
              <button className="fs-feature-btn" onClick={() => setShowScenario(true)} title="Scenario Simulator">
                <Compass size={14} />
                <span>Scenarios</span>
              </button>
              <button 
                className={`fs-feature-btn ${showPatterns ? 'active' : ''}`} 
                onClick={() => setShowPatterns(!showPatterns)} 
                title="Pattern Detection"
              >
                <Zap size={14} />
                <span>Patterns</span>
                {patterns.length > 0 && <span className="fs-feature-badge">{patterns.length}</span>}
              </button>
            </div>
          </div>
          <div className="fs-header-center">
            {/* OHLC moved to chart overlay */}
          </div>
          <div className="fs-header-right">
            {/* Replay Mode Button */}
            <button 
              className={`fs-replay-toggle ${replayMode ? 'active' : ''}`} 
              onClick={() => replayMode ? stopReplay() : startReplay()} 
              title="Replay Mode - Step through historical data"
            >
              {replayMode ? <X size={14} /> : <Play size={14} />}
              <span>{replayMode ? 'Exit' : 'Replay'}</span>
            </button>
            <div className="fs-header-divider" />
            <TimeframeSelector timeframe={timeframe} onTimeframeChange={handleTimeframeChange} isPro={isPro} />
            <div className="fs-header-divider" />
            <button 
              className={chartType === 'candle' ? 'active' : ''} 
              onClick={() => {
                setChartType('candle');
                if (chartLayout !== 'single') {
                  setMultiChartTypes(['candle', 'candle', 'candle']);
                }
              }} 
              title={chartLayout !== 'single' ? "Candlestick (Sync All)" : "Candlestick"}
            >
              <BarChart2 size={16} />
            </button>
            <button 
              className={chartType === 'area' ? 'active' : ''} 
              onClick={() => {
                setChartType('area');
                if (chartLayout !== 'single') {
                  setMultiChartTypes(['area', 'area', 'area']);
                }
              }} 
              title={chartLayout !== 'single' ? "Area (Sync All)" : "Area"}
            >
              <Activity size={16} />
            </button>
            {isPro && <span className="fs-pro-badge"><Crown size={12} /> PRO</span>}
          </div>
        </div>

        {/* Replay Controls Bar - Shows when replay mode is active */}
        {replayMode && (
          <div className="fs-replay-bar" onClick={e => e.stopPropagation()}>
            <div className="replay-info">
              <Play size={14} />
              <span className="replay-label">REPLAY MODE</span>
              <span className="replay-date">
                {displayData?.[replayIndex]?.date ? formatDate(displayData[replayIndex].date, timeframe) : '--'}
              </span>
              <span className="replay-progress">
                {replayIndex + 1} / {data?.length || 0}
              </span>
            </div>
            
            <div className="replay-controls">
              <button onClick={replayGoToStart} title="Go to Start (Home)">
                <SkipBack size={16} />
              </button>
              <button onClick={replayStepBackward} title="Step Back (←)">
                <ChevronLeft size={18} />
              </button>
              <button 
                className={`replay-play-btn ${replayPlaying ? 'playing' : ''}`}
                onClick={toggleReplayPlay} 
                title={replayPlaying ? 'Pause (Space)' : 'Play (Space)'}
              >
                {replayPlaying ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <button onClick={replayStepForward} title="Step Forward (→)">
                <ChevronRight size={18} />
              </button>
              <button onClick={replayGoToEnd} title="Go to End (End)">
                <SkipForward size={16} />
              </button>
            </div>
            
            <div className="replay-speed">
              <span className="speed-label">Speed:</span>
              <select 
                value={replaySpeed} 
                onChange={(e) => setReplaySpeed(Number(e.target.value))}
              >
                <option value={1000}>0.5x</option>
                <option value={500}>1x</option>
                <option value={250}>2x</option>
                <option value={100}>5x</option>
                <option value={50}>10x</option>
              </select>
            </div>
            
            <div className="replay-slider">
              <input 
                type="range" 
                min={0} 
                max={(data?.length || 1) - 1} 
                value={replayIndex}
                onChange={(e) => {
                  setReplayIndex(Number(e.target.value));
                  setReplayPlaying(false);
                }}
              />
            </div>
            
            <button className="replay-exit-btn" onClick={stopReplay}>
              <X size={14} />
              Exit
            </button>
          </div>
        )}

        {/* Multi-Chart Grid Layout */}
        {chartLayout !== 'single' && (
          <>
            {/* Full Grid View */}
            <div className={`chart-grid layout-${chartLayout}`}>
              {/* Chart 1 - Current Symbol (Primary) */}
              <div className="grid-chart-cell">
                <SplitChartView 
                  symbol={symbol} 
                  timeframe={timeframe}
                  chartType={chartType}
                  chartSettings={chartSettings}
                  onFullscreen={() => setChartLayout('single')}
                  onRemove={null}
                  onSymbolChange={(newSym) => navigate(`/chart/${newSym}`)}
                  onTimeframeChange={handleTimeframeChange}
                  onChartTypeChange={setChartType}
                  hoveredDate={gridHoveredDate}
                  onHover={setGridHoveredDate}
                />
              </div>
              
              {/* Chart 2 */}
              <div className="grid-chart-cell">
                <SplitChartView 
                  symbol={multiChartSymbols[0]} 
                  timeframe={multiChartTimeframes[0]}
                  chartType={multiChartTypes[0]}
                  chartSettings={chartSettings}
                  onFullscreen={() => navigate(`/chart/${multiChartSymbols[0]}`)}
                  onRemove={() => {
                    if (chartLayout === '4grid') {
                      setChartLayout('3');
                      setMultiChartSymbols(prev => [prev[1], prev[2], 'MSFT']);
                      setMultiChartTimeframes(prev => [prev[1], prev[2], '3M']);
                      setMultiChartTypes(prev => [prev[1], prev[2], 'candle']);
                    } else if (chartLayout === '3') {
                      setChartLayout('2h');
                      setMultiChartSymbols(prev => [prev[1], prev[0], prev[2]]);
                      setMultiChartTimeframes(prev => [prev[1], prev[0], prev[2]]);
                      setMultiChartTypes(prev => [prev[1], prev[0], prev[2]]);
                    } else {
                      setChartLayout('single');
                    }
                  }}
                  onSymbolChange={(newSym) => setMultiChartSymbols(prev => [newSym, prev[1], prev[2]])}
                  onTimeframeChange={(newTf) => setMultiChartTimeframes(prev => [newTf, prev[1], prev[2]])}
                  onChartTypeChange={(newType) => setMultiChartTypes(prev => [newType, prev[1], prev[2]])}
                  hoveredDate={gridHoveredDate}
                  onHover={setGridHoveredDate}
                />
              </div>
              
              {/* Chart 3 - only in 3 or 4grid */}
              {(chartLayout === '3' || chartLayout === '4grid') && (
                <div className="grid-chart-cell">
                  <SplitChartView 
                    symbol={multiChartSymbols[1]} 
                    timeframe={multiChartTimeframes[1]}
                    chartType={multiChartTypes[1]}
                    chartSettings={chartSettings}
                    onFullscreen={() => navigate(`/chart/${multiChartSymbols[1]}`)}
                    onRemove={() => {
                      if (chartLayout === '4grid') {
                        setChartLayout('3');
                        setMultiChartSymbols(prev => [prev[0], prev[2], 'GOOGL']);
                        setMultiChartTimeframes(prev => [prev[0], prev[2], '3M']);
                        setMultiChartTypes(prev => [prev[0], prev[2], 'candle']);
                      } else {
                        setChartLayout('2h');
                      }
                    }}
                    onSymbolChange={(newSym) => setMultiChartSymbols(prev => [prev[0], newSym, prev[2]])}
                    onTimeframeChange={(newTf) => setMultiChartTimeframes(prev => [prev[0], newTf, prev[2]])}
                    onChartTypeChange={(newType) => setMultiChartTypes(prev => [prev[0], newType, prev[2]])}
                    hoveredDate={gridHoveredDate}
                    onHover={setGridHoveredDate}
                  />
                </div>
              )}
              
              {/* Chart 4 - only in 4grid */}
              {chartLayout === '4grid' && (
                <div className="grid-chart-cell">
                  <SplitChartView 
                    symbol={multiChartSymbols[2]} 
                    timeframe={multiChartTimeframes[2]}
                    chartType={multiChartTypes[2]}
                    chartSettings={chartSettings}
                    onFullscreen={() => navigate(`/chart/${multiChartSymbols[2]}`)}
                    onRemove={() => setChartLayout('3')}
                    onSymbolChange={(newSym) => setMultiChartSymbols(prev => [prev[0], prev[1], newSym])}
                    onTimeframeChange={(newTf) => setMultiChartTimeframes(prev => [prev[0], prev[1], newTf])}
                    onChartTypeChange={(newType) => setMultiChartTypes(prev => [prev[0], prev[1], newType])}
                    hoveredDate={gridHoveredDate}
                    onHover={setGridHoveredDate}
                  />
                </div>
              )}
            </div>
            
            {/* Grid Controls */}
            <div className="grid-controls">
              {/* Add Chart Button - only show if not at max */}
              {chartLayout !== '4grid' && (
                <button 
                  className="grid-add-btn"
                  onClick={() => {
                    if (chartLayout === '2h') setChartLayout('3');
                    else if (chartLayout === '3') setChartLayout('4grid');
                  }}
                  title="Add Chart"
                >
                  <Plus size={14} />
                  <span>Add Chart</span>
                </button>
              )}
              <button 
                className="grid-exit-btn"
                onClick={() => setChartLayout('single')}
                title="Exit Grid View"
              >
                <X size={14} />
                <span>Exit Grid</span>
              </button>
            </div>
          </>
        )}

        {/* Left Toolbar - only show in single mode */}
        {chartLayout === 'single' && (
        <div className="fs-toolbar" onClick={e => e.stopPropagation()}>
          {/* Cursor Tools */}
          <div className="fs-toolbar-group">
            <button 
              className={selectedTool === 'crosshair' ? 'active' : ''} 
              onClick={() => setSelectedTool('crosshair')}
              title="Crosshair (C)"
            >
              <Crosshair size={18} />
            </button>
            <button 
              className={selectedTool === 'pointer' ? 'active' : ''} 
              onClick={() => setSelectedTool('pointer')}
              title="Pointer (V)"
            >
              <MousePointer size={18} />
            </button>
          </div>

          <div className="fs-toolbar-divider" />

          {/* Line Tools */}
          <div className="fs-toolbar-group">
            <button 
              className={`${['trendline', 'vline', 'ray', 'channel'].includes(selectedTool) ? 'active' : ''} ${showDrawingMenu === 'line' ? 'menu-open' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowDrawingMenu(showDrawingMenu === 'line' ? null : 'line');
              }}
              title="Line Tools (T)"
            >
              {selectedTool === 'vline' ? <Minus size={18} style={{ transform: 'rotate(90deg)' }} /> :
               selectedTool === 'ray' ? <ArrowUpRight size={18} /> :
               selectedTool === 'channel' ? <GitBranch size={18} /> :
               <TrendingUp size={18} />}
            </button>
            {showDrawingMenu === 'line' && (
              <div className="fs-toolbar-menu" style={{ top: '90px' }} onClick={(e) => e.stopPropagation()}>
                <button className={selectedTool === 'trendline' ? 'active' : ''} onClick={() => { setSelectedTool('trendline'); setShowDrawingMenu(null); }}>
                  <TrendingUp size={14} /> Trend Line
                </button>
                <button className={selectedTool === 'vline' ? 'active' : ''} onClick={() => { setSelectedTool('vline'); setShowDrawingMenu(null); }}>
                  <Minus size={14} style={{ transform: 'rotate(90deg)' }} /> Vertical Line
                </button>
                <button className={selectedTool === 'ray' ? 'active' : ''} onClick={() => { setSelectedTool('ray'); setShowDrawingMenu(null); }}>
                  <ArrowUpRight size={14} /> Ray
                </button>
                <button className={selectedTool === 'channel' ? 'active' : ''} onClick={() => { setSelectedTool('channel'); setShowDrawingMenu(null); }}>
                  <GitBranch size={14} /> Parallel Channel
                </button>
              </div>
            )}
            
            <button 
              className={selectedTool === 'hline' ? 'active' : ''} 
              onClick={() => setSelectedTool('hline')}
              title="Horizontal Line (H)"
            >
              <Minus size={18} />
            </button>
          </div>

          <div className="fs-toolbar-divider" />

          {/* Fibonacci & Measure Tools */}
          <div className="fs-toolbar-group">
            <button 
              className={`${['fibretracement', 'fibextension', 'fibfan', 'fibtimezone'].includes(selectedTool) ? 'active' : ''} ${showDrawingMenu === 'fib' ? 'menu-open' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowDrawingMenu(showDrawingMenu === 'fib' ? null : 'fib');
              }}
              title="Fibonacci Tools"
            >
              <Hash size={18} />
            </button>
            {showDrawingMenu === 'fib' && (
              <div className="fs-toolbar-menu" style={{ top: '220px' }} onClick={(e) => e.stopPropagation()}>
                <button className={selectedTool === 'fibretracement' ? 'active' : ''} onClick={() => { setSelectedTool('fibretracement'); setShowDrawingMenu(null); }}>
                  <Hash size={14} /> Fib Retracement
                </button>
                <button className={selectedTool === 'fibextension' ? 'active' : ''} onClick={() => { setSelectedTool('fibextension'); setShowDrawingMenu(null); }}>
                  <Hash size={14} /> Fib Extension
                </button>
              </div>
            )}
            
            <button 
              className={selectedTool === 'measure' ? 'active' : ''} 
              onClick={() => setSelectedTool('measure')}
              title="Measure Tool (M)"
            >
              <Ruler size={18} />
            </button>
          </div>

          <div className="fs-toolbar-divider" />

          {/* Shape & Text Tools */}
          <div className="fs-toolbar-group">
            <button 
              className={`${['rectangle', 'ellipse', 'triangle', 'arrow'].includes(selectedTool) ? 'active' : ''} ${showDrawingMenu === 'shape' ? 'menu-open' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowDrawingMenu(showDrawingMenu === 'shape' ? null : 'shape');
              }}
              title="Shapes"
            >
              {selectedTool === 'ellipse' ? <Circle size={18} /> :
               selectedTool === 'triangle' ? <Triangle size={18} /> :
               selectedTool === 'arrow' ? <ArrowUpRight size={18} /> :
               <Square size={18} />}
            </button>
            {showDrawingMenu === 'shape' && (
              <div className="fs-toolbar-menu" style={{ top: '310px' }} onClick={(e) => e.stopPropagation()}>
                <button className={selectedTool === 'rectangle' ? 'active' : ''} onClick={() => { setSelectedTool('rectangle'); setShowDrawingMenu(null); }}>
                  <Square size={14} /> Rectangle
                </button>
                <button className={selectedTool === 'ellipse' ? 'active' : ''} onClick={() => { setSelectedTool('ellipse'); setShowDrawingMenu(null); }}>
                  <Circle size={14} /> Ellipse
                </button>
                <button className={selectedTool === 'triangle' ? 'active' : ''} onClick={() => { setSelectedTool('triangle'); setShowDrawingMenu(null); }}>
                  <Triangle size={14} /> Triangle
                </button>
                <button className={selectedTool === 'arrow' ? 'active' : ''} onClick={() => { setSelectedTool('arrow'); setShowDrawingMenu(null); }}>
                  <ArrowUpRight size={14} /> Arrow
                </button>
              </div>
            )}
            
            <button 
              className={selectedTool === 'text' ? 'active' : ''} 
              onClick={() => setSelectedTool('text')}
              title="Text"
            >
              <Type size={18} />
            </button>
            
            <button 
              className={selectedTool === 'pencil' ? 'active' : ''} 
              onClick={() => setSelectedTool('pencil')}
              title="Freehand Draw"
            >
              <Pencil size={18} />
            </button>
            
            <button 
              className={selectedTool === 'eraser' ? 'active' : ''} 
              onClick={() => setSelectedTool('eraser')}
              title="Eraser - Click on drawings to delete"
            >
              <Eraser size={18} />
            </button>
          </div>

          <div className="fs-toolbar-divider" />

          {/* Zoom Controls */}
          <div className="fs-toolbar-group">
            <button 
              onClick={() => setZoomLevel(prev => Math.min(prev * 1.2, 5))}
              title="Zoom In (+)"
            >
              <ZoomIn size={18} />
            </button>
            <button 
              onClick={() => setZoomLevel(prev => Math.max(prev / 1.2, 0.5))}
              title="Zoom Out (-)"
            >
              <ZoomOut size={18} />
            </button>
            <button 
              onClick={() => { setZoomLevel(1); setPanOffset(0); }}
              title="Reset View (R)"
            >
              <RotateCcw size={18} />
            </button>
          </div>

          <div className="fs-toolbar-divider" />

          {/* Drawing Management */}
          <div className="fs-toolbar-group">
            <button 
              onClick={undoDrawing}
              title="Undo (Ctrl+Z)"
              className={historyIndex <= 0 && drawings.length === 0 ? 'disabled' : ''}
            >
              <RotateCcw size={18} />
            </button>
            <button 
              onClick={redoDrawing}
              title="Redo (Ctrl+Y)"
              className={historyIndex >= drawingHistory.length - 1 ? 'disabled' : ''}
            >
              <RotateCcw size={18} style={{ transform: 'scaleX(-1)' }} />
            </button>
            <button 
              onClick={() => setDrawingsWithHistory([])}
              title="Clear All Drawings"
              className={drawings.length === 0 ? 'disabled' : ''}
            >
              <Trash2 size={18} />
            </button>
            <button 
              onClick={() => {/* Toggle drawings visibility */}}
              title="Toggle Drawings"
            >
              <Eye size={18} />
            </button>
          </div>
          
          {/* Drawing Style */}
          <div className="fs-toolbar-group">
            {/* Color Picker */}
            <div className="fs-color-picker-wrapper">
              <button 
                onClick={() => setShowColorPicker(!showColorPicker)}
                title="Drawing Color"
                className={showColorPicker ? 'active' : ''}
              >
                <div 
                  className="fs-color-swatch" 
                  style={{ backgroundColor: drawingColor }}
                />
              </button>
              {showColorPicker && (
                <div className="fs-color-picker-dropdown">
                  <div className="fs-color-picker-colors">
                    {drawingColors.map(color => (
                      <button
                        key={color}
                        className={`fs-color-option ${drawingColor === color ? 'active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          setDrawingColor(color);
                          setShowColorPicker(false);
                        }}
                      />
                    ))}
                  </div>
                  <div className="fs-stroke-width-section">
                    <span className="fs-stroke-label">Thickness</span>
                    <div className="fs-stroke-options">
                      {strokeWidths.map(width => (
                        <button
                          key={width}
                          className={`fs-stroke-option ${drawingStrokeWidth === width ? 'active' : ''}`}
                          onClick={() => setDrawingStrokeWidth(width)}
                        >
                          <div 
                            className="fs-stroke-preview" 
                            style={{ height: width, backgroundColor: drawingColor }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Spacer */}
          <div className="fs-toolbar-spacer" />

          {/* Export & Settings at bottom */}
          <div className="fs-toolbar-group">
            <div className="fs-toolbar-export-wrap" style={{ position: 'relative' }}>
              <button 
                title="Export" 
                className={showExportMenu ? 'active' : ''}
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                <Download size={18} />
              </button>
              {showExportMenu && (
                <div className="fs-toolbar-export-menu" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { takeScreenshot(); setShowExportMenu(false); }}>
                    <Camera size={14} /> Screenshot
                  </button>
                  <button onClick={() => { exportToCSV(); setShowExportMenu(false); }}>
                    <FileText size={14} /> CSV
                  </button>
                  <button onClick={() => { exportToJSON(); setShowExportMenu(false); }}>
                    <Database size={14} /> JSON
                  </button>
                </div>
              )}
            </div>
            <button 
              title="Chart Settings" 
              className={showSettings ? 'active' : ''}
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings size={18} />
            </button>
          </div>
        </div>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <div className="fs-settings-panel">
            <div className="fs-settings-header">
              <h3>Chart Settings</h3>
              <button className="fs-settings-close" onClick={() => setShowSettings(false)}>
                <X size={16} />
              </button>
            </div>
            
            <div className="fs-settings-content">
              {/* Appearance Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Layers size={14} />
                  <span>Appearance</span>
                </div>
                
                {/* Theme Toggle */}
                <div className="fs-settings-row">
                  <span className="fs-settings-label">Light Mode</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.theme === 'light'}
                      onChange={(e) => applyTheme(e.target.checked ? 'light' : 'dark')}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Up Candle</span>
                  <div className="fs-color-picker-row">
                    <input 
                      type="color" 
                      value={chartSettings.candleUpColor}
                      onChange={(e) => setChartSettings(prev => ({...prev, candleUpColor: e.target.value}))}
                      className="fs-color-input"
                    />
                    <span className="fs-color-value">{chartSettings.candleUpColor}</span>
                  </div>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Down Candle</span>
                  <div className="fs-color-picker-row">
                    <input 
                      type="color" 
                      value={chartSettings.candleDownColor}
                      onChange={(e) => setChartSettings(prev => ({...prev, candleDownColor: e.target.value}))}
                      className="fs-color-input"
                    />
                    <span className="fs-color-value">{chartSettings.candleDownColor}</span>
                  </div>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Grid Lines</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.gridLines}
                      onChange={(e) => setChartSettings(prev => ({...prev, gridLines: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Grid Opacity</span>
                  <div className="fs-slider-row">
                    <input 
                      type="range" 
                      min="0.01" 
                      max="0.2" 
                      step="0.01"
                      value={chartSettings.gridOpacity}
                      onChange={(e) => setChartSettings(prev => ({...prev, gridOpacity: parseFloat(e.target.value)}))}
                      className="fs-slider"
                    />
                    <span className="fs-slider-value">{Math.round(chartSettings.gridOpacity * 100)}%</span>
                  </div>
                </div>
              </div>

              {/* Chart Behavior Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Activity size={14} />
                  <span>Chart Behavior</span>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Auto Scale</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.autoScale}
                      onChange={(e) => setChartSettings(prev => ({...prev, autoScale: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Log Scale</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.logScale}
                      onChange={(e) => setChartSettings(prev => ({...prev, logScale: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Show Volume</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.showVolume}
                      onChange={(e) => setChartSettings(prev => ({...prev, showVolume: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Crosshair</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.showCrosshair}
                      onChange={(e) => setChartSettings(prev => ({...prev, showCrosshair: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Snap to Candle</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.snapToCandle}
                      onChange={(e) => setChartSettings(prev => ({...prev, snapToCandle: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Data & Trading Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Database size={14} />
                  <span>Data & Trading</span>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Extended Hours</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.extendedHours}
                      onChange={(e) => setChartSettings(prev => ({...prev, extendedHours: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Adjust for Dividends</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.adjustForDividends}
                      onChange={(e) => setChartSettings(prev => ({...prev, adjustForDividends: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Show Positions</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.showPositions}
                      onChange={(e) => setChartSettings(prev => ({...prev, showPositions: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Show Orders</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.showOrders}
                      onChange={(e) => setChartSettings(prev => ({...prev, showOrders: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Timezone</span>
                  <select 
                    value={chartSettings.timezone}
                    onChange={(e) => setChartSettings(prev => ({...prev, timezone: e.target.value}))}
                    className="fs-settings-select"
                  >
                    <option value="local">Local Time</option>
                    <option value="exchange">Exchange Time</option>
                    <option value="utc">UTC</option>
                    <option value="est">EST (New York)</option>
                    <option value="pst">PST (Los Angeles)</option>
                    <option value="gmt">GMT (London)</option>
                  </select>
                </div>
              </div>

              {/* Alerts Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Bell size={14} />
                  <span>Notifications</span>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Alert Sound</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.alertSound}
                      onChange={(e) => setChartSettings(prev => ({...prev, alertSound: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Alert Popup</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.alertPopup}
                      onChange={(e) => setChartSettings(prev => ({...prev, alertPopup: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Performance Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Cpu size={14} />
                  <span>Performance</span>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Max Bars</span>
                  <select 
                    value={chartSettings.maxBars}
                    onChange={(e) => setChartSettings(prev => ({...prev, maxBars: parseInt(e.target.value)}))}
                    className="fs-settings-select"
                  >
                    <option value="100">100 (Fastest)</option>
                    <option value="250">250</option>
                    <option value="500">500 (Default)</option>
                    <option value="1000">1000</option>
                    <option value="2000">2000 (Slowest)</option>
                  </select>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Animations</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.enableAnimations}
                      onChange={(e) => setChartSettings(prev => ({...prev, enableAnimations: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>

                <div className="fs-settings-row">
                  <span className="fs-settings-label">Hotkeys</span>
                  <label className="fs-toggle">
                    <input 
                      type="checkbox" 
                      checked={chartSettings.enableHotkeys}
                      onChange={(e) => setChartSettings(prev => ({...prev, enableHotkeys: e.target.checked}))}
                    />
                    <span className="fs-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Hotkeys Reference */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Keyboard size={14} />
                  <span>Keyboard Shortcuts</span>
                </div>
                <div className="fs-hotkeys-grid">
                  <div className="fs-hotkey"><kbd>A</kbd><span>AI Analysis</span></div>
                  <div className="fs-hotkey"><kbd>I</kbd><span>Indicators</span></div>
                  <div className="fs-hotkey"><kbd>D</kbd><span>Drawing Mode</span></div>
                  <div className="fs-hotkey"><kbd>C</kbd><span>Crosshair</span></div>
                  <div className="fs-hotkey"><kbd>R</kbd><span>Reset Zoom</span></div>
                  <div className="fs-hotkey"><kbd>F</kbd><span>Fullscreen</span></div>
                  <div className="fs-hotkey"><kbd>S</kbd><span>Screenshot</span></div>
                  <div className="fs-hotkey"><kbd>Esc</kbd><span>Close Panel</span></div>
                </div>
              </div>
              
              {/* Help Section */}
              <div className="fs-settings-section">
                <div className="fs-settings-section-title">
                  <Info size={14} />
                  <span>Help</span>
                </div>
                <button 
                  className="fs-restart-tour-btn"
                  onClick={() => {
                    setShowSettings(false);
                    setOnboardingStep(0);
                    setShowOnboarding(true);
                  }}
                >
                  <Sparkles size={14} />
                  Restart Tour
                </button>
              </div>

              {/* Save & Reset */}
              <div className="fs-settings-actions">
                <button 
                  className="fs-settings-btn primary"
                  onClick={(e) => {
                    localStorage.setItem('elemental-chart-settings', JSON.stringify(chartSettings));
                    const btn = e.currentTarget;
                    btn.classList.add('saved');
                    const originalContent = btn.innerHTML;
                    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Saved!';
                    setTimeout(() => {
                      btn.classList.remove('saved');
                      btn.innerHTML = originalContent;
                    }, 2000);
                  }}
                >
                  <Check size={14} />
                  Save Settings
                </button>
                <button 
                  className="fs-settings-btn secondary"
                  onClick={() => {
                    if (window.confirm('Reset all settings to defaults?')) {
                      setChartSettings({
                        theme: 'dark',
                        candleUpColor: '#26a69a',
                        candleDownColor: '#ef5350',
                        backgroundColor: '#0a0a0f',
                        gridColor: '#ffffff',
                        gridOpacity: 0.04,
                        textColor: '#ffffff',
                        gridLines: true,
                        autoScale: true,
                        logScale: false,
                        showVolume: false,
                        showCrosshair: true,
                        snapToCandle: true,
                        priceScalePosition: 'right',
                        showPriceLabels: true,
                        showLastPrice: true,
                        showTimeLabels: true,
                        timezone: 'local',
                        use24Hour: true,
                        showPositions: true,
                        showOrders: true,
                        orderLineColor: '#f59e0b',
                        maxBars: 500,
                        enableAnimations: true,
                        alertSound: true,
                        alertPopup: true,
                        extendedHours: false,
                        adjustForDividends: true,
                        enableHotkeys: true
                      });
                      localStorage.removeItem('elemental-chart-settings');
                    }
                  }}
                >
                  <RotateCcw size={14} />
                  Reset Defaults
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Alert Notification Popup */}
        {showAlertNotification && (
          <div className="fs-alert-notification">
            <div className="fs-alert-notif-icon">
              <Bell size={20} />
            </div>
            <div className="fs-alert-notif-content">
              <div className="fs-alert-notif-title">
                <span>Alert Triggered</span>
                <span className="fs-alert-notif-symbol">{showAlertNotification.symbol}</span>
              </div>
              <div className="fs-alert-notif-message">{showAlertNotification.name}</div>
              <div className="fs-alert-notif-price">
                Current: <strong>${showAlertNotification.price?.toFixed(2)}</strong>
                <span className="fs-alert-notif-target">
                  Target: ${showAlertNotification.alertPrice?.toFixed(2)}
                </span>
              </div>
            </div>
            <button 
              className="fs-alert-notif-close"
              onClick={() => setShowAlertNotification(null)}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Main Chart SVG - Only show in single mode */}
        {chartLayout === 'single' && (
        <svg 
          ref={chartRef} 
          width={dimensions.width} 
          height={svgHeight} 
          className="fs-svg" 
          style={{marginTop: HEADER_HEIGHT, pointerEvents: 'all', cursor: selectedTool === 'pencil' ? 'crosshair' : 'crosshair', touchAction: 'none'}}
          onContextMenu={handleContextMenu}
          onMouseDown={handleChartMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleChartMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          {/* Transparent background to catch mouse events */}
          <rect x={0} y={0} width={dimensions.width} height={svgHeight} fill="transparent" />
          
          {/* RSI Sub-Chart - At TOP of SVG */}
          {showRSI && rsiData.length > 0 && (
            <g className="rsi-subchart">
              <rect x={chartLeft} y={0} width={chartWidth} height={RSI_HEIGHT} fill="rgba(0,0,0,0.2)" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={RSI_HEIGHT} y2={RSI_HEIGHT} stroke="rgba(255,255,255,0.1)" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={RSI_HEIGHT * 0.3} y2={RSI_HEIGHT * 0.3} stroke="rgba(239,83,80,0.3)" strokeDasharray="3,3" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={RSI_HEIGHT * 0.5} y2={RSI_HEIGHT * 0.5} stroke="rgba(255,255,255,0.1)" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={RSI_HEIGHT * 0.7} y2={RSI_HEIGHT * 0.7} stroke="rgba(38,166,154,0.3)" strokeDasharray="3,3" />
              <text x={chartLeft + 5} y={12} fill="rgba(255,255,255,0.5)" fontSize="9" fontWeight="600">RSI (14)</text>
              <text x={chartLeft + chartWidth + 8} y={RSI_HEIGHT * 0.3 + 3} fill="rgba(255,255,255,0.4)" fontSize="9">70</text>
              <text x={chartLeft + chartWidth + 8} y={RSI_HEIGHT * 0.5 + 3} fill="rgba(255,255,255,0.4)" fontSize="9">50</text>
              <text x={chartLeft + chartWidth + 8} y={RSI_HEIGHT * 0.7 + 3} fill="rgba(255,255,255,0.4)" fontSize="9">30</text>
              <path 
                d={rsiData.reduce((path, val, i) => {
                  if (val === null) return path;
                  const x = getX(i) + candleWidth / 2;
                  const y = RSI_HEIGHT - (val / 100) * RSI_HEIGHT;
                  return path ? `${path} L${x},${y}` : `M${x},${y}`;
                }, '')} 
                fill="none" 
                stroke="#8b5cf6" 
                strokeWidth={1.5} 
              />
              {rsiData[rsiData.length - 1] !== null && (
                <text x={chartLeft + chartWidth + 8} y={RSI_HEIGHT - (rsiData[rsiData.length - 1] / 100) * RSI_HEIGHT + 3} fill="#8b5cf6" fontSize="10" fontWeight="600">
                  {rsiData[rsiData.length - 1].toFixed(1)}
                </text>
              )}
            </g>
          )}

          {/* MACD Sub-Chart - Below RSI (or at top if no RSI) */}
          {showMACD && macdData.macdLine.length > 0 && (
            <g className="macd-subchart" transform={`translate(0, ${RSI_HEIGHT})`}>
              <rect x={chartLeft} y={0} width={chartWidth} height={MACD_HEIGHT} fill="rgba(0,0,0,0.2)" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={MACD_HEIGHT} y2={MACD_HEIGHT} stroke="rgba(255,255,255,0.1)" />
              <line x1={chartLeft} x2={chartLeft + chartWidth} y1={MACD_HEIGHT / 2} y2={MACD_HEIGHT / 2} stroke="rgba(255,255,255,0.2)" />
              <text x={chartLeft + 5} y={12} fill="rgba(255,255,255,0.5)" fontSize="9" fontWeight="600">MACD (12,26,9)</text>
              {macdData.histogram.map((val, i) => {
                if (val === null) return null;
                const x = getX(i);
                const maxHist = Math.max(...macdData.histogram.filter(v => v !== null).map(Math.abs)) || 1;
                const height = Math.abs(val) / maxHist * (MACD_HEIGHT / 2 - 5);
                const y = val >= 0 ? MACD_HEIGHT / 2 - height : MACD_HEIGHT / 2;
                return (
                  <rect 
                    key={i}
                    x={x} 
                    y={y} 
                    width={candleWidth} 
                    height={height}
                    fill={val >= 0 ? chartSettings.candleUpColor : chartSettings.candleDownColor}
                    opacity={0.6}
                  />
                );
              })}
              <path 
                d={macdData.macdLine.reduce((path, val, i) => {
                  if (val === null) return path;
                  const x = getX(i) + candleWidth / 2;
                  const maxVal = Math.max(...macdData.macdLine.filter(v => v !== null).map(Math.abs)) || 1;
                  const y = MACD_HEIGHT / 2 - (val / maxVal) * (MACD_HEIGHT / 2 - 5);
                  return path ? `${path} L${x},${y}` : `M${x},${y}`;
                }, '')} 
                fill="none" 
                stroke="#3b82f6" 
                strokeWidth={1.5} 
              />
              <path 
                d={macdData.signalLine.reduce((path, val, i) => {
                  if (val === null) return path;
                  const x = getX(i) + candleWidth / 2;
                  const maxVal = Math.max(...macdData.macdLine.filter(v => v !== null).map(Math.abs)) || 1;
                  const y = MACD_HEIGHT / 2 - (val / maxVal) * (MACD_HEIGHT / 2 - 5);
                  return path ? `${path} L${x},${y}` : `M${x},${y}`;
                }, '')} 
                fill="none" 
                stroke="#f59e0b" 
                strokeWidth={1} 
                strokeDasharray="3,3"
              />
            </g>
          )}
          
          {/* Grid lines - controlled by settings */}
          {chartSettings.gridLines && [...Array(8)].map((_, i) => { const y = candleYOffset + (mainChartHeight / 7) * i; const price = maxPrice - (priceRange / 7) * i; const gridRgb = chartSettings.theme === 'light' ? '0,0,0' : '255,255,255'; return (<g key={i}><line x1={chartLeft} x2={chartLeft + chartWidth} y1={y} y2={y} stroke={`rgba(${gridRgb},${chartSettings.gridOpacity})`} />{chartSettings.showPriceLabels && <text x={chartLeft + chartWidth + 8} y={y + 4} fill={chartSettings.theme === 'light' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.4)'} fontSize="10">{formatPrice(price)}</text>}</g>); })}

          {indicators.bollinger && bollinger.length > 0 && (<g opacity={0.4}><path d={bollinger.reduce((p, b, i) => b.upper ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(b.upper) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(b.upper) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#ec4899" strokeWidth={1} /><path d={bollinger.reduce((p, b, i) => b.middle ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(b.middle) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(b.middle) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#ec4899" strokeWidth={1} strokeDasharray="3,3" /><path d={bollinger.reduce((p, b, i) => b.lower ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(b.lower) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(b.lower) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#ec4899" strokeWidth={1} /></g>)}
          {indicators.sma20 && <path d={sma20.reduce((p, v, i) => v ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#f59e0b" strokeWidth={1} />}
          {indicators.sma50 && <path d={sma50.reduce((p, v, i) => v ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#3b82f6" strokeWidth={1} />}
          {indicators.ema20 && <path d={ema20.reduce((p, v, i) => v ? (p ? `${p} L${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}` : `M${getX(i) + candleWidth/2},${getY(v) - HEADER_HEIGHT}`) : p, '')} fill="none" stroke="#8b5cf6" strokeWidth={1} />}

          {chartType === 'candle' && !compareSymbol ? chartDataSource.map((d, i) => {
            if (!d || d.high == null || d.low == null || d.open == null || d.close == null) return null;
            const up = d.close >= d.open, color = up ? chartSettings.candleUpColor : chartSettings.candleDownColor, x = getX(i);
            const bodyTop = getY(Math.max(d.open, d.close)) - HEADER_HEIGHT, bodyBottom = getY(Math.min(d.open, d.close)) - HEADER_HEIGHT;
            return (<g key={i} opacity={hoveredIndex === i ? 1 : 0.85}><line x1={x + candleWidth/2} x2={x + candleWidth/2} y1={getY(d.high) - HEADER_HEIGHT} y2={getY(d.low) - HEADER_HEIGHT} stroke={color} /><rect x={x} y={bodyTop} width={candleWidth} height={Math.max(1, bodyBottom - bodyTop)} fill={color} /></g>);
          }) : (
            <>
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#667eea" stopOpacity="0.3"/>
                  <stop offset="100%" stopColor="#667eea" stopOpacity="0"/>
                </linearGradient>
                {compareSymbol && (
                  <linearGradient id="compareFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.2"/>
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity="0"/>
                  </linearGradient>
                )}
              </defs>
              
              {/* Main symbol line */}
              {compareSymbol ? (
                <>
                  {/* Percentage-based comparison mode */}
                  <path 
                    d={`M${getX(0) + candleWidth/2},${volumeTop/2 - HEADER_HEIGHT} ${data.map((d,i) => {
                      const pctChange = ((d.close - data[0].close) / data[0].close) * 100;
                      const y = volumeTop/2 - HEADER_HEIGHT - (pctChange * 3);
                      return `L${getX(i) + candleWidth/2},${y}`;
                    }).join(' ')}`} 
                    fill="none" stroke="#667eea" strokeWidth={2} 
                  />
                  {/* Compare symbol line */}
                  {compareData && compareData.length > 0 && (
                    <path 
                      d={`M${getX(0) + candleWidth/2},${volumeTop/2 - HEADER_HEIGHT} ${compareData.slice(0, data.length).map((d,i) => {
                        const pctChange = ((d.close - compareData[0].close) / compareData[0].close) * 100;
                        const y = volumeTop/2 - HEADER_HEIGHT - (pctChange * 3);
                        return `L${getX(i) + candleWidth/2},${y}`;
                      }).join(' ')}`} 
                      fill="none" stroke="#f59e0b" strokeWidth={2} 
                    />
                  )}
                  {/* Zero line */}
                  <line x1={chartLeft} x2={chartLeft + chartWidth} y1={volumeTop/2 - HEADER_HEIGHT} y2={volumeTop/2 - HEADER_HEIGHT} stroke="rgba(255,255,255,0.15)" strokeDasharray="4,4" />
                  
                  {/* Hover dots on lines */}
                  {hoveredIndex !== null && (
                    <>
                      {/* Main symbol dot */}
                      <circle 
                        cx={getX(hoveredIndex) + candleWidth/2} 
                        cy={volumeTop/2 - HEADER_HEIGHT - (((data[hoveredIndex].close - data[0].close) / data[0].close) * 100 * 3)}
                        r={5} fill="#667eea" stroke="#fff" strokeWidth={2}
                      />
                      {/* Compare symbol dot */}
                      {compareData && compareData[hoveredIndex] && (
                        <circle 
                          cx={getX(hoveredIndex) + candleWidth/2} 
                          cy={volumeTop/2 - HEADER_HEIGHT - (((compareData[hoveredIndex].close - compareData[0].close) / compareData[0].close) * 100 * 3)}
                          r={5} fill="#f59e0b" stroke="#fff" strokeWidth={2}
                        />
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <path d={`M${getX(0)},${getY(chartDataSource[0].close) - HEADER_HEIGHT} ${chartDataSource.map((d,i) => `L${getX(i)},${getY(d.close) - HEADER_HEIGHT}`).join(' ')} L${getX(chartDataSource.length-1)},${volumeTop - HEADER_HEIGHT} L${getX(0)},${volumeTop - HEADER_HEIGHT} Z`} fill="url(#areaFill)" />
                  <path d={`M${getX(0)},${getY(chartDataSource[0].close) - HEADER_HEIGHT} ${chartDataSource.map((d,i) => `L${getX(i)},${getY(d.close) - HEADER_HEIGHT}`).join(' ')}`} fill="none" stroke="#667eea" strokeWidth={1.5} />
                </>
              )}
            </>
          )}

          {chartSettings.showVolume && indicators.volume && (<g><line x1={chartLeft} x2={chartLeft + chartWidth} y1={volumeTop - HEADER_HEIGHT} y2={volumeTop - HEADER_HEIGHT} stroke="rgba(255,255,255,0.06)" /><text x={chartLeft + 5} y={volumeTop - HEADER_HEIGHT - 5} fill="rgba(255,255,255,0.3)" fontSize="9">Vol {formatVolume((hovered || current).volume)}</text>{chartDataSource.map((d, i) => { const h = (d.volume / maxVolume) * VOLUME_HEIGHT * 0.9; return <rect key={i} x={getX(i)} y={volumeTop - HEADER_HEIGHT + VOLUME_HEIGHT - h} width={candleWidth} height={h} fill={d.close >= d.open ? chartSettings.candleUpColor : chartSettings.candleDownColor} opacity={hoveredIndex === i ? 0.6 : 0.25} />; })}</g>)}

          {/* Render saved drawings */}
          {drawings.map(drawing => {
            // For pencil and text, we don't have start/end - handle separately
            const startY = drawing.start?.price != null ? getY(drawing.start.price) - HEADER_HEIGHT : 0;
            const endY = drawing.end?.price != null ? getY(drawing.end.price) - HEADER_HEIGHT : startY;
            
            // Wrapper function to add right-click handling and eraser support to drawings
            const wrapWithInteraction = (content) => (
              <g 
                key={drawing.id}
                className={selectedTool === 'eraser' ? 'eraser-target' : ''}
                style={{ cursor: selectedTool === 'eraser' ? 'pointer' : 'default' }}
                onClick={(e) => {
                  if (selectedTool === 'eraser') {
                    e.stopPropagation();
                    setDrawingsWithHistory(prev => prev.filter(d => d.id !== drawing.id));
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContextMenu({ x: e.clientX, y: e.clientY, drawingId: drawing.id });
                }}
              >
                {content}
              </g>
            );
            
            switch(drawing.type) {
              case 'trendline':
              case 'ray':
                return wrapWithInteraction(
                  <>
                    {/* Invisible wider stroke for easier clicking */}
                    <line 
                      x1={drawing.start.x} y1={startY} 
                      x2={drawing.end.x} y2={endY} 
                      stroke="transparent" strokeWidth={12} 
                    />
                    <line 
                      x1={drawing.start.x} y1={startY} 
                      x2={drawing.end.x} y2={endY} 
                      stroke={selectedTool === 'eraser' ? '#ef4444' : drawing.color} strokeWidth={drawing.strokeWidth || 2} 
                    />
                    <circle cx={drawing.start.x} cy={startY} r={4} fill={drawing.color} />
                    <circle cx={drawing.end.x} cy={endY} r={4} fill={drawing.color} />
                  </>
                );
              case 'hline':
                const hlineY = getY(drawing.price) - HEADER_HEIGHT;
                return wrapWithInteraction(
                  <>
                    {/* Invisible wider stroke for easier clicking */}
                    <line 
                      x1={chartLeft} y1={hlineY} 
                      x2={chartLeft + chartWidth} y2={hlineY} 
                      stroke="transparent" strokeWidth={12}
                    />
                    <line 
                      x1={chartLeft} y1={hlineY} 
                      x2={chartLeft + chartWidth} y2={hlineY} 
                      stroke={selectedTool === 'eraser' ? '#ef4444' : drawing.color} strokeWidth={drawing.strokeWidth || 1.5} strokeDasharray="5,5"
                    />
                    <rect x={chartLeft + chartWidth + 2} y={hlineY - 9} width={62} height={18} fill={selectedTool === 'eraser' ? '#ef4444' : drawing.color} rx={3} />
                    <text x={chartLeft + chartWidth + 33} y={hlineY + 1} fill="#fff" fontSize="10" textAnchor="middle" dominantBaseline="middle">{formatPrice(drawing.price)}</text>
                  </>
                );
              case 'vline':
                return wrapWithInteraction(
                  <>
                    {/* Invisible wider stroke for easier clicking */}
                    <line 
                      x1={drawing.x} y1={0} 
                      x2={drawing.x} y2={volumeTop - HEADER_HEIGHT} 
                      stroke="transparent" strokeWidth={12}
                    />
                    <line 
                      x1={drawing.x} y1={0} 
                      x2={drawing.x} y2={volumeTop - HEADER_HEIGHT} 
                      stroke={selectedTool === 'eraser' ? '#ef4444' : drawing.color} strokeWidth={1.5} strokeDasharray="5,5"
                    />
                  </>
                );
              case 'rectangle':
                const rectTop = Math.min(drawing.start.price, drawing.end.price);
                const rectBottom = Math.max(drawing.start.price, drawing.end.price);
                const rectYTop = getY(rectBottom) - HEADER_HEIGHT;
                const rectYBottom = getY(rectTop) - HEADER_HEIGHT;
                const rectX = Math.min(drawing.start.x, drawing.end.x);
                const rectWidth = Math.abs(drawing.end.x - drawing.start.x);
                const rectHeight = Math.abs(rectYBottom - rectYTop);
                
                return wrapWithInteraction(
                  <g className="rect-drawing">
                    {/* Filled rectangle */}
                    <rect 
                      x={rectX} 
                      y={rectYTop} 
                      width={rectWidth} 
                      height={rectHeight} 
                      fill={`${drawing.color}15`}
                      stroke={drawing.color} 
                      strokeWidth={1.5}
                    />
                    {/* Top price label */}
                    <rect x={rectX + rectWidth + 4} y={rectYTop - 8} width={58} height={16} fill="rgba(26,26,46,0.9)" stroke={drawing.color} strokeWidth={1} rx={3} />
                    <text x={rectX + rectWidth + 8} y={rectYTop + 3} fill={drawing.color} fontSize="10">{formatPrice(rectBottom)}</text>
                    {/* Bottom price label */}
                    <rect x={rectX + rectWidth + 4} y={rectYTop + rectHeight - 8} width={58} height={16} fill="rgba(26,26,46,0.9)" stroke={drawing.color} strokeWidth={1} rx={3} />
                    <text x={rectX + rectWidth + 8} y={rectYTop + rectHeight + 3} fill={drawing.color} fontSize="10">{formatPrice(rectTop)}</text>
                    {/* Zone label */}
                    <rect x={rectX + 4} y={rectYTop + 4} width={40} height={14} fill="rgba(26,26,46,0.8)" rx={2} />
                    <text x={rectX + 8} y={rectYTop + 13} fill="rgba(255,255,255,0.7)" fontSize="8">ZONE</text>
                  </g>
                );
              case 'fibretracement':
                const fibLevels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
                const fibExtensions = [1.272, 1.618, 2.0, 2.618]; // Extension levels
                const fibColors = {
                  0: '#ef5350',
                  0.236: '#f97316', 
                  0.382: '#eab308',
                  0.5: '#22c55e',
                  0.618: '#3b82f6',
                  0.786: '#8b5cf6',
                  1: '#ef5350',
                  1.272: '#6366f1',
                  1.618: '#a855f7',
                  2.0: '#ec4899',
                  2.618: '#f43f5e'
                };
                const fibPriceDiff = drawing.end.price - drawing.start.price;
                const fibIsUp = fibPriceDiff > 0;
                
                return wrapWithInteraction(
                  <g className="fib-drawing">
                    {/* Shaded zones between key levels */}
                    {[[0.382, 0.5], [0.5, 0.618], [0.618, 0.786]].map(([l1, l2], idx) => {
                      const y1 = getY(drawing.start.price + fibPriceDiff * l1) - HEADER_HEIGHT;
                      const y2 = getY(drawing.start.price + fibPriceDiff * l2) - HEADER_HEIGHT;
                      return (
                        <rect 
                          key={`zone-${idx}`}
                          x={chartLeft} 
                          y={Math.min(y1, y2)} 
                          width={chartWidth} 
                          height={Math.abs(y2 - y1)}
                          fill={idx === 1 ? 'rgba(34, 197, 94, 0.06)' : 'rgba(59, 130, 246, 0.04)'}
                        />
                      );
                    })}
                    
                    {/* Main retracement levels */}
                    {fibLevels.map((level) => {
                      const fibPrice = drawing.start.price + fibPriceDiff * level;
                      const fibY = getY(fibPrice) - HEADER_HEIGHT;
                      const color = fibColors[level];
                      const isKeyLevel = [0.5, 0.618].includes(level);
                      return (
                        <g key={level}>
                          <line 
                            x1={chartLeft} y1={fibY} 
                            x2={chartLeft + chartWidth} y2={fibY} 
                            stroke={color} 
                            strokeWidth={isKeyLevel ? 1.5 : 1} 
                            strokeDasharray={isKeyLevel ? "none" : "4,4"} 
                            opacity={isKeyLevel ? 1 : 0.7}
                          />
                          {/* Left label */}
                          <rect x={chartLeft + 2} y={fibY - 8} width={42} height={16} fill="rgba(26,26,46,0.9)" rx={3} />
                          <text x={chartLeft + 6} y={fibY + 3} fill={color} fontSize="10" fontWeight={isKeyLevel ? "600" : "400"}>
                            {(level * 100).toFixed(1)}%
                          </text>
                          {/* Right price label */}
                          <rect x={chartLeft + chartWidth - 62} y={fibY - 8} width={60} height={16} fill="rgba(26,26,46,0.9)" rx={3} />
                          <text x={chartLeft + chartWidth - 58} y={fibY + 3} fill={color} fontSize="10">
                            {formatPrice(fibPrice)}
                          </text>
                        </g>
                      );
                    })}
                    
                    {/* Extension levels (beyond 100%) */}
                    {fibExtensions.map((level) => {
                      const fibPrice = drawing.start.price + fibPriceDiff * level;
                      const fibY = getY(fibPrice) - HEADER_HEIGHT;
                      if (fibY < 0 || fibY > mainChartHeight) return null;
                      const color = fibColors[level];
                      return (
                        <g key={level}>
                          <line 
                            x1={chartLeft} y1={fibY} 
                            x2={chartLeft + chartWidth} y2={fibY} 
                            stroke={color} 
                            strokeWidth={1} 
                            strokeDasharray="8,4" 
                            opacity={0.6}
                          />
                          <rect x={chartLeft + 2} y={fibY - 8} width={48} height={16} fill="rgba(26,26,46,0.9)" rx={3} />
                          <text x={chartLeft + 6} y={fibY + 3} fill={color} fontSize="10">
                            {(level * 100).toFixed(1)}%
                          </text>
                          <rect x={chartLeft + chartWidth - 62} y={fibY - 8} width={60} height={16} fill="rgba(26,26,46,0.9)" rx={3} />
                          <text x={chartLeft + chartWidth - 58} y={fibY + 3} fill={color} fontSize="10">
                            {formatPrice(fibPrice)}
                          </text>
                        </g>
                      );
                    })}
                    
                    {/* Start/End markers */}
                    <circle cx={drawing.start.x} cy={startY} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                    <circle cx={drawing.end.x} cy={endY} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                    
                    {/* Trend line connecting start to end */}
                    <line x1={drawing.start.x} y1={startY} x2={drawing.end.x} y2={endY} stroke="#667eea" strokeWidth={1.5} opacity={0.5} />
                  </g>
                );
              case 'arrow':
                // Calculate arrow angle
                const arrowDx = drawing.end.x - drawing.start.x;
                const arrowDy = endY - startY;
                const arrowAngle = Math.atan2(arrowDy, arrowDx);
                const arrowHeadLength = 12;
                const arrowHeadAngle = Math.PI / 6; // 30 degrees
                
                // Arrow head points
                const arrowX1 = drawing.end.x - arrowHeadLength * Math.cos(arrowAngle - arrowHeadAngle);
                const arrowY1 = endY - arrowHeadLength * Math.sin(arrowAngle - arrowHeadAngle);
                const arrowX2 = drawing.end.x - arrowHeadLength * Math.cos(arrowAngle + arrowHeadAngle);
                const arrowY2 = endY - arrowHeadLength * Math.sin(arrowAngle + arrowHeadAngle);
                
                const arrowIsUp = drawing.end.price > drawing.start.price;
                const arrowColor = arrowIsUp ? '#22c55e' : '#ef4444';
                
                return wrapWithInteraction(
                  <g className="arrow-drawing">
                    {/* Arrow line */}
                    <line 
                      x1={drawing.start.x} y1={startY} 
                      x2={drawing.end.x} y2={endY} 
                      stroke={arrowColor} 
                      strokeWidth={2.5}
                      strokeLinecap="round"
                    />
                    {/* Arrow head */}
                    <polygon 
                      points={`${drawing.end.x},${endY} ${arrowX1},${arrowY1} ${arrowX2},${arrowY2}`}
                      fill={arrowColor}
                    />
                    {/* Start dot */}
                    <circle cx={drawing.start.x} cy={startY} r={4} fill={arrowColor} stroke="#fff" strokeWidth={2} />
                  </g>
                );
              case 'measure':
                const measurePriceDiff = drawing.end.price - drawing.start.price;
                const measurePct = (measurePriceDiff / drawing.start.price * 100).toFixed(2);
                const midX = (drawing.start.x + drawing.end.x) / 2;
                const midY = (startY + endY) / 2;
                const isPositive = measurePriceDiff >= 0;
                const measureColor = isPositive ? '#22c55e' : '#ef4444';
                
                // Calculate candle/time difference
                const startCandle = Math.round((drawing.start.x - chartLeft - candleGap) / (candleWidth + candleGap));
                const endCandle = Math.round((drawing.end.x - chartLeft - candleGap) / (candleWidth + candleGap));
                const candlesDiff = Math.abs(endCandle - startCandle);
                const startDate = chartDataSource[Math.max(0, Math.min(startCandle, chartDataSource.length - 1))]?.date;
                const endDate = chartDataSource[Math.max(0, Math.min(endCandle, chartDataSource.length - 1))]?.date;
                const daysDiff = startDate && endDate ? Math.abs(Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))) : candlesDiff;
                
                return wrapWithInteraction(
                  <g className="measure-drawing">
                    {/* Connecting line */}
                    <line x1={drawing.start.x} y1={startY} x2={drawing.end.x} y2={endY} stroke={measureColor} strokeWidth={2} strokeDasharray="6,4" />
                    
                    {/* Vertical price reference lines */}
                    <line x1={drawing.start.x} y1={startY} x2={drawing.start.x} y2={endY} stroke={measureColor} strokeWidth={1} strokeDasharray="3,3" opacity={0.4} />
                    <line x1={drawing.end.x} y1={startY} x2={drawing.end.x} y2={endY} stroke={measureColor} strokeWidth={1} strokeDasharray="3,3" opacity={0.4} />
                    
                    {/* Horizontal time reference line */}
                    <line x1={drawing.start.x} y1={endY} x2={drawing.end.x} y2={endY} stroke={measureColor} strokeWidth={1} strokeDasharray="3,3" opacity={0.4} />
                    
                    {/* End points */}
                    <circle cx={drawing.start.x} cy={startY} r={5} fill={measureColor} stroke="#fff" strokeWidth={2} />
                    <circle cx={drawing.end.x} cy={endY} r={5} fill={measureColor} stroke="#fff" strokeWidth={2} />
                    
                    {/* Info box */}
                    <rect x={midX - 65} y={midY - 28} width={130} height={56} fill="rgba(26,26,46,0.95)" stroke={measureColor} strokeWidth={1.5} rx={6} />
                    
                    {/* Price change */}
                    <text x={midX} y={midY - 14} fill={measureColor} fontSize="12" fontWeight="700" textAnchor="middle">
                      {isPositive ? '+' : ''}{formatPrice(measurePriceDiff)} ({isPositive ? '+' : ''}{measurePct}%)
                    </text>
                    
                    {/* Time info */}
                    <text x={midX} y={midY + 4} fill="rgba(255,255,255,0.7)" fontSize="10" textAnchor="middle">
                      {candlesDiff} candles • {daysDiff} days
                    </text>
                    
                    {/* Price levels */}
                    <text x={midX} y={midY + 18} fill="rgba(255,255,255,0.5)" fontSize="9" textAnchor="middle">
                      {formatPrice(drawing.start.price)} → {formatPrice(drawing.end.price)}
                    </text>
                  </g>
                );
              case 'text':
                const textY = getY(drawing.price) - HEADER_HEIGHT;
                const textLines = drawing.text.split('\n');
                const lineHeight = 16;
                const maxLineLength = Math.max(...textLines.map(l => l.length));
                const textBoxWidth = maxLineLength * 7 + 28; // Extra space for delete button
                const textBoxHeight = textLines.length * lineHeight + 10;
                
                return (
                  <g 
                    key={drawing.id}
                    style={{ cursor: selectedTool === 'eraser' ? 'pointer' : 'default' }}
                    className={`text-drawing-group ${selectedTool === 'eraser' ? 'eraser-target' : ''}`}
                    onClick={(e) => {
                      if (selectedTool === 'eraser') {
                        e.stopPropagation();
                        setDrawingsWithHistory(prev => prev.filter(d => d.id !== drawing.id));
                      }
                    }}
                  >
                    <rect 
                      x={drawing.x - 4} 
                      y={textY - 14} 
                      width={textBoxWidth} 
                      height={textBoxHeight} 
                      fill="rgba(26,26,46,0.9)" 
                      stroke={selectedTool === 'eraser' ? '#ef4444' : drawing.color} 
                      strokeWidth={selectedTool === 'eraser' ? 2 : 1}
                      rx={4} 
                    />
                    {textLines.map((line, idx) => (
                      <text 
                        key={idx}
                        x={drawing.x + 2} 
                        y={textY - 2 + (idx * lineHeight)} 
                        fill="#fff" 
                        fontSize="12" 
                        fontWeight="500"
                        dominantBaseline="middle"
                      >
                        {line}
                      </text>
                    ))}
                    {/* Delete button - shows on hover via CSS (hidden when eraser active) */}
                    {selectedTool !== 'eraser' && (
                    <g 
                      className="text-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawingsWithHistory(prev => prev.filter(d => d.id !== drawing.id));
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <circle 
                        cx={drawing.x + textBoxWidth - 14} 
                        cy={textY - 8} 
                        r={8} 
                        fill="rgba(239,68,68,0.9)"
                      />
                      <line 
                        x1={drawing.x + textBoxWidth - 17} 
                        y1={textY - 11} 
                        x2={drawing.x + textBoxWidth - 11} 
                        y2={textY - 5} 
                        stroke="#fff" 
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                      <line 
                        x1={drawing.x + textBoxWidth - 11} 
                        y1={textY - 11} 
                        x2={drawing.x + textBoxWidth - 17} 
                        y2={textY - 5} 
                        stroke="#fff" 
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                    </g>
                    )}
                  </g>
                );
              case 'pencil':
                if (!drawing.path || drawing.path.length < 2) return null;
                const pencilPathD = drawing.path.map((p, i) => {
                  return `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`;
                }).join(' ');
                // Find the first point for delete button position
                const firstPoint = drawing.path[0];
                return (
                  <g 
                    key={drawing.id}
                    style={{ cursor: selectedTool === 'eraser' ? 'pointer' : 'default' }}
                    className={`pencil-drawing-group ${selectedTool === 'eraser' ? 'eraser-target' : ''}`}
                    onClick={(e) => {
                      if (selectedTool === 'eraser') {
                        e.stopPropagation();
                        setDrawingsWithHistory(prev => prev.filter(d => d.id !== drawing.id));
                      }
                    }}
                  >
                    {/* Invisible wider path for easier clicking */}
                    <path 
                      d={pencilPathD}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={12}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path 
                      d={pencilPathD}
                      fill="none"
                      stroke={selectedTool === 'eraser' ? '#ef4444' : drawing.color}
                      strokeWidth={selectedTool === 'eraser' ? (drawing.strokeWidth || 2) + 1 : (drawing.strokeWidth || 2)}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Delete button - shows on hover via CSS (hidden when eraser active) */}
                    {selectedTool !== 'eraser' && (
                    <g 
                      className="pencil-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawingsWithHistory(prev => prev.filter(d => d.id !== drawing.id));
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <circle 
                        cx={firstPoint.x} 
                        cy={firstPoint.y - 12} 
                        r={8} 
                        fill="rgba(239,68,68,0.9)"
                      />
                      <line 
                        x1={firstPoint.x - 3} 
                        y1={firstPoint.y - 15} 
                        x2={firstPoint.x + 3} 
                        y2={firstPoint.y - 9} 
                        stroke="#fff" 
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                      <line 
                        x1={firstPoint.x + 3} 
                        y1={firstPoint.y - 15} 
                        x2={firstPoint.x - 3} 
                        y2={firstPoint.y - 9} 
                        stroke="#fff" 
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                    </g>
                    )}
                  </g>
                );
              case 'ellipse':
                const ellipseRx = Math.abs(drawing.end.x - drawing.start.x) / 2;
                const ellipseRy = Math.abs(endY - startY) / 2;
                const ellipseCx = (drawing.start.x + drawing.end.x) / 2;
                const ellipseCy = (startY + endY) / 2;
                return wrapWithInteraction(
                  <ellipse
                    cx={ellipseCx}
                    cy={ellipseCy}
                    rx={ellipseRx}
                    ry={ellipseRy}
                    fill={`${drawing.color}15`}
                    stroke={drawing.color}
                    strokeWidth={1.5}
                  />
                );
              case 'triangle':
                const triX1 = drawing.start.x;
                const triY1 = endY; // Bottom left
                const triX2 = drawing.end.x;
                const triY2 = endY; // Bottom right
                const triX3 = (drawing.start.x + drawing.end.x) / 2;
                const triY3 = startY; // Top center
                return wrapWithInteraction(
                  <polygon
                    points={`${triX1},${triY1} ${triX2},${triY2} ${triX3},${triY3}`}
                    fill={`${drawing.color}15`}
                    stroke={drawing.color}
                    strokeWidth={1.5}
                  />
                );
              case 'channel':
                const channelHeight = Math.abs(endY - startY);
                return wrapWithInteraction(
                  <g className="channel-drawing">
                    {/* Upper line */}
                    <line 
                      x1={drawing.start.x} y1={startY} 
                      x2={drawing.end.x} y2={startY} 
                      stroke={drawing.color} strokeWidth={1.5} 
                    />
                    {/* Lower line */}
                    <line 
                      x1={drawing.start.x} y1={endY} 
                      x2={drawing.end.x} y2={endY} 
                      stroke={drawing.color} strokeWidth={1.5} 
                    />
                    {/* Fill between */}
                    <rect
                      x={Math.min(drawing.start.x, drawing.end.x)}
                      y={Math.min(startY, endY)}
                      width={Math.abs(drawing.end.x - drawing.start.x)}
                      height={channelHeight}
                      fill={`${drawing.color}10`}
                    />
                    {/* End caps */}
                    <line x1={drawing.start.x} y1={startY} x2={drawing.start.x} y2={endY} stroke={drawing.color} strokeWidth={1} strokeDasharray="3,3" />
                    <line x1={drawing.end.x} y1={startY} x2={drawing.end.x} y2={endY} stroke={drawing.color} strokeWidth={1} strokeDasharray="3,3" />
                  </g>
                );
              case 'fibextension':
                const extPriceDiff = drawing.end.price - drawing.start.price;
                const extLevels = [0, 0.618, 1, 1.618, 2, 2.618, 3.618, 4.236];
                const extColors = {
                  0: '#ef5350', 0.618: '#3b82f6', 1: '#22c55e',
                  1.618: '#f59e0b', 2: '#8b5cf6', 2.618: '#ec4899',
                  3.618: '#06b6d4', 4.236: '#84cc16'
                };
                return wrapWithInteraction(
                  <g className="fibext-drawing">
                    {extLevels.map(level => {
                      const extY = getY(drawing.start.price + extPriceDiff * level) - HEADER_HEIGHT;
                      if (extY < 0 || extY > mainChartHeight + candleYOffset) return null;
                      return (
                        <g key={level}>
                          <line
                            x1={chartLeft} y1={extY}
                            x2={chartLeft + chartWidth} y2={extY}
                            stroke={extColors[level] || '#667eea'}
                            strokeWidth={level === 1 ? 2 : 1}
                            strokeDasharray={level > 1 ? '5,5' : 'none'}
                          />
                          <text x={chartLeft + 5} y={extY - 4} fill={extColors[level] || '#667eea'} fontSize="9">
                            {(level * 100).toFixed(1)}%
                          </text>
                          <text x={chartLeft + chartWidth - 50} y={extY - 4} fill="rgba(255,255,255,0.6)" fontSize="9">
                            {formatPrice(drawing.start.price + extPriceDiff * level)}
                          </text>
                        </g>
                      );
                    })}
                    <circle cx={drawing.start.x} cy={startY} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                    <circle cx={drawing.end.x} cy={endY} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                  </g>
                );
              default:
                return null;
            }
          })}

          {/* Active drawing preview */}
          {isDrawing && drawStart && (mousePos || currentPencilPath.length > 0) && (
            <g className="drawing-preview" opacity={0.7}>
              {selectedTool === 'trendline' || selectedTool === 'ray' ? (
                <line 
                  x1={drawStart.x} y1={getY(drawStart.price) - HEADER_HEIGHT} 
                  x2={mousePos?.x || drawStart.x} y2={(mousePos?.y || drawStart.y) - HEADER_HEIGHT} 
                  stroke="#667eea" strokeWidth={2} strokeDasharray="5,5"
                />
              ) : selectedTool === 'hline' ? (
                <line 
                  x1={chartLeft} y1={getY(drawStart.price) - HEADER_HEIGHT} 
                  x2={chartLeft + chartWidth} y2={getY(drawStart.price) - HEADER_HEIGHT} 
                  stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5"
                />
              ) : selectedTool === 'vline' ? (
                <line 
                  x1={drawStart.x} y1={0} 
                  x2={drawStart.x} y2={volumeTop - HEADER_HEIGHT} 
                  stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5"
                />
              ) : selectedTool === 'rectangle' ? (
                <rect 
                  x={Math.min(drawStart.x, mousePos.x)} 
                  y={Math.min(getY(drawStart.price) - HEADER_HEIGHT, mousePos.y - HEADER_HEIGHT)} 
                  width={Math.abs(mousePos.x - drawStart.x)} 
                  height={Math.abs(mousePos.y - HEADER_HEIGHT - (getY(drawStart.price) - HEADER_HEIGHT))} 
                  fill="rgba(102, 126, 234, 0.1)" 
                  stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5"
                />
              ) : selectedTool === 'fibretracement' ? (
                (() => {
                  const fibLevels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
                  const fibColors = {
                    0: '#ef5350', 0.236: '#f97316', 0.382: '#eab308',
                    0.5: '#22c55e', 0.618: '#3b82f6', 0.786: '#8b5cf6', 1: '#ef5350'
                  };
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endPrice = getPriceFromY(mousePos.y);
                  const priceDiff = endPrice - drawStart.price;
                  
                  return (
                    <g className="fib-preview">
                      {/* Preview lines for each level */}
                      {fibLevels.map((level) => {
                        const fibPrice = drawStart.price + priceDiff * level;
                        const fibY = getY(fibPrice) - HEADER_HEIGHT;
                        const color = fibColors[level];
                        const isKeyLevel = [0.5, 0.618].includes(level);
                        return (
                          <g key={level}>
                            <line 
                              x1={chartLeft} y1={fibY} 
                              x2={chartLeft + chartWidth} y2={fibY} 
                              stroke={color} 
                              strokeWidth={isKeyLevel ? 1.5 : 1} 
                              strokeDasharray="5,5" 
                              opacity={0.6}
                            />
                            <text x={chartLeft + 6} y={fibY + 3} fill={color} fontSize="9" opacity={0.8}>
                              {(level * 100).toFixed(1)}%
                            </text>
                          </g>
                        );
                      })}
                      {/* Trend line */}
                      <line x1={drawStart.x} y1={startY} x2={mousePos.x} y2={mousePos.y - HEADER_HEIGHT} stroke="#667eea" strokeWidth={2} strokeDasharray="6,4" />
                      {/* End points */}
                      <circle cx={drawStart.x} cy={startY} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                      <circle cx={mousePos.x} cy={mousePos.y - HEADER_HEIGHT} r={5} fill="#667eea" stroke="#fff" strokeWidth={2} />
                    </g>
                  );
                })()
              ) : selectedTool === 'arrow' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endY = mousePos.y - HEADER_HEIGHT;
                  const endPrice = getPriceFromY(mousePos.y);
                  const isUp = endPrice > drawStart.price;
                  const color = isUp ? '#22c55e' : '#ef4444';
                  
                  // Arrow head calculation
                  const dx = mousePos.x - drawStart.x;
                  const dy = endY - startY;
                  const angle = Math.atan2(dy, dx);
                  const headLen = 12;
                  const headAngle = Math.PI / 6;
                  const x1 = mousePos.x - headLen * Math.cos(angle - headAngle);
                  const y1 = endY - headLen * Math.sin(angle - headAngle);
                  const x2 = mousePos.x - headLen * Math.cos(angle + headAngle);
                  const y2 = endY - headLen * Math.sin(angle + headAngle);
                  
                  return (
                    <g className="arrow-preview">
                      <line x1={drawStart.x} y1={startY} x2={mousePos.x} y2={endY} stroke={color} strokeWidth={2.5} strokeLinecap="round" />
                      <polygon points={`${mousePos.x},${endY} ${x1},${y1} ${x2},${y2}`} fill={color} />
                      <circle cx={drawStart.x} cy={startY} r={4} fill={color} stroke="#fff" strokeWidth={2} />
                    </g>
                  );
                })()
              ) : selectedTool === 'measure' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endY = mousePos.y - HEADER_HEIGHT;
                  const endPrice = getPriceFromY(mousePos.y);
                  const priceDiff = endPrice - drawStart.price;
                  const pctChange = (priceDiff / drawStart.price * 100).toFixed(2);
                  const isPositive = priceDiff >= 0;
                  const color = isPositive ? '#22c55e' : '#ef4444';
                  const midX = (drawStart.x + mousePos.x) / 2;
                  const midY = (startY + endY) / 2;
                  
                  // Calculate candles
                  const startCandle = Math.round((drawStart.x - chartLeft - candleGap) / (candleWidth + candleGap));
                  const endCandle = Math.round((mousePos.x - chartLeft - candleGap) / (candleWidth + candleGap));
                  const candlesDiff = Math.abs(endCandle - startCandle);
                  
                  return (
                    <g className="measure-preview">
                      {/* Main diagonal line */}
                      <line x1={drawStart.x} y1={startY} x2={mousePos.x} y2={endY} stroke={color} strokeWidth={2} strokeDasharray="6,4" />
                      
                      {/* Reference lines */}
                      <line x1={drawStart.x} y1={startY} x2={drawStart.x} y2={endY} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.3} />
                      <line x1={mousePos.x} y1={startY} x2={mousePos.x} y2={endY} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.3} />
                      <line x1={drawStart.x} y1={endY} x2={mousePos.x} y2={endY} stroke={color} strokeWidth={1} strokeDasharray="3,3" opacity={0.3} />
                      
                      {/* End points */}
                      <circle cx={drawStart.x} cy={startY} r={5} fill={color} stroke="#fff" strokeWidth={2} />
                      <circle cx={mousePos.x} cy={endY} r={5} fill={color} stroke="#fff" strokeWidth={2} />
                      
                      {/* Live info box */}
                      <rect x={midX - 55} y={midY - 22} width={110} height={44} fill="rgba(26,26,46,0.95)" stroke={color} strokeWidth={1.5} rx={6} />
                      <text x={midX} y={midY - 6} fill={color} fontSize="13" fontWeight="700" textAnchor="middle">
                        {isPositive ? '+' : ''}{formatPrice(priceDiff)} ({isPositive ? '+' : ''}{pctChange}%)
                      </text>
                      <text x={midX} y={midY + 12} fill="rgba(255,255,255,0.6)" fontSize="10" textAnchor="middle">
                        {candlesDiff} candles
                      </text>
                    </g>
                  );
                })()
              ) : selectedTool === 'ellipse' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endY = mousePos.y - HEADER_HEIGHT;
                  const rx = Math.abs(mousePos.x - drawStart.x) / 2;
                  const ry = Math.abs(endY - startY) / 2;
                  const cx = (drawStart.x + mousePos.x) / 2;
                  const cy = (startY + endY) / 2;
                  return (
                    <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="rgba(102, 126, 234, 0.1)" stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5" />
                  );
                })()
              ) : selectedTool === 'triangle' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endY = mousePos.y - HEADER_HEIGHT;
                  const x1 = drawStart.x;
                  const y1 = endY;
                  const x2 = mousePos.x;
                  const y2 = endY;
                  const x3 = (drawStart.x + mousePos.x) / 2;
                  const y3 = startY;
                  return (
                    <polygon points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`} fill="rgba(102, 126, 234, 0.1)" stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5" />
                  );
                })()
              ) : selectedTool === 'channel' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endY = mousePos.y - HEADER_HEIGHT;
                  const height = Math.abs(endY - startY);
                  return (
                    <g className="channel-preview">
                      <line x1={drawStart.x} y1={startY} x2={mousePos.x} y2={startY} stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5" />
                      <line x1={drawStart.x} y1={endY} x2={mousePos.x} y2={endY} stroke="#667eea" strokeWidth={1.5} strokeDasharray="5,5" />
                      <rect x={Math.min(drawStart.x, mousePos.x)} y={Math.min(startY, endY)} width={Math.abs(mousePos.x - drawStart.x)} height={height} fill="rgba(102, 126, 234, 0.08)" />
                    </g>
                  );
                })()
              ) : selectedTool === 'fibextension' ? (
                (() => {
                  const startY = getY(drawStart.price) - HEADER_HEIGHT;
                  const endPrice = getPriceFromY(mousePos.y);
                  const priceDiff = endPrice - drawStart.price;
                  const extLevels = [0, 0.618, 1, 1.618, 2, 2.618];
                  return (
                    <g className="fibext-preview">
                      {extLevels.map((level) => {
                        const fibPrice = drawStart.price + priceDiff * level;
                        const fibY = getY(fibPrice) - HEADER_HEIGHT;
                        return (
                          <g key={level}>
                            <line x1={chartLeft} y1={fibY} x2={chartLeft + chartWidth} y2={fibY} stroke="#667eea" strokeWidth={1} strokeDasharray="5,5" opacity={0.6} />
                            <text x={chartLeft + 5} y={fibY - 4} fill="rgba(255,255,255,0.5)" fontSize="9">{(level * 100).toFixed(1)}%</text>
                          </g>
                        );
                      })}
                      <circle cx={drawStart.x} cy={startY} r={4} fill="#667eea" />
                    </g>
                  );
                })()
              ) : selectedTool === 'pencil' && currentPencilPath.length > 0 ? (
                <path 
                  d={currentPencilPath.map((p, i) => {
                    return `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`;
                  }).join(' ')}
                  fill="none"
                  stroke={drawingColor}
                  strokeWidth={drawingStrokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.8}
                />
              ) : null}
            </g>
          )}

          <line x1={chartLeft} x2={chartLeft + chartWidth} y1={getY(current.close) - HEADER_HEIGHT} y2={getY(current.close) - HEADER_HEIGHT} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
          <rect x={chartLeft + chartWidth + 2} y={getY(current.close) - HEADER_HEIGHT - 9} width={62} height={18} fill="#667eea" rx={3} />
          <text x={chartLeft + chartWidth + 33} y={getY(current.close) - HEADER_HEIGHT + 1} fill="#fff" fontSize="10" textAnchor="middle" dominantBaseline="middle">{formatPrice(current.close)}</text>

          {chartSettings.showCrosshair && mousePos && selectedTool === 'crosshair' && mousePos.y > HEADER_HEIGHT && mousePos.y < volumeTop && (<><line x1={chartLeft} x2={chartLeft + chartWidth} y1={mousePos.y - HEADER_HEIGHT} y2={mousePos.y - HEADER_HEIGHT} stroke={chartSettings.theme === 'light' ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.3)"} strokeDasharray="3,3" />{cursorX && <line x1={cursorX} x2={cursorX} y1={0} y2={timeAxisY} stroke={chartSettings.theme === 'light' ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.3)"} strokeDasharray="3,3" />}<rect x={chartLeft + chartWidth + 2} y={mousePos.y - HEADER_HEIGHT - 9} width={62} height={18} fill={chartSettings.theme === 'light' ? "rgba(255,255,255,0.95)" : "rgba(40,40,50,0.95)"} stroke={chartSettings.theme === 'light' ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.1)"} rx={3} /><text x={chartLeft + chartWidth + 33} y={mousePos.y - HEADER_HEIGHT + 1} fill={chartSettings.theme === 'light' ? "#1a1a2e" : "#fff"} fontSize="10" textAnchor="middle" dominantBaseline="middle">{formatPrice(mousePos.price)}</text></>)}

          {chartDataSource.filter((_, i) => i % Math.ceil(chartDataSource.length / 12) === 0).map((d, idx) => { const i = chartDataSource.indexOf(d); return <text key={idx} x={getX(i) + candleWidth / 2} y={timeAxisY + 15} fill="rgba(255,255,255,0.4)" fontSize="9" textAnchor="middle">{formatDate(d.date, timeframe)}</text>; })}
          {hovered && cursorX && mousePos && selectedTool === 'crosshair' && (<><rect x={cursorX - 40} y={timeAxisY + 3} width={80} height={18} fill={chartSettings.theme === 'light' ? "rgba(255,255,255,0.95)" : "rgba(40,40,50,0.95)"} stroke={chartSettings.theme === 'light' ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.1)"} rx={3} /><text x={cursorX} y={timeAxisY + 13} fill={chartSettings.theme === 'light' ? "#1a1a2e" : "#fff"} fontSize="10" textAnchor="middle" dominantBaseline="middle" fontWeight="500">{formatDate(hovered.date, timeframe)}</text></>)}
          
          {/* Price Alert Lines */}
          {priceAlerts.filter(a => a.active && a.type === 'price').map((alert, idx) => {
            const y = getY(alert.price) - HEADER_HEIGHT;
            if (y < 0 || y > mainChartHeight) return null;
            const isAbove = alert.condition === 'above';
            const alertColor = alert.triggered ? '#f59e0b' : (isAbove ? '#22c55e' : '#ef4444');
            return (
              <g key={`alert-${alert.id}`}>
                <line 
                  x1={chartLeft} 
                  x2={chartLeft + chartWidth} 
                  y1={y} 
                  y2={y} 
                  stroke={alertColor} 
                  strokeWidth={1.5} 
                  strokeDasharray={alert.triggered ? "none" : "6,3"} 
                  opacity={0.8} 
                />
                <rect 
                  x={chartLeft + chartWidth - 85} 
                  y={y - 10} 
                  width={80} 
                  height={20} 
                  fill={alertColor} 
                  rx={3} 
                />
                <Bell size={10} x={chartLeft + chartWidth - 80} y={y - 5} fill="#fff" />
                <text 
                  x={chartLeft + chartWidth - 45} 
                  y={y + 1} 
                  fill="#fff" 
                  fontSize="10" 
                  textAnchor="middle" 
                  dominantBaseline="middle"
                  fontWeight="600"
                >
                  ${alert.price.toFixed(2)}
                </text>
              </g>
            );
          })}

          {/* Support/Resistance Lines */}
          {showSupportResistance && supportResistance.resistance.map((level, idx) => {
            const y = getY(level.price) - HEADER_HEIGHT;
            if (y < 0 || y > mainChartHeight) return null;
            return (
              <g key={`res-${idx}`}>
                <line x1={chartLeft} x2={chartLeft + chartWidth} y1={y} y2={y} stroke="#ef5350" strokeWidth={1} strokeDasharray="8,4" opacity={0.7} />
                <rect x={chartLeft + 5} y={y - 10} width={65} height={16} fill="rgba(239,83,80,0.2)" rx={2} />
                <text x={chartLeft + 37} y={y} fill="#ef5350" fontSize="9" textAnchor="middle" dominantBaseline="middle">R{idx + 1}: {formatPrice(level.price)}</text>
              </g>
            );
          })}
          {showSupportResistance && supportResistance.support.map((level, idx) => {
            const y = getY(level.price) - HEADER_HEIGHT;
            if (y < 0 || y > mainChartHeight) return null;
            return (
              <g key={`sup-${idx}`}>
                <line x1={chartLeft} x2={chartLeft + chartWidth} y1={y} y2={y} stroke="#26a69a" strokeWidth={1} strokeDasharray="8,4" opacity={0.7} />
                <rect x={chartLeft + 5} y={y - 10} width={65} height={16} fill="rgba(38,166,154,0.2)" rx={2} />
                <text x={chartLeft + 37} y={y} fill="#26a69a" fontSize="9" textAnchor="middle" dominantBaseline="middle">S{idx + 1}: {formatPrice(level.price)}</text>
              </g>
            );
          })}

          {/* Volume Profile (horizontal bars on right) */}
          {showVolumeProfile && volumeProfile.map((bin, idx) => {
            const barWidth = bin.percentage * 60;
            const y1 = getY(bin.priceEnd) - HEADER_HEIGHT;
            const y2 = getY(bin.priceStart) - HEADER_HEIGHT;
            const height = Math.abs(y2 - y1);
            return (
              <rect 
                key={idx}
                x={chartLeft + chartWidth - barWidth - 5}
                y={Math.min(y1, y2)}
                width={barWidth}
                height={height}
                fill="rgba(102, 126, 234, 0.3)"
                stroke="rgba(102, 126, 234, 0.5)"
                strokeWidth={0.5}
              />
            );
          })}

          {/* Price Alerts */}
          {priceAlerts.map((alert, idx) => {
            const y = getY(alert.price) - HEADER_HEIGHT;
            if (y < 0 || y > mainChartHeight) return null;
            return (
              <g key={`alert-${idx}`}>
                <line x1={chartLeft} x2={chartLeft + chartWidth} y1={y} y2={y} stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4,4" />
                <circle cx={chartLeft + chartWidth - 10} cy={y} r={8} fill="#f59e0b" />
                <Bell x={chartLeft + chartWidth - 14} y={y - 4} size={8} stroke="#fff" strokeWidth={2} />
              </g>
            );
          })}

          {/* Pulsing current price dot */}
          {pulsePrice && (
            <g>
              <circle cx={getX(data.length - 1) + candleWidth / 2} cy={getY(current.close) - HEADER_HEIGHT} r={4} fill="#667eea">
                <animate attributeName="r" values="4;8;4" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx={getX(data.length - 1) + candleWidth / 2} cy={getY(current.close) - HEADER_HEIGHT} r={3} fill="#667eea" />
            </g>
          )}

          {/* Candle hover glow effect */}
          {hoveredIndex !== null && chartType === 'candle' && data[hoveredIndex] && data[hoveredIndex].high != null && (
            <rect 
              x={getX(hoveredIndex) - 2} 
              y={getY(data[hoveredIndex].high) - HEADER_HEIGHT - 2}
              width={candleWidth + 4}
              height={getY(data[hoveredIndex].low) - getY(data[hoveredIndex].high) + 4}
              fill="none"
              stroke="rgba(102, 126, 234, 0.5)"
              strokeWidth={2}
              rx={2}
            />
          )}
        </svg>
        )}

        {/* OHLC Stats Overlay - Only show in single mode */}
        {!compareSymbol && chartLayout === 'single' && (
          <div className="fs-chart-ohlc-overlay">
            <span className="ohlc-symbol">{symbol}</span>
            <span className="ohlc-item">O <b>{formatPrice((hovered || current).open)}</b></span>
            <span className="ohlc-item">H <b className="green">{formatPrice((hovered || current).high)}</b></span>
            <span className="ohlc-item">L <b className="red">{formatPrice((hovered || current).low)}</b></span>
            <span className="ohlc-item">C <b className={(hovered || current).close >= (hovered || current).open ? 'green' : 'red'}>{formatPrice((hovered || current).close)}</b></span>
            <span className={`ohlc-change ${change >= 0 ? 'green' : 'red'}`}>
              {change >= 0 ? '+' : ''}{formatPrice(change)} ({changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%)
            </span>
          </div>
        )}

        {/* Comparison Table - Shows when comparing */}
        {compareSymbol && compareData && compareData.length > 0 && (
          <div className="fs-compare-panel-container">
            {/* Compact OHLC-style header */}
            <div className="fs-compare-ohlc-bar">
              <span className="fs-compare-ohlc-sym" style={{color: '#667eea'}}>{symbol}</span>
              <span className="fs-compare-ohlc-item">O <b>{formatPrice((hovered || current).open)}</b></span>
              <span className="fs-compare-ohlc-item">H <b className="green">{formatPrice((hovered || current).high)}</b></span>
              <span className="fs-compare-ohlc-item">L <b className="red">{formatPrice((hovered || current).low)}</b></span>
              <span className="fs-compare-ohlc-item">C <b className={(hovered || current).close >= (hovered || current).open ? 'green' : 'red'}>{formatPrice((hovered || current).close)}</b></span>
              <span className={`fs-compare-ohlc-change ${changePct >= 0 ? 'green' : 'red'}`}>
                {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
              </span>
              <span className="fs-compare-ohlc-divider">|</span>
              <span className="fs-compare-ohlc-sym" style={{color: '#f59e0b'}}>{compareSymbol}</span>
              <span className="fs-compare-ohlc-item">O <b>{formatPrice(compareData[hoveredIndex || compareData.length-1]?.open)}</b></span>
              <span className="fs-compare-ohlc-item">H <b className="green">{formatPrice(compareData[hoveredIndex || compareData.length-1]?.high)}</b></span>
              <span className="fs-compare-ohlc-item">L <b className="red">{formatPrice(compareData[hoveredIndex || compareData.length-1]?.low)}</b></span>
              <span className="fs-compare-ohlc-item">C <b>{formatPrice(compareData[hoveredIndex || compareData.length-1]?.close)}</b></span>
              <span className={`fs-compare-ohlc-change ${((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100) >= 0 ? 'green' : 'red'}`}>
                {((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100) >= 0 ? '+' : ''}
                {((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100).toFixed(2)}%
              </span>
              <button 
                className={`fs-compare-expand-btn ${showCompareTechnicals ? 'active' : ''}`}
                onClick={() => setShowCompareTechnicals(!showCompareTechnicals)}
              >
                <ChevronDown size={14} className={showCompareTechnicals ? 'rotated' : ''} />
              </button>
            </div>

            {/* Expandable Full Details Panel */}
            {showCompareTechnicals && (
              <div className="fs-compare-details-panel">
                <div className="fs-compare-details-grid">
                  {/* Left Column - Main Symbol */}
                  <div className="fs-compare-col">
                    <div className="fs-compare-col-header" style={{color: '#667eea'}}>{symbol}</div>
                    <div className="fs-compare-detail-row">
                      <span>Price</span><span>${formatPrice(current?.close)}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>Change</span>
                      <span className={changePct >= 0 ? 'green' : 'red'}>{changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>Volume</span><span>{formatVolume((hovered || current).volume)}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>RSI</span>
                      <span className={rsiData[rsiData.length-1] > 70 ? 'red' : rsiData[rsiData.length-1] < 30 ? 'green' : ''}>
                        {rsiData[rsiData.length-1]?.toFixed(1) || 'N/A'}
                      </span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>MACD</span>
                      <span className={macdData[macdData.length-1]?.histogram > 0 ? 'green' : 'red'}>
                        {macdData[macdData.length-1]?.histogram?.toFixed(2) || 'N/A'}
                      </span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>SMA 20</span><span>${formatPrice(sma20[sma20.length-1])}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>SMA 50</span><span>${formatPrice(sma50[sma50.length-1])}</span>
                    </div>
                    <div className="fs-compare-signal-row">
                      <span>Signal</span>
                      <span className={`signal-badge ${rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 ? 'buy' : rsiData[rsiData.length-1] < 40 ? 'sell' : 'hold'}`}>
                        {rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 ? 'BUY' : rsiData[rsiData.length-1] < 40 ? 'SELL' : 'HOLD'}
                      </span>
                    </div>
                  </div>

                  {/* Right Column - Compare Symbol */}
                  <div className="fs-compare-col">
                    <div className="fs-compare-col-header" style={{color: '#f59e0b'}}>{compareSymbol}</div>
                    <div className="fs-compare-detail-row">
                      <span>Price</span><span>${formatPrice(compareData[compareData.length-1]?.close)}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>Change</span>
                      <span className={((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100) >= 0 ? 'green' : 'red'}>
                        {((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100) >= 0 ? '+' : ''}
                        {((compareData[compareData.length-1]?.close - compareData[0]?.close) / compareData[0]?.close * 100).toFixed(2)}%
                      </span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>Volume</span><span>{formatVolume(compareData[compareData.length-1]?.volume)}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>RSI</span><span>52.3</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>MACD</span><span className="green">0.45</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>SMA 20</span><span>${formatPrice(compareData[compareData.length-1]?.close * 0.98)}</span>
                    </div>
                    <div className="fs-compare-detail-row">
                      <span>SMA 50</span><span>${formatPrice(compareData[compareData.length-1]?.close * 0.95)}</span>
                    </div>
                    <div className="fs-compare-signal-row">
                      <span>Signal</span>
                      <span className="signal-badge wait">WAIT</span>
                    </div>
                  </div>
                </div>

                {/* AI Analysis Button */}
                <button 
                  className={`fs-compare-ai-btn ${showCompareAIAnalysis ? 'active' : ''}`}
                  onClick={() => setShowCompareAIAnalysis(!showCompareAIAnalysis)}
                >
                  <Sparkles size={12} />
                  <span>AI Comparison Analysis</span>
                  <ChevronDown size={12} className={showCompareAIAnalysis ? 'rotated' : ''} />
                </button>

                {/* AI Analysis Explanation */}
                {showCompareAIAnalysis && (
                  <div className="fs-compare-ai-analysis">
                    <div className="fs-ai-analysis-section">
                      <div className="fs-ai-analysis-header">
                        <span className="fs-ai-sym" style={{color: '#667eea'}}>{symbol}</span>
                        <span className={`signal-badge ${rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 ? 'buy' : rsiData[rsiData.length-1] < 40 ? 'sell' : 'hold'}`}>
                          {rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 ? 'BUY' : rsiData[rsiData.length-1] < 40 ? 'SELL' : 'HOLD'}
                        </span>
                      </div>
                      <p className="fs-ai-analysis-text">
                        {rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 
                          ? `${symbol} shows strong bullish momentum. RSI at ${rsiData[rsiData.length-1]?.toFixed(1)} indicates healthy buying pressure without being overbought. MACD histogram is positive (${macdData[macdData.length-1]?.histogram?.toFixed(2)}), confirming upward momentum. Price is trading above both SMA 20 and SMA 50, suggesting a solid uptrend. Consider entering on minor pullbacks.`
                          : rsiData[rsiData.length-1] < 40 
                          ? `${symbol} is showing bearish signals. RSI at ${rsiData[rsiData.length-1]?.toFixed(1)} indicates weakening momentum. MACD histogram is negative, suggesting continued downward pressure. Consider reducing exposure or waiting for a reversal signal before entering.`
                          : `${symbol} is in a consolidation phase. RSI at ${rsiData[rsiData.length-1]?.toFixed(1)} is neutral, indicating indecision. MACD shows mixed signals. Hold current positions but avoid adding until a clear trend emerges.`
                        }
                      </p>
                    </div>
                    
                    <div className="fs-ai-analysis-section">
                      <div className="fs-ai-analysis-header">
                        <span className="fs-ai-sym" style={{color: '#f59e0b'}}>{compareSymbol}</span>
                        <span className="signal-badge wait">WAIT</span>
                      </div>
                      <p className="fs-ai-analysis-text">
                        {compareSymbol} is currently in a wait zone. RSI at 52.3 shows neutral momentum with no clear directional bias. While MACD is slightly positive at 0.45, the signal lacks conviction. The price action suggests consolidation. Wait for a breakout above resistance or a pullback to key support levels before initiating a position.
                      </p>
                    </div>

                    <div className="fs-ai-verdict">
                      <div className="fs-ai-verdict-header">
                        <Award size={14} />
                        <span>Verdict</span>
                      </div>
                      <p className="fs-ai-verdict-text">
                        Based on technical analysis, <b style={{color: '#667eea'}}>{symbol}</b> presents a stronger opportunity compared to <b style={{color: '#f59e0b'}}>{compareSymbol}</b>. 
                        {rsiData[rsiData.length-1] > 50 && macdData[macdData.length-1]?.histogram > 0 
                          ? ` ${symbol} has clearer bullish momentum with confirming indicators. Consider allocating more weight to ${symbol} in your portfolio.`
                          : ` However, both securities show mixed signals. Consider waiting for clearer setups or diversifying between both positions.`
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pattern Detection Panel - triggered from header button */}
        {showPatterns && patterns.length > 0 && (
          <div className="fs-patterns-container">
            <div className="fs-patterns-panel">
              {patterns.map((pattern, idx) => (
                <div key={idx} className={`fs-pattern-badge ${pattern.type.includes('bullish') ? 'bullish' : pattern.type.includes('bearish') ? 'bearish' : 'neutral'}`}>
                  {pattern.type.includes('bullish') ? <TrendingUp size={12} /> : pattern.type.includes('bearish') ? <TrendingDown size={12} /> : <Minus size={12} />}
                  <span>{pattern.name}</span>
                  <span className="confidence">{Math.round(pattern.confidence * 100)}%</span>
                  <div className="fs-pattern-tooltip">
                    <div className="tooltip-title">{pattern.name}</div>
                    <div className="tooltip-desc">{pattern.description}</div>
                    {pattern.significance && <div className="tooltip-sig"><strong>Significance:</strong> {pattern.significance}</div>}
                    <div className="tooltip-confidence">
                      <span>Confidence:</span>
                      <div className="tooltip-bar">
                        <div className="tooltip-bar-fill" style={{ width: `${Math.round(pattern.confidence * 100)}%` }} />
                      </div>
                      <span>{Math.round(pattern.confidence * 100)}%</span>
                    </div>
                  </div>
                </div>
              ))}
              <button className="fs-patterns-view-all" onClick={() => setShowPatternLibrary(true)}>
                <BookOpen size={12} />
                <span>View All Patterns</span>
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
          
        {showPatterns && patterns.length === 0 && (
          <div className="fs-patterns-container">
            <div className="fs-patterns-empty">
              <span>No patterns detected</span>
              <button className="fs-patterns-view-all-link" onClick={() => setShowPatternLibrary(true)}>
                Learn about patterns →
              </button>
            </div>
          </div>
        )}

        {/* Pattern Library Modal */}
        {showPatternLibrary && (
          <PatternLibraryModal 
            onClose={() => setShowPatternLibrary(false)}
            detectedPatterns={patterns}
          />
        )}

        {/* Smart Money Flow Modal */}
        <SmartMoneyModal
          isOpen={showSmartMoney}
          onClose={() => setShowSmartMoney(false)}
          data={data}
          symbol={symbol}
        />

        {/* Time Machine Modal */}
        <TimeMachineModal
          isOpen={showTimeMachine}
          onClose={() => setShowTimeMachine(false)}
          data={data}
          patterns={patterns}
          symbol={symbol}
        />

        {/* Scenario Simulator Modal */}
        <ScenarioModal
          isOpen={showScenario}
          onClose={() => setShowScenario(false)}
          data={data}
          symbol={symbol}
          companyName={companyName}
        />

        {/* Context Menu */}
        {contextMenu && (
          <div 
            className="fs-context-menu"
            style={{ 
              left: contextMenu.x, 
              top: contextMenu.y,
              position: 'fixed',
              zIndex: 99999,
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => handleSetAlert(contextMenu.price)}>
              <Bell size={14} /> Set Alert at ${formatPrice(contextMenu.price)}
            </button>
            <button onClick={handleAddToWatchlist}>
              <Star size={14} /> Add {symbol} to Watchlist
            </button>
            <button onClick={() => handleCopyPrice(contextMenu.price)}>
              <Copy size={14} /> Copy Price
            </button>
            <div className="fs-context-divider" />
            <button onClick={() => { setSelectedTool('measure'); setContextMenu(null); }}>
              <Ruler size={14} /> Measure
            </button>
            <button onClick={() => { setSelectedTool('hline'); setContextMenu(null); }}>
              <Minus size={14} /> Draw Horizontal Line
            </button>
            {contextMenu?.drawingId && (
              <>
                <div className="fs-context-divider" />
                <button 
                  onClick={() => { 
                    setDrawingsWithHistory(prev => prev.filter(d => d.id !== contextMenu.drawingId)); 
                    setContextMenu(null); 
                  }}
                  className="fs-context-delete"
                >
                  <Trash2 size={14} /> Delete Drawing
                </button>
              </>
            )}
            <div className="fs-context-divider" />
            <button onClick={() => { setShowRSI(!showRSI); setContextMenu(null); }}>
              <LineChart size={14} /> {showRSI ? 'Hide' : 'Show'} RSI
            </button>
            <button onClick={() => { setShowMACD(!showMACD); setContextMenu(null); }}>
              <BarChart size={14} /> {showMACD ? 'Hide' : 'Show'} MACD
            </button>
            <button onClick={() => { setShowSupportResistance(!showSupportResistance); setContextMenu(null); }}>
              <Layers2 size={14} /> {showSupportResistance ? 'Hide' : 'Show'} S/R Levels
            </button>
            <button onClick={() => { setShowVolumeProfile(!showVolumeProfile); setContextMenu(null); }}>
              <BarChart2 size={14} /> {showVolumeProfile ? 'Hide' : 'Show'} Volume Profile
            </button>
          </div>
        )}

        {/* Right Panel - TradingView Style */}
        <div className="fs-right-panel" onClick={e => e.stopPropagation()}>
          
          {/* Watchlist Header with Controls */}
          <div className="fs-tv-wl-header">
            <div className="fs-tv-wl-title">
              <span>Watchlist</span>
              {watchlistLoading && <RotateCcw size={12} className="spinning" />}
              <ChevronDown size={14} />
            </div>
            <div className="fs-tv-wl-actions">
              <button 
                className="fs-tv-action-btn" 
                title="Add Symbol"
                onClick={() => setShowSymbolSearch(true)}
              >
                <Plus size={14} />
              </button>
              <button 
                className="fs-tv-action-btn" 
                title="Compare"
                onClick={() => setShowCompareDropdown(true)}
              >
                <GitCompare size={14} />
              </button>
              <button 
                className={`fs-tv-action-btn ${watchlistLoading ? 'loading' : ''}`}
                title="Refresh Data"
                onClick={() => refreshWatchlistData()}
                disabled={watchlistLoading}
              >
                <RotateCcw size={14} className={watchlistLoading ? 'spinning' : ''} />
              </button>
            </div>
          </div>

          {/* Watchlist Column Headers */}
          <div className="fs-tv-wl-cols">
            <span className="col-symbol">Symbol</span>
            <span className="col-last">Last</span>
            <span className="col-chg">Chg</span>
            <span className="col-chgp">Chg%</span>
          </div>

          {/* Watchlist Scrollable Content */}
          <div className="fs-tv-wl-content">
            {/* Indices */}
            <div className="fs-tv-wl-group">
              <div className="fs-tv-wl-group-header">
                <ChevronDown size={10} />
                <span>INDICES</span>
              </div>
              {watchlist.filter(w => w.type === 'index').map(item => (
                <div 
                  key={item.symbol}
                  className={`fs-tv-wl-row ${item.symbol === symbol ? 'active' : ''}`}
                  onClick={() => handleSymbolChange(item.symbol)}
                >
                  <div className="fs-tv-wl-sym">
                    <span className="ticker">{item.symbol}</span>
                  </div>
                  <span className={`fs-tv-wl-last ${item.changePct >= 0 ? 'up' : 'down'}`}>{item.price.toLocaleString()}</span>
                  <span className={`fs-tv-wl-chg ${item.change >= 0 ? 'up' : 'down'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}
                  </span>
                  <span className={`fs-tv-wl-chgp ${item.changePct >= 0 ? 'up' : 'down'}`}>
                    {item.changePct >= 0 ? '+' : ''}{item.changePct.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>

            {/* Stocks */}
            <div className="fs-tv-wl-group">
              <div className="fs-tv-wl-group-header">
                <ChevronDown size={10} />
                <span>STOCKS</span>
                <button className="fs-tv-group-add" title="Add to group" onClick={() => setShowSymbolSearch(true)}><Plus size={10} /></button>
              </div>
              {watchlist.filter(w => w.type === 'stock').map(item => (
                <div 
                  key={item.symbol}
                  className={`fs-tv-wl-row ${item.symbol === symbol ? 'active' : ''}`}
                  onClick={() => handleSymbolChange(item.symbol)}
                >
                  <div className="fs-tv-wl-sym">
                    <span className="ticker">{item.symbol}</span>
                  </div>
                  <span className={`fs-tv-wl-last ${item.changePct >= 0 ? 'up' : 'down'}`}>{item.price.toLocaleString()}</span>
                  <span className={`fs-tv-wl-chg ${item.change >= 0 ? 'up' : 'down'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}
                  </span>
                  <span className={`fs-tv-wl-chgp ${item.changePct >= 0 ? 'up' : 'down'}`}>
                    {item.changePct >= 0 ? '+' : ''}{item.changePct.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>

            {/* Futures */}
            <div className="fs-tv-wl-group">
              <div className="fs-tv-wl-group-header">
                <ChevronDown size={10} />
                <span>FUTURES</span>
              </div>
              {watchlist.filter(w => w.type === 'futures').map(item => (
                <div 
                  key={item.symbol}
                  className={`fs-tv-wl-row ${item.symbol === symbol ? 'active' : ''}`}
                  onClick={() => handleSymbolChange(item.symbol)}
                >
                  <div className="fs-tv-wl-sym">
                    <span className="ticker">{item.symbol}</span>
                  </div>
                  <span className={`fs-tv-wl-last ${item.changePct >= 0 ? 'up' : 'down'}`}>{item.price.toLocaleString()}</span>
                  <span className={`fs-tv-wl-chg ${item.change >= 0 ? 'up' : 'down'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}
                  </span>
                  <span className={`fs-tv-wl-chgp ${item.changePct >= 0 ? 'up' : 'down'}`}>
                    {item.changePct >= 0 ? '+' : ''}{item.changePct.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Symbol Details Section */}
          <div className="fs-tv-symbol-section">
            <div className="fs-tv-symbol-header">
              <div className="fs-tv-symbol-main">
                <span className="fs-tv-logo-lg" style={{ background: change >= 0 ? '#26a69a' : '#ef5350' }}>
                  {symbol.charAt(0)}
                </span>
                <span className="fs-tv-symbol-ticker">{symbol}</span>
              </div>
              <div className="fs-tv-symbol-actions">
                <button title="Screener" onClick={() => window.open(`https://finviz.com/screener.ashx?v=111&t=${symbol}`, '_blank')}><Hash size={14} /></button>
                <button title="Financials" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/financials`, '_blank')}><FileText size={14} /></button>
                <button title="More" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}`, '_blank')}><MoreHorizontal size={14} /></button>
              </div>
            </div>
            
            <div className="fs-tv-symbol-info">
              <span className="name">{companyName}</span>
              <ExternalLink size={10} />
              <span className="exchange">• NASDAQ</span>
            </div>
            <div className="fs-tv-symbol-sector">Electronic Technology • Semiconductors</div>
            
            <div className="fs-tv-symbol-price">
              <span className="price">{formatPrice(current?.close || 0)}</span>
              <span className="currency">USD</span>
              <span className={`change ${change >= 0 ? 'up' : 'down'}`}>
                {change >= 0 ? '+' : ''}{formatPrice(change)} {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
              </span>
            </div>
            <div className="fs-tv-market-status">
              <span className="status-dot open" />
              <span>Market open</span>
            </div>

            {/* AI Insight Snippet */}
            <div className="fs-tv-ai-insight" onClick={() => setShowAIAnalysis(true)} style={{cursor: 'pointer'}}>
              <Sparkles size={12} />
              <span>Strong momentum with {Math.abs(changePct).toFixed(1)}% move. RSI indicates {changePct > 0 ? 'bullish' : 'neutral'} conditions.</span>
              <ChevronRight size={12} />
            </div>
          </div>

          {/* Key Stats - TradingView Style */}
          <div className="fs-tv-stats-section">
            <div className="fs-tv-stats-title">Key stats</div>
            <div className="fs-tv-stats-grid">
              <div className="fs-tv-stat-row">
                <span className="label">Next earnings report</span>
                <span className="value">In 71 days</span>
              </div>
              <div className="fs-tv-stat-row">
                <span className="label">Volume</span>
                <span className="value">{formatVolume(current?.volume || 0)}</span>
              </div>
              <div className="fs-tv-stat-row">
                <span className="label">Average Volume (30D)</span>
                <span className="value">{formatVolume(maxVolume * 0.8)}</span>
              </div>
              <div className="fs-tv-stat-row">
                <span className="label">Market capitalization</span>
                <span className="value">{fundamentals?.mktCap || '---'}B</span>
              </div>
            </div>

            {/* Expandable Fundamentals */}
            <div 
              className={`fs-tv-expand-header ${collapsedSections.fundamentals ? '' : 'expanded'}`}
              onClick={() => toggleSection('fundamentals')}
            >
              <span>Fundamentals</span>
              <ChevronDown size={14} className={collapsedSections.fundamentals ? '' : 'rotated'} />
            </div>
            
            {!collapsedSections.fundamentals && fundamentals && (
              <div className="fs-tv-fund-grid">
                <div className="fs-tv-stat-row">
                  <span className="label">P/E Ratio (TTM)</span>
                  <span className="value">{fundamentals.peRatio}</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">EPS (TTM)</span>
                  <span className="value">${fundamentals.eps}</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">Revenue (TTM)</span>
                  <span className="value">{fundamentals.revenue}B</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">Dividend Yield</span>
                  <span className="value">{fundamentals.divYield}%</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">Beta</span>
                  <span className="value">{fundamentals.beta}</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">52W High</span>
                  <span className="value up">${fundamentals.high52w}</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">52W Low</span>
                  <span className="value down">${fundamentals.low52w}</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">ROE</span>
                  <span className="value">{fundamentals.roe}%</span>
                </div>
                <div className="fs-tv-stat-row">
                  <span className="label">Profit Margin</span>
                  <span className="value">{fundamentals.profitMargin}%</span>
                </div>
              </div>
            )}

            {/* Earnings Section with Chart */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header">
                <span>Earnings</span>
                <span className="fs-tv-widget-badge">71</span>
              </div>
              <div className="fs-tv-earnings-chart">
                <div className="fs-tv-earnings-bars">
                  <div className="bar-group">
                    <div className="bar estimate" style={{height: '45%'}}></div>
                    <div className="bar actual" style={{height: '50%'}}></div>
                  </div>
                  <div className="bar-group">
                    <div className="bar estimate" style={{height: '55%'}}></div>
                    <div className="bar actual" style={{height: '60%'}}></div>
                  </div>
                  <div className="bar-group">
                    <div className="bar estimate" style={{height: '65%'}}></div>
                    <div className="bar actual" style={{height: '75%'}}></div>
                  </div>
                  <div className="bar-group">
                    <div className="bar estimate" style={{height: '70%'}}></div>
                    <div className="bar actual" style={{height: '85%'}}></div>
                  </div>
                </div>
                <div className="fs-tv-earnings-labels">
                  <span>Q4'24</span><span>Q1'25</span><span>Q2'25</span><span>Q3'25</span>
                </div>
                <div className="fs-tv-earnings-legend">
                  <span><i className="dot actual"></i>Actual</span>
                  <span><i className="dot estimate"></i>Estimate</span>
                </div>
              </div>
            </div>

            {/* Dividends Section */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header">
                <span>Dividends</span>
              </div>
              <div className="fs-tv-dividend-chart">
                <svg viewBox="0 0 100 100" className="fs-tv-pie">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#26a69a" strokeWidth="12" 
                    strokeDasharray="251.2" strokeDashoffset="248" transform="rotate(-90 50 50)"/>
                  <text x="50" y="50" textAnchor="middle" dy="4" fill="#26a69a" fontSize="12" fontWeight="600">0.99%</text>
                </svg>
                <div className="fs-tv-dividend-legend">
                  <span><i className="dot retained"></i>Earnings retained</span>
                  <span><i className="dot payout"></i>Payout ratio (TTM)</span>
                </div>
              </div>
              <div className="fs-tv-dividend-stats">
                <div className="fs-tv-stat-row"><span className="label">Dividend yield TTM</span><span className="value">0.02%</span></div>
                <div className="fs-tv-stat-row"><span className="label">Last payment</span><span className="value">0.01</span></div>
                <div className="fs-tv-stat-row"><span className="label">Last ex-dividend date</span><span className="value">Dec 4, 2025</span></div>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}`, '_blank')}>More info</button>
            </div>

            {/* Income Statement */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header">
                <span>Income statement</span>
                <div className="fs-tv-toggle-btns">
                  <button className="active">Annual</button>
                  <button>Quarterly</button>
                </div>
              </div>
              <div className="fs-tv-income-chart">
                <div className="fs-tv-income-bars">
                  {[35, 42, 48, 55, 60].map((h, i) => (
                    <div key={i} className="bar-stack">
                      <div className="bar revenue" style={{height: `${h}%`}}></div>
                      <div className="bar income" style={{height: `${h * 0.4}%`}}></div>
                    </div>
                  ))}
                </div>
                <div className="fs-tv-income-labels">
                  <span>Q3'24</span><span>Q4'24</span><span>Q1'25</span><span>Q2'25</span><span>Q3'25</span>
                </div>
                <div className="fs-tv-income-legend">
                  <span><i className="dot revenue"></i>Revenue</span>
                  <span><i className="dot income"></i>Net income</span>
                  <span><i className="line margin"></i>Net margin %</span>
                </div>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/financials`, '_blank')}>More financials</button>
            </div>

            {/* Performance */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Performance</span></div>
              <div className="fs-tv-perf-grid">
                <div className="fs-tv-perf-box down"><span className="val">-4.68%</span><span className="period">1W</span></div>
                <div className="fs-tv-perf-box down"><span className="val">-3.28%</span><span className="period">1M</span></div>
                <div className="fs-tv-perf-box up"><span className="val">2.45%</span><span className="period">3M</span></div>
                <div className="fs-tv-perf-box up"><span className="val">22.82%</span><span className="period">6M</span></div>
                <div className="fs-tv-perf-box up"><span className="val">30.05%</span><span className="period">YTD</span></div>
                <div className="fs-tv-perf-box up"><span className="val">31.82%</span><span className="period">1Y</span></div>
              </div>
            </div>

            {/* Seasonals */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Seasonals</span></div>
              <div className="fs-tv-seasonals-chart">
                <svg viewBox="0 0 200 60" className="fs-tv-seasonals-svg">
                  <polyline fill="none" stroke="#2962ff" strokeWidth="1.5" points="10,45 40,40 70,30 100,25 130,35 160,30 190,20"/>
                  <polyline fill="none" stroke="#26a69a" strokeWidth="1.5" points="10,50 40,45 70,35 100,40 130,38 160,42 190,35"/>
                  <polyline fill="none" stroke="#ef5350" strokeWidth="1.5" points="10,48 40,42 70,38 100,32 130,40 160,38 190,30"/>
                </svg>
                <div className="fs-tv-seasonals-labels"><span>Jan</span><span>May</span><span>Sep</span></div>
                <div className="fs-tv-seasonals-legend">
                  <span><i style={{background:'#2962ff'}}></i>2025</span>
                  <span><i style={{background:'#26a69a'}}></i>2024</span>
                  <span><i style={{background:'#ef5350'}}></i>2023</span>
                </div>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/history`, '_blank')}>More seasonals</button>
            </div>

            {/* Technicals Gauge */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Technicals</span></div>
              <div className="fs-tv-gauge">
                <svg viewBox="0 0 120 70" className="fs-tv-gauge-svg">
                  <path d="M 10 60 A 50 50 0 0 1 110 60" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" strokeLinecap="round"/>
                  <path d="M 10 60 A 50 50 0 0 1 110 60" fill="none" stroke="url(#gaugeGrad)" strokeWidth="8" strokeLinecap="round" strokeDasharray="157" strokeDashoffset="80"/>
                  <defs><linearGradient id="gaugeGrad"><stop offset="0%" stopColor="#ef5350"/><stop offset="50%" stopColor="#787b86"/><stop offset="100%" stopColor="#26a69a"/></linearGradient></defs>
                  <line x1="60" y1="60" x2="45" y2="25" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="60" cy="60" r="4" fill="#fff"/>
                </svg>
                <div className="fs-tv-gauge-labels">
                  <span>Strong<br/>sell</span>
                  <span>Sell</span>
                  <span>Neutral</span>
                  <span>Buy</span>
                  <span>Strong<br/>buy</span>
                </div>
                <div className="fs-tv-gauge-value">Sell</div>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/analysis`, '_blank')}>More technicals</button>
            </div>

            {/* Analyst Rating Gauge */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Analyst rating</span></div>
              <div className="fs-tv-gauge">
                <svg viewBox="0 0 120 70" className="fs-tv-gauge-svg">
                  <path d="M 10 60 A 50 50 0 0 1 110 60" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" strokeLinecap="round"/>
                  <path d="M 10 60 A 50 50 0 0 1 110 60" fill="none" stroke="url(#gaugeGrad)" strokeWidth="8" strokeLinecap="round" strokeDasharray="157" strokeDashoffset="20"/>
                  <line x1="60" y1="60" x2="95" y2="30" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="60" cy="60" r="4" fill="#fff"/>
                </svg>
                <div className="fs-tv-gauge-value up">Strong buy</div>
              </div>
              <div className="fs-tv-stat-row">
                <span className="label">1 year price target</span>
                <span className="value">256.95 <span className="up">(44.93%)</span></span>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/analysis`, '_blank')}>See forecast</button>
            </div>

            {/* Highest YTM Bonds */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Highest YTM bonds</span></div>
              <div className="fs-tv-bonds-list">
                <div className="fs-tv-stat-row"><span className="label">Apr 1, 2060</span><span className="value">5.44%</span></div>
                <div className="fs-tv-stat-row"><span className="label">Apr 1, 2050</span><span className="value">5.42%</span></div>
                <div className="fs-tv-stat-row"><span className="label">Apr 1, 2040</span><span className="value">5.05%</span></div>
              </div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://www.wsj.com/market-data/bonds`, '_blank')}>More bonds</button>
            </div>

            {/* Volatility Curve */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header">
                <span>Volatility curve (31 days)</span>
              </div>
              <div className="fs-tv-vol-chart">
                <svg viewBox="0 0 200 80" className="fs-tv-vol-svg">
                  <polyline fill="none" stroke="#2962ff" strokeWidth="2" points="10,70 50,65 90,50 130,30 170,15 190,10"/>
                  <circle cx="170" cy="15" r="4" fill="#2962ff"/>
                </svg>
                <div className="fs-tv-vol-tooltip">199.97%</div>
              </div>
              <div className="fs-tv-vol-labels"><span>0.5</span><span>155</span><span>1110</span></div>
              <button className="fs-tv-more-btn" onClick={() => window.open(`https://finance.yahoo.com/quote/${symbol}/options`, '_blank')}>More on options</button>
            </div>

            {/* Company Profile */}
            <div className="fs-tv-widget-section">
              <div className="fs-tv-widget-header"><span>Profile</span></div>
              <div className="fs-tv-profile-stats">
                <div className="fs-tv-stat-row">
                  <span className="label">Website</span>
                  <span className="value link" onClick={() => window.open(`https://www.google.com/search?q=${symbol}+investor+relations`, '_blank')} style={{cursor: 'pointer'}}>
                    {symbol.toLowerCase()}.com <ExternalLink size={10}/>
                  </span>
                </div>
                <div className="fs-tv-stat-row"><span className="label">Employees (FY)</span><span className="value">36 K</span></div>
                <div className="fs-tv-stat-row">
                  <span className="label">ISIN</span>
                  <span className="value" style={{cursor: 'pointer'}} onClick={() => navigator.clipboard.writeText('US67066G1040')}>
                    US67066G1040 <Copy size={10}/>
                  </span>
                </div>
                <div className="fs-tv-stat-row"><span className="label">CUSIP</span><span className="value">67066G104</span></div>
              </div>
              <p className="fs-tv-profile-desc">
                NVIDIA Corp. engages in the design and manufacture of computer graphics processors, chipsets, and related multimedia software...
              </p>
            </div>
          </div>
        </div>

        {/* Text Input Popup */}
        {showTextInput && textInputPos && (
          <div 
            className="fs-text-input-popup"
            style={{
              position: 'absolute',
              left: textInputPos.x,
              top: textInputPos.y + HEADER_HEIGHT - 15,
              zIndex: 1000,
            }}
            onClick={e => e.stopPropagation()}
          >
            <textarea
              ref={textInputRef}
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              onKeyDown={handleTextKeyDown}
              onBlur={handleTextSubmit}
              placeholder="Enter text..."
              className="fs-text-input"
              rows={1}
            />
          </div>
        )}

        {/* Indicators Modal */}
        <IndicatorsModal 
          isOpen={showIndicatorsModal} 
          onClose={() => setShowIndicatorsModal(false)} 
          indicators={indicators} 
          setIndicators={setIndicators} 
          isPro={isPro}
          symbol={symbol}
          onOpenAI={() => { setShowAIAnalysis(true); runAIAnalysis(); }}
        />

        {/* Draggable AI Chat Window */}
        {showAIAnalysis && (
          <DraggableAIChat 
            symbol={symbol}
            companyName={companyName}
            data={data}
            aiAnalysisLoading={aiAnalysisLoading}
            aiAnalysisResult={aiAnalysisResult}
            onClose={() => setShowAIAnalysis(false)}
            onRefresh={runAIAnalysis}
            patterns={patterns}
            rsiData={rsiData}
            macdData={macdData}
            supportResistance={supportResistance}
          />
        )}
        
        {/* Keyboard Shortcuts Modal */}
        {showKeyboardShortcuts && (
          <div className="keyboard-shortcuts-overlay" onClick={() => setShowKeyboardShortcuts(false)}>
            <div className="keyboard-shortcuts-modal" onClick={e => e.stopPropagation()}>
              <div className="ks-header">
                <Keyboard size={20} />
                <h3>Keyboard Shortcuts</h3>
                <button onClick={() => setShowKeyboardShortcuts(false)}><X size={18} /></button>
              </div>
              <div className="ks-content">
                <div className="ks-section">
                  <h4>Layout</h4>
                  <div className="ks-row"><kbd>1</kbd><span>Single Chart</span></div>
                  <div className="ks-row"><kbd>2</kbd><span>2 Charts</span></div>
                  <div className="ks-row"><kbd>3</kbd><span>3 Charts</span></div>
                  <div className="ks-row"><kbd>4</kbd><span>4 Grid</span></div>
                </div>
                <div className="ks-section">
                  <h4>Chart Type</h4>
                  <div className="ks-row"><kbd>C</kbd><span>Candlestick</span></div>
                  <div className="ks-row"><kbd>A</kbd><span>Area Chart</span></div>
                </div>
                <div className="ks-section">
                  <h4>Timeframe</h4>
                  <div className="ks-row"><kbd>D</kbd><span>1 Day</span></div>
                  <div className="ks-row"><kbd>W</kbd><span>1 Week</span></div>
                  <div className="ks-row"><kbd>M</kbd><span>1 Month</span></div>
                  <div className="ks-row"><kbd>Q</kbd><span>3 Months</span></div>
                  <div className="ks-row"><kbd>Y</kbd><span>1 Year</span></div>
                </div>
                <div className="ks-section">
                  <h4>Tools</h4>
                  <div className="ks-row"><kbd>X</kbd><span>Crosshair</span></div>
                  <div className="ks-row"><kbd>P</kbd><span>Pointer</span></div>
                  <div className="ks-row"><kbd>L</kbd><span>Line</span></div>
                  <div className="ks-row"><kbd>H</kbd><span>Horizontal Line</span></div>
                  <div className="ks-row"><kbd>R</kbd><span>Rectangle</span></div>
                </div>
                <div className="ks-section">
                  <h4>Features</h4>
                  <div className="ks-row"><kbd>I</kbd><span>Indicators</span></div>
                  <div className="ks-row"><kbd>S</kbd><span>AI Analysis</span></div>
                  <div className="ks-row"><kbd>?</kbd><span>This Menu</span></div>
                  <div className="ks-row"><kbd>Esc</kbd><span>Close Modal</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Onboarding Tour */}
        {showOnboarding && (() => {
          const currentStep = onboardingSteps[onboardingStep];
          const hasSpotlight = spotlightRect && currentStep?.selector;
          
          // Calculate tooltip position based on spotlight and position preference
          const getTooltipStyle = () => {
            // During transition or no spotlight, center the tooltip
            if (isTransitioning || !hasSpotlight) {
              return {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              };
            }
            
            const padding = 20;
            const tooltipWidth = 360;
            const tooltipHeight = 220;
            const pos = currentStep.position || 'bottom';
            
            let style = { position: 'fixed' };
            
            switch (pos) {
              case 'right':
                style.left = Math.min(spotlightRect.left + spotlightRect.width + padding, window.innerWidth - tooltipWidth - 20);
                style.top = Math.max(20, Math.min(spotlightRect.top, window.innerHeight - tooltipHeight - 20));
                break;
              case 'left':
                style.left = Math.max(20, spotlightRect.left - tooltipWidth - padding);
                style.top = Math.max(20, Math.min(spotlightRect.top, window.innerHeight - tooltipHeight - 20));
                break;
              case 'top':
                style.left = Math.max(20, Math.min(spotlightRect.left, window.innerWidth - tooltipWidth - 20));
                style.top = Math.max(20, spotlightRect.top - tooltipHeight - padding);
                break;
              case 'bottom':
              default:
                style.left = Math.max(20, Math.min(spotlightRect.left, window.innerWidth - tooltipWidth - 20));
                style.top = Math.min(spotlightRect.top + spotlightRect.height + padding, window.innerHeight - tooltipHeight - 20);
                break;
            }
            
            return style;
          };
          
          return (
            <div 
              style={{ 
                position: 'fixed', 
                inset: 0, 
                zIndex: 99999, 
                pointerEvents: 'none',
                opacity: isTransitioning ? 0 : 1,
                transition: 'opacity 0.15s ease-out'
              }}
            >
              {/* SVG Mask for spotlight effect */}
              <svg 
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                preserveAspectRatio="none"
              >
                <defs>
                  <mask id="spotlight-mask">
                    {/* White = visible, Black = hidden */}
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    {hasSpotlight && (
                      <rect 
                        x={spotlightRect.left} 
                        y={spotlightRect.top} 
                        width={spotlightRect.width} 
                        height={spotlightRect.height}
                        rx="8"
                        fill="black"
                      />
                    )}
                  </mask>
                </defs>
                {/* Dark overlay with hole cut out */}
                <rect 
                  x="0" 
                  y="0" 
                  width="100%" 
                  height="100%" 
                  fill="rgba(0,0,0,0.85)" 
                  mask="url(#spotlight-mask)"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => { e.stopPropagation(); }}
                />
              </svg>
              
              {/* Spotlight highlight border */}
              {hasSpotlight && (
                <div 
                  style={{
                    position: 'absolute',
                    left: spotlightRect.left,
                    top: spotlightRect.top,
                    width: spotlightRect.width,
                    height: spotlightRect.height,
                    borderRadius: '8px',
                    border: '2px solid #667eea',
                    boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.3), 0 0 30px rgba(102, 126, 234, 0.4)',
                    animation: 'spotlightPulse 2s ease-in-out infinite',
                    pointerEvents: 'none'
                  }}
                />
              )}
              
              {/* Tooltip */}
              <div 
                style={{
                  ...getTooltipStyle(),
                  background: 'linear-gradient(145deg, #1e1e2e 0%, #12121a 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.4)',
                  borderRadius: '16px',
                  padding: '24px',
                  width: '360px',
                  maxWidth: 'calc(100vw - 40px)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 30px rgba(102, 126, 234, 0.15)',
                  color: '#fff',
                  pointerEvents: 'auto',
                  zIndex: 100000
                }}
              >
                {/* Progress dots */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
                  {onboardingSteps.map((_, idx) => (
                    <div 
                      key={idx}
                      style={{
                        flex: 1,
                        height: '4px',
                        borderRadius: '2px',
                        background: idx <= onboardingStep 
                          ? 'linear-gradient(90deg, #667eea, #764ba2)' 
                          : 'rgba(255,255,255,0.1)',
                        transition: 'background 0.3s ease'
                      }}
                    />
                  ))}
                </div>
                
                {/* Step indicator */}
                <div style={{ 
                  fontSize: '11px', 
                  color: '#667eea', 
                  marginBottom: '8px',
                  fontWeight: 600,
                  letterSpacing: '1px'
                }}>
                  STEP {onboardingStep + 1} OF {onboardingSteps.length}
                </div>
                
                {/* Title */}
                <h2 style={{ 
                  fontSize: '22px', 
                  fontWeight: 700, 
                  margin: '0 0 10px 0',
                  background: 'linear-gradient(135deg, #fff 0%, #ccc 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {currentStep?.title}
                </h2>
                
                {/* Description */}
                <p style={{ 
                  fontSize: '14px', 
                  color: 'rgba(255,255,255,0.7)', 
                  lineHeight: 1.6,
                  margin: '0 0 24px 0'
                }}>
                  {currentStep?.description}
                </p>
                
                {/* Buttons */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  <button
                    onClick={completeOnboarding}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      padding: '8px 0',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.color = 'rgba(255,255,255,0.8)'}
                    onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.5)'}
                  >
                    Skip Tour
                  </button>
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {onboardingStep > 0 && (
                      <button
                        onClick={prevStep}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: '#fff',
                          padding: '10px 18px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                      >
                        Back
                      </button>
                    )}
                    
                    <button
                      onClick={nextStep}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        color: '#fff',
                        padding: '10px 22px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      {onboardingStep < onboardingSteps.length - 1 ? 'Next' : 'Get Started'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
});

// ============================================
// AI ANALYSIS DATA CONTEXT (Ready for real APIs)
// ============================================
const generateAIContext = (symbol, companyName, data, patterns, rsiData, macdData, supportResistance) => {
  if (!data || data.length === 0) return null;
  
  const current = data[data.length - 1];
  const prev = data[data.length - 2];
  const weekAgo = data[Math.max(0, data.length - 5)];
  const monthAgo = data[Math.max(0, data.length - 22)];
  
  const currentRSI = rsiData[rsiData.length - 1];
  const currentMACD = macdData.macdLine[macdData.macdLine.length - 1];
  const currentSignal = macdData.signalLine[macdData.signalLine.length - 1];
  const currentHistogram = macdData.histogram[macdData.histogram.length - 1];
  
  // Calculate price changes
  const dailyChange = prev ? ((current.close - prev.close) / prev.close * 100) : 0;
  const weeklyChange = weekAgo ? ((current.close - weekAgo.close) / weekAgo.close * 100) : 0;
  const monthlyChange = monthAgo ? ((current.close - monthAgo.close) / monthAgo.close * 100) : 0;
  
  // Simulated news data (ready for API integration)
  const newsData = [
    { title: `${symbol} Reports Strong Q4 Earnings Beat`, sentiment: 'positive', time: '2 hours ago', source: 'Reuters' },
    { title: `Analysts Upgrade ${symbol} Price Target to $${(current.close * 1.15).toFixed(0)}`, sentiment: 'positive', time: '5 hours ago', source: 'Bloomberg' },
    { title: `${symbol} Announces New Product Launch`, sentiment: 'neutral', time: '1 day ago', source: 'CNBC' },
    { title: `Market Volatility Impacts Tech Sector Including ${symbol}`, sentiment: 'negative', time: '2 days ago', source: 'WSJ' },
  ];
  
  // Simulated economic data (ready for API integration)
  const economicData = {
    fedRate: 5.25,
    inflation: 3.2,
    unemployment: 3.7,
    gdpGrowth: 2.8,
    vix: 16.5,
    tenYearYield: 4.25,
    dollarIndex: 104.2,
    sectorPerformance: {
      technology: 2.3,
      healthcare: -0.5,
      financials: 1.1,
      energy: -1.2,
      consumer: 0.8
    }
  };
  
  // Fundamental data (ready for API integration)
  const fundamentals = {
    marketCap: '2.8T',
    peRatio: 28.5,
    eps: 6.12,
    dividend: 0.96,
    dividendYield: 0.55,
    beta: 1.25,
    fiftyTwoWeekHigh: current.close * 1.2,
    fiftyTwoWeekLow: current.close * 0.7,
    avgVolume: '52.3M',
    institutionalOwnership: 72.5
  };
  
  return {
    symbol,
    companyName,
    price: {
      current: current.close,
      open: current.open,
      high: current.high,
      low: current.low,
      volume: current.volume,
      dailyChange,
      weeklyChange,
      monthlyChange
    },
    technicals: {
      rsi: currentRSI,
      rsiSignal: currentRSI > 70 ? 'overbought' : currentRSI < 30 ? 'oversold' : 'neutral',
      macd: currentMACD,
      macdSignal: currentSignal,
      macdHistogram: currentHistogram,
      macdTrend: currentHistogram > 0 ? 'bullish' : 'bearish',
      support: supportResistance.support.slice(0, 3),
      resistance: supportResistance.resistance.slice(0, 3)
    },
    patterns,
    news: newsData,
    economic: economicData,
    fundamentals
  };
};

// AI Response Generator (Ready for real LLM API integration)
const generateAIResponse = (query, context) => {
  if (!context) return "I'm still loading the market data. Please wait a moment.";
  
  const q = query.toLowerCase();
  const { symbol, price, technicals, patterns, news, economic, fundamentals } = context;
  
  // Pattern-based responses
  if (q.includes('pattern') || q.includes('formation')) {
    if (patterns.length === 0) {
      return `I'm not detecting any significant chart patterns on ${symbol} at the moment. The price action appears to be in a consolidation phase. Keep monitoring for emerging patterns.`;
    }
    const patternList = patterns.map(p => `• **${p.name}** (${Math.round(p.confidence * 100)}% confidence): ${p.description}`).join('\n');
    const bullishCount = patterns.filter(p => p.type.includes('bullish')).length;
    const bearishCount = patterns.filter(p => p.type.includes('bearish')).length;
    const bias = bullishCount > bearishCount ? 'bullish' : bearishCount > bullishCount ? 'bearish' : 'neutral';
    return `**Detected Patterns for ${symbol}:**\n\n${patternList}\n\n**Pattern Bias:** ${bias.toUpperCase()} (${bullishCount} bullish, ${bearishCount} bearish signals)\n\nRemember: Patterns should be confirmed with volume and other indicators.`;
  }
  
  // Support/Resistance responses
  if (q.includes('support') || q.includes('resistance') || q.includes('levels')) {
    const supports = technicals.support.map(s => `$${s.price?.toFixed(2)}`).join(', ') || 'N/A';
    const resistances = technicals.resistance.map(r => `$${r.price?.toFixed(2)}`).join(', ') || 'N/A';
    const nearestSupport = technicals.support[0]?.price;
    const nearestResistance = technicals.resistance[0]?.price;
    const riskReward = nearestSupport && nearestResistance ? 
      ((nearestResistance - price.current) / (price.current - nearestSupport)).toFixed(2) : 'N/A';
    
    return `**Key Levels for ${symbol}:**\n\n**Resistance:** ${resistances}\n**Support:** ${supports}\n**Current Price:** $${price.current.toFixed(2)}\n\n**Risk/Reward Ratio:** ${riskReward}:1\n\nThe nearest support is ${nearestSupport ? `$${nearestSupport.toFixed(2)} (${((price.current - nearestSupport) / price.current * 100).toFixed(1)}% below)` : 'N/A'}.`;
  }
  
  // RSI/Momentum responses
  if (q.includes('rsi') || q.includes('momentum') || q.includes('overbought') || q.includes('oversold')) {
    const rsiValue = technicals.rsi?.toFixed(1) || 'N/A';
    let rsiAnalysis = '';
    if (technicals.rsi > 70) {
      rsiAnalysis = `**Warning:** RSI is in overbought territory. This suggests the stock may be due for a pullback. Consider taking profits or waiting for a better entry.`;
    } else if (technicals.rsi < 30) {
      rsiAnalysis = `**Opportunity:** RSI is in oversold territory. This could present a buying opportunity if fundamentals remain strong.`;
    } else if (technicals.rsi > 50) {
      rsiAnalysis = `RSI shows bullish momentum. The stock is trending upward with room to run before becoming overbought.`;
    } else {
      rsiAnalysis = `RSI shows bearish momentum. The stock is trending downward but not yet oversold.`;
    }
    return `**RSI Analysis for ${symbol}:**\n\nCurrent RSI: **${rsiValue}** (${technicals.rsiSignal})\n\n${rsiAnalysis}`;
  }
  
  // MACD responses
  if (q.includes('macd') || q.includes('crossover')) {
    const trend = technicals.macdTrend;
    const histogram = technicals.macdHistogram?.toFixed(3) || 'N/A';
    return `**MACD Analysis for ${symbol}:**\n\n**Trend:** ${trend.toUpperCase()}\n**Histogram:** ${histogram}\n\n${trend === 'bullish' ? 'The MACD histogram is positive, indicating bullish momentum. Look for continuation patterns.' : 'The MACD histogram is negative, indicating bearish momentum. Consider waiting for a bullish crossover before entering.'}`;
  }
  
  // News responses
  if (q.includes('news') || q.includes('headline') || q.includes('recent')) {
    const newsItems = news.map(n => `• **${n.title}** (${n.source}, ${n.time}) - ${n.sentiment}`).join('\n');
    const sentimentScore = news.filter(n => n.sentiment === 'positive').length - news.filter(n => n.sentiment === 'negative').length;
    const overallSentiment = sentimentScore > 0 ? 'POSITIVE' : sentimentScore < 0 ? 'NEGATIVE' : 'MIXED';
    return `**Recent News for ${symbol}:**\n\n${newsItems}\n\n**Overall Sentiment:** ${overallSentiment}\n\nAlways verify news from multiple sources before making trading decisions.`;
  }
  
  // Economic/Macro responses
  if (q.includes('economic') || q.includes('macro') || q.includes('fed') || q.includes('interest') || q.includes('inflation')) {
    return `**Economic Environment:**\n\n• **Fed Rate:** ${economic.fedRate}%\n• **Inflation:** ${economic.inflation}%\n• **Unemployment:** ${economic.unemployment}%\n• **GDP Growth:** ${economic.gdpGrowth}%\n• **VIX:** ${economic.vix}\n• **10Y Yield:** ${economic.tenYearYield}%\n\n**Impact on ${symbol}:** ${economic.fedRate > 5 ? 'Higher interest rates may pressure growth stocks. Consider the company\'s debt levels and profitability.' : 'Lower rates are generally supportive of equity valuations.'}`;
  }
  
  // Fundamental responses
  if (q.includes('fundamental') || q.includes('valuation') || q.includes('pe') || q.includes('earnings')) {
    return `**Fundamentals for ${symbol}:**\n\n• **Market Cap:** ${fundamentals.marketCap}\n• **P/E Ratio:** ${fundamentals.peRatio}\n• **EPS:** $${fundamentals.eps}\n• **Dividend Yield:** ${fundamentals.dividendYield}%\n• **Beta:** ${fundamentals.beta}\n• **52W Range:** $${fundamentals.fiftyTwoWeekLow.toFixed(2)} - $${fundamentals.fiftyTwoWeekHigh.toFixed(2)}\n• **Institutional Ownership:** ${fundamentals.institutionalOwnership}%\n\n${fundamentals.peRatio > 30 ? 'The P/E ratio is elevated - ensure growth expectations justify the premium.' : 'Valuation appears reasonable based on P/E.'}`;
  }
  
  // Trend analysis responses
  if (q.includes('trend') || q.includes('direction') || q.includes('outlook')) {
    const trendPatterns = patterns.filter(p => p.category === 'trend');
    const trendBias = price.weeklyChange > 0 && price.monthlyChange > 0 ? 'bullish' : 
                      price.weeklyChange < 0 && price.monthlyChange < 0 ? 'bearish' : 'mixed';
    return `**Trend Analysis for ${symbol}:**\n\n**Price Performance:**\n• Daily: ${price.dailyChange >= 0 ? '+' : ''}${price.dailyChange.toFixed(2)}%\n• Weekly: ${price.weeklyChange >= 0 ? '+' : ''}${price.weeklyChange.toFixed(2)}%\n• Monthly: ${price.monthlyChange >= 0 ? '+' : ''}${price.monthlyChange.toFixed(2)}%\n\n**Trend Bias:** ${trendBias.toUpperCase()}\n**RSI:** ${technicals.rsi?.toFixed(1)} (${technicals.rsiSignal})\n**MACD:** ${technicals.macdTrend}\n\n${trendPatterns.length > 0 ? `Detected trend pattern: ${trendPatterns[0].name}` : 'No specific trend pattern detected.'}`;
  }
  
  // Should I buy/sell responses
  if (q.includes('should i buy') || q.includes('should i sell') || q.includes('recommendation') || q.includes('what should i do')) {
    const bullishSignals = [];
    const bearishSignals = [];
    
    if (technicals.rsi < 30) bullishSignals.push('RSI oversold');
    if (technicals.rsi > 70) bearishSignals.push('RSI overbought');
    if (technicals.macdTrend === 'bullish') bullishSignals.push('MACD bullish');
    if (technicals.macdTrend === 'bearish') bearishSignals.push('MACD bearish');
    if (price.weeklyChange > 2) bullishSignals.push('Strong weekly performance');
    if (price.weeklyChange < -2) bearishSignals.push('Weak weekly performance');
    
    patterns.forEach(p => {
      if (p.type.includes('bullish')) bullishSignals.push(p.name);
      if (p.type.includes('bearish')) bearishSignals.push(p.name);
    });
    
    const signal = bullishSignals.length > bearishSignals.length ? 'CAUTIOUSLY BULLISH' :
                   bearishSignals.length > bullishSignals.length ? 'CAUTIOUSLY BEARISH' : 'NEUTRAL';
    
    return `**Analysis Summary for ${symbol}:**\n\n**Signal:** ${signal}\n\n**Bullish Factors:**\n${bullishSignals.length > 0 ? bullishSignals.map(s => `• ${s}`).join('\n') : '• None currently'}\n\n**Bearish Factors:**\n${bearishSignals.length > 0 ? bearishSignals.map(s => `• ${s}`).join('\n') : '• None currently'}\n\n⚠️ **Disclaimer:** This is not financial advice. Always do your own research and consider consulting a financial advisor.`;
  }
  
  // Default comprehensive response
  return `**${symbol} Quick Analysis:**\n\n**Price:** $${price.current.toFixed(2)} (${price.dailyChange >= 0 ? '+' : ''}${price.dailyChange.toFixed(2)}%)\n**RSI:** ${technicals.rsi?.toFixed(1)} (${technicals.rsiSignal})\n**MACD:** ${technicals.macdTrend}\n**Patterns:** ${patterns.length > 0 ? patterns.slice(0, 2).map(p => p.name).join(', ') : 'None detected'}\n\nAsk me about:\n• Support/resistance levels\n• Pattern analysis\n• RSI & momentum\n• News & sentiment\n• Economic factors\n• Fundamentals`;
};

// ============================================
// DRAGGABLE AI CHAT COMPONENT
// ============================================
const DraggableAIChat = ({ symbol, companyName, data, aiAnalysisLoading, aiAnalysisResult, onClose, onRefresh, patterns, rsiData, macdData, supportResistance }) => {
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'patterns', 'news', 'technicals'
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Generate AI context from all available data
  const aiContext = useMemo(() => 
    generateAIContext(symbol, companyName, data, patterns, rsiData, macdData, supportResistance),
    [symbol, companyName, data, patterns, rsiData, macdData, supportResistance]
  );

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Add initial AI message when result arrives
  useEffect(() => {
    if (aiAnalysisResult && chatMessages.length === 0 && aiContext) {
      const initialMessage = generateAIResponse('trend outlook', aiContext);
      setChatMessages([{
        type: 'ai',
        content: initialMessage,
        timestamp: new Date()
      }]);
    }
  }, [aiAnalysisResult, aiContext]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.fs-ai-chat-input') || e.target.closest('.fs-ai-chat-messages') || e.target.closest('.fs-ai-chat-tabs')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 550, e.clientY - dragOffset.y))
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    
    const userMessage = { type: 'user', content: inputValue, timestamp: new Date() };
    setChatMessages(prev => [...prev, userMessage]);
    const query = inputValue;
    setInputValue('');

    // Generate AI response based on context
    setTimeout(() => {
      const aiResponse = {
        type: 'ai',
        content: generateAIResponse(query, aiContext),
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiResponse]);
    }, 500);
  };

  const handleQuickAction = (query) => {
    setChatMessages(prev => [...prev, { type: 'user', content: query, timestamp: new Date() }]);
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        type: 'ai',
        content: generateAIResponse(query, aiContext),
        timestamp: new Date()
      }]);
    }, 500);
  };

  const current = data?.[data.length - 1];

  return (
    <div 
      className={`fs-ai-chat-window ${isMinimized ? 'minimized' : ''}`}
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
    >
      {/* Header - Draggable */}
      <div className="fs-ai-chat-header">
        <div className="fs-ai-chat-title">
          <Brain size={18} />
          <span>inTheoria AI</span>
          <span className="fs-ai-chat-symbol">{symbol}</span>
        </div>
        <div className="fs-ai-chat-controls">
          <button onClick={() => setIsMinimized(!isMinimized)} title={isMinimized ? 'Expand' : 'Minimize'}>
            {isMinimized ? <Maximize2 size={14} /> : <Minus size={14} />}
          </button>
          <button onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Signal Banner */}
          {aiAnalysisResult && (
            <div className={`fs-ai-chat-signal ${aiAnalysisResult.signal.toLowerCase()}`}>
              {aiAnalysisResult.signal === 'BUY' ? <TrendingUp size={16} /> : aiAnalysisResult.signal === 'SELL' ? <TrendingDown size={16} /> : <Minus size={16} />}
              <span className="signal">{aiAnalysisResult.signal}</span>
              <span className="confidence">{aiAnalysisResult.confidence}% confidence</span>
              <span className="price">${current?.close?.toFixed(2)}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="fs-ai-chat-tabs">
            <button className={activeTab === 'chat' ? 'active' : ''} onClick={() => setActiveTab('chat')}>
              <Brain size={12} /> Chat
            </button>
            <button className={activeTab === 'patterns' ? 'active' : ''} onClick={() => setActiveTab('patterns')}>
              <Zap size={12} /> Patterns ({patterns.length})
            </button>
            <button className={activeTab === 'technicals' ? 'active' : ''} onClick={() => setActiveTab('technicals')}>
              <Activity size={12} /> Technicals
            </button>
            <button className={activeTab === 'news' ? 'active' : ''} onClick={() => setActiveTab('news')}>
              <Newspaper size={12} /> News
            </button>
          </div>

          {/* Tab Content */}
          <div className="fs-ai-chat-content">
            {activeTab === 'chat' && (
              <>
                {/* Chat Messages */}
                <div className="fs-ai-chat-messages" ref={chatRef}>
                  {aiAnalysisLoading ? (
                    <div className="fs-ai-chat-loading">
                      <Cpu size={24} className="spinning" />
                      <span>Analyzing {symbol}...</span>
                    </div>
                  ) : (
                    <>
                      {chatMessages.map((msg, idx) => (
                        <div key={idx} className={`fs-ai-chat-message ${msg.type}`}>
                          {msg.type === 'ai' && <Brain size={16} />}
                          <div className="message-content">
                            {msg.content.split('\n').map((line, i) => (
                              <span key={i}>
                                {line.startsWith('**') && line.endsWith('**') ? <strong>{line.replace(/\*\*/g, '')}</strong> :
                                 line.startsWith('**') ? <strong>{line.replace(/\*\*/g, '')}</strong> :
                                 line.startsWith('•') ? <span className="bullet-point">{line}</span> :
                                 line}
                                {i < msg.content.split('\n').length - 1 && <br />}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="fs-ai-chat-quick">
                  <button onClick={() => handleQuickAction('What are the key support and resistance levels?')}>S/R Levels</button>
                  <button onClick={() => handleQuickAction('Analyze the current trend and momentum')}>Trend</button>
                  <button onClick={() => handleQuickAction('What patterns do you see?')}>Patterns</button>
                  <button onClick={() => handleQuickAction('What is the latest news?')}>News</button>
                  <button onClick={() => handleQuickAction('Should I buy or sell?')}>Advice</button>
                </div>

                {/* Input */}
                <div className="fs-ai-chat-input">
                  <input 
                    type="text" 
                    placeholder="Ask about patterns, technicals, news..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button onClick={handleSendMessage}>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}

            {activeTab === 'patterns' && (
              <div className="fs-ai-tab-content">
                <div className="fs-ai-section-title">Detected Patterns</div>
                {patterns.length > 0 ? (
                  <div className="fs-ai-patterns-list">
                    {patterns.map((pattern, idx) => (
                      <div key={idx} className={`fs-ai-pattern-item ${pattern.type.includes('bullish') ? 'bullish' : pattern.type.includes('bearish') ? 'bearish' : 'neutral'}`}>
                        <div className="pattern-header">
                          {pattern.type.includes('bullish') ? <TrendingUp size={14} /> : pattern.type.includes('bearish') ? <TrendingDown size={14} /> : <Minus size={14} />}
                          <span className="pattern-name">{pattern.name}</span>
                          <span className="pattern-confidence">{Math.round(pattern.confidence * 100)}%</span>
                        </div>
                        <p className="pattern-desc">{pattern.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="fs-ai-empty">No patterns currently detected</div>
                )}
              </div>
            )}

            {activeTab === 'technicals' && (
              <div className="fs-ai-tab-content">
                <div className="fs-ai-section-title">Technical Indicators</div>
                <div className="fs-ai-technicals-grid">
                  <div className="fs-ai-tech-item">
                    <span className="label">RSI (14)</span>
                    <span className={`value ${rsiData[rsiData.length - 1] > 70 ? 'bearish' : rsiData[rsiData.length - 1] < 30 ? 'bullish' : ''}`}>
                      {rsiData[rsiData.length - 1]?.toFixed(1) || 'N/A'}
                    </span>
                    <span className="status">{rsiData[rsiData.length - 1] > 70 ? 'Overbought' : rsiData[rsiData.length - 1] < 30 ? 'Oversold' : 'Neutral'}</span>
                  </div>
                  <div className="fs-ai-tech-item">
                    <span className="label">MACD</span>
                    <span className={`value ${macdData.histogram[macdData.histogram.length - 1] > 0 ? 'bullish' : 'bearish'}`}>
                      {macdData.histogram[macdData.histogram.length - 1]?.toFixed(3) || 'N/A'}
                    </span>
                    <span className="status">{macdData.histogram[macdData.histogram.length - 1] > 0 ? 'Bullish' : 'Bearish'}</span>
                  </div>
                  <div className="fs-ai-tech-item">
                    <span className="label">Support</span>
                    <span className="value">${supportResistance.support[0]?.price?.toFixed(2) || 'N/A'}</span>
                    <span className="status">Key Level</span>
                  </div>
                  <div className="fs-ai-tech-item">
                    <span className="label">Resistance</span>
                    <span className="value">${supportResistance.resistance[0]?.price?.toFixed(2) || 'N/A'}</span>
                    <span className="status">Key Level</span>
                  </div>
                </div>
                <button className="fs-ai-analyze-btn" onClick={() => { setActiveTab('chat'); handleQuickAction('Give me a full technical analysis'); }}>
                  <Brain size={14} /> Get AI Analysis
                </button>
              </div>
            )}

            {activeTab === 'news' && (
              <div className="fs-ai-tab-content">
                <div className="fs-ai-section-title">Recent News</div>
                <div className="fs-ai-news-list">
                  {aiContext?.news.map((item, idx) => (
                    <div key={idx} className={`fs-ai-news-item ${item.sentiment}`}>
                      <div className="news-sentiment">
                        {item.sentiment === 'positive' ? <TrendingUp size={12} /> : item.sentiment === 'negative' ? <TrendingDown size={12} /> : <Minus size={12} />}
                      </div>
                      <div className="news-content">
                        <span className="news-title">{item.title}</span>
                        <span className="news-meta">{item.source} • {item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="fs-ai-analyze-btn" onClick={() => { setActiveTab('chat'); handleQuickAction('Analyze the news sentiment and impact'); }}>
                  <Brain size={14} /> Analyze News Impact
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FullscreenChart;